export const en = {
  workforce: {
    title: 'Workforce',
    description: 'Canonical people, effective relationships, governed contracts, operational evidence, and explainable compensation.',
    summary: 'Workforce summary',
    profilesTab: 'Profiles',
    engagementsTab: 'Relationships & contracts',
    compensationTab: 'Compensation',
    timeTab: 'Time & leave',
    revenueTab: 'Commission & sales',
    claimsTab: 'Claims & loans',
    settlementsTab: 'Settlements & payment',
    disputesTab: 'Disputes',
    reportsTab: 'Reports & AI',
    selectProfile: 'Select a workforce profile',
    selectProfileHint: 'Operational records are always scoped to one verified workforce profile.',
    workflowFailed: 'The workflow action failed. Refresh the record and retry with its current version.',
    activeProfiles: 'Active profiles',
    relationships: 'Relationships',
    roleAssignments: 'Role assignments',
    persistedContracts: 'Persisted contracts',
    newProfile: 'New profile',
    addRelationship: 'Add relationship',
    newContract: 'New contract',
    newPlan: 'New plan',
    calculate: 'Calculate',
    profilesTitle: 'Workforce profiles',
    profilesHint: 'One workforce identity per canonical real person.',
    noProfiles: 'No workforce profiles',
    noProfilesHint: 'Create a profile to establish relationships, contracts, and compensation.',
    numberPerson: 'Number / person',
    externalReference: 'External reference',
    viewProfile: 'View profile',
    profileLoadFailed: 'Profile details could not be loaded.',
    profileVersion: 'Profile {{number}} · optimistic version {{version}}',
    canonicalPerson: 'Canonical person',
    relationshipsRolesTitle: 'Relationships and role assignments',
    engagementsFor: 'Effective workforce engagements for {{name}}.',
    selectProfileEngagement: 'Select a profile before creating an engagement.',
    noRelationships: 'No relationships',
    noRelationshipsHint: 'Select a profile and add its first effective workforce relationship.',
    actions: 'Actions',
    assignRole: 'Assign role',
    contractsTitle: 'Contracts',
    contractsHint: 'Persisted governed contracts for the selected workforce profile.',
    noContracts: 'No contracts',
    noContractsHint: 'Create a contract for one of the selected profile’s relationships.',
    profileRelationship: 'Profile / relationship',
    termsFingerprint: 'Terms fingerprint',
    compensationWorkbenchTitle: 'Compensation plan workbench',
    compensationWorkbenchHint: 'Plan changes are versioned; calculation is an explainable read-only kernel.',
    addVersion: 'Add version',
    addComponent: 'Add component',
    noPlan: 'No plan in this workspace',
    noPlanHint: 'Create a plan to add an effective version and compensation components.',
    plan: 'Plan',
    planVersion: 'Plan version',
    versions: 'Versions',
    component: 'Component',
    kindMethod: 'Kind / method',
    multiplier: 'Multiplier',
    latestCalculationTitle: 'Latest calculation',
    latestCalculationHint: 'A calculation never posts payroll or ledger entries; use the fingerprint to prove the inputs and result.',
    noCalculation: 'No calculation result',
    noCalculationHint: 'Calculate an active compensation plan for a profile and relationship.',
    grossEarnings: 'Gross earnings',
    netAmount: 'Net amount',
    fingerprint: 'Fingerprint',
    newWorkforceProfileTitle: 'New workforce profile',
    newWorkforceProfileHint: 'Link exactly one active real person from Varza’s canonical person directory.',
    createProfile: 'Create profile',
    profileCreated: 'Workforce profile created.',
    addRelationshipTitle: 'Add relationship',
    addRelationshipFor: 'Create an effective relationship for {{name}}.',
    relationshipAdded: 'Relationship added.',
    assignRoleTitle: 'Assign role',
    assignRoleFor: 'Assign an effective role to relationship {{number}}.',
    roleAssigned: 'Role assigned.',
    newWorkforceContractTitle: 'New workforce contract',
    newWorkforceContractHint: 'The initial terms are stored as a canonical immutable JSON snapshot.',
    createContract: 'Create contract',
    contractCreated: 'Contract created.',
    newCompensationPlanTitle: 'New compensation plan',
    newCompensationPlanHint: 'Create the plan header before adding an effective version and its calculation components.',
    createPlan: 'Create plan',
    planCreated: 'Compensation plan created.',
    addPlanVersionTitle: 'Add compensation-plan version',
    planVersionAdded: 'Plan version added.',
    addCompensationComponentTitle: 'Add compensation component',
    componentHint: 'Components may be fixed, time-based, or piece-based earnings or deductions.',
    componentAdded: 'Compensation component added.',
    calculateCompensationTitle: 'Calculate compensation',
    calculateCompensationHint: 'Calculation is read-only and deterministic. The selected plan must already have an active effective version.',
    compensationCalculated: 'Compensation calculated. No accounting entry was posted.',
    relationshipNumber: 'Relationship number',
    effectiveTo: 'Effective to',
    canonicalPersonId: 'Canonical person ID',
    personGuid: 'Person GUID',
    workforceNumber: 'Workforce number',
    displayName: 'Display name',
    roleCode: 'Role code',
    roleCodePlaceholder: 'EMPLOYEE, CONTRACTOR, RESELLER',
    contractNumber: 'Contract number',
    startsOn: 'Starts on',
    endsOn: 'Ends on',
    termsSnapshot: 'Terms snapshot',
    planCode: 'Plan code',
    planName: 'Plan name',
    currencyAssetCode: 'Currency / asset code',
    currencyPrecision: 'Currency precision',
    componentCode: 'Component code',
    componentName: 'Component name',
    method: 'Method',
    expenseAccountId: 'Expense account ID',
    liabilityAccountId: 'Liability account ID',
    profile: 'Profile',
    profileId: 'Profile ID',
    activePlanId: 'Active plan ID',
    relationshipId: 'Relationship ID',
    approvalCutoff: 'Approval cutoff',
    approvedSources: 'Approved time or piece sources',
    sourcesArrayError: 'Sources must be a JSON array.',
    sourceObjectError: 'Source {{index}} must be an object.',
    sourceMethodError: 'Source {{index}} has an invalid method.',
    compensationKind: { Earning: 'Earning', Deduction: 'Deduction' },
    compensationMethod: { Fixed: 'Fixed', Time: 'Time', Piece: 'Piece' },
    date: 'Date',
    kind: 'Kind',
    quantity: 'Quantity',
    status: 'Status',
    version: 'Version',
    action: 'Action',
    amount: 'Amount',
    asset: 'Asset',
    relationship: 'Relationship',
    contract: 'Contract',
    period: 'Period',
    open: 'Open',
    openEnded: 'open',
    submit: 'Submit',
    approve: 'Approve',
    reverse: 'Reverse',
    activate: 'Activate',
    close: 'Close',
    reversalReason: 'Reversal reason',
    timeTitle: 'Time, attendance, leave, and output evidence',
    timeDescription: 'Persisted operational evidence stays draft until submitted and approved.',
    noWorkEvidence: 'No work evidence',
    noWorkEvidenceHint: 'Record a shift, attendance, timesheet, leave, output, work order, or project-time fact.',
    recordWork: 'Record work evidence',
    recordWorkHint: 'Choose the evidence kind; its authoritative unit is enforced automatically.',
    noRelationship: 'No relationship',
    noRelationshipWorkHint: 'Create a workforce relationship before recording work evidence.',
    projectOptional: 'Project ID (optional)',
    workOrderOptional: 'Work-order ID (optional)',
    costCenterOptional: 'Cost-center ID (optional)',
    recordDraft: 'Record draft',
    workKind: {
      Shift: 'Shift', Attendance: 'Attendance', Timesheet: 'Timesheet', Leave: 'Leave',
      WorkOutput: 'Work output', WorkOrder: 'Work order', ProjectTime: 'Project time',
    },
    workUnit: {
      Hours: 'hours', Days: 'days', Shifts: 'shifts', Units: 'units', Jobs: 'jobs', Kilometers: 'kilometers',
    },
    createClaim: 'Create financial claim',
    createClaimHint: 'Expenses are reimbursable; advances, draws, and loans require an explicit recovery cap and schedule.',
    noRelationshipClaimHint: 'Create a workforce relationship before recording a claim.',
    claimKind: {
      ExpenseReimbursement: 'Expense reimbursement', Advance: 'Advance',
      CommissionDraw: 'Commission draw', Loan: 'Loan',
    },
    recoverySchedule: 'Recovery schedule',
    recoveryCap: 'Maximum recovery per settlement',
    recoveryDue: 'Due date',
    installmentAmount: 'Installment amount',
    removeInstallment: 'Remove installment',
    addInstallment: 'Add installment',
    recoveryTotal: 'Scheduled total: {{total}} / claim amount: {{amount}}',
    createClaimDraft: 'Create claim draft',
    jurisdictionTitle: 'Jurisdiction pack status',
    jurisdictionDescription: 'Check the effective, citation-bearing policy pack without embedding statutory assumptions in Workforce.',
    jurisdictionCode: 'Jurisdiction code',
    effectiveOn: 'Effective on',
    checkJurisdiction: 'Check configured pack',
    jurisdictionMissing: 'No effective jurisdiction pack is configured for that code and date.',
    jurisdictionConfigured: '{{pack}} version {{version}} is configured and effective.',
    jurisdictionNoAssumptions: 'The example pack is generic and does not make statutory tax, labor, or payroll claims.',
    jurisdictionUnchecked: 'Enter a jurisdiction code and date to verify configuration status. The included EXAMPLE pack is non-statutory.',
    reportsTitle: 'Workforce reports',
    reportsHint: 'Tenant-private deterministic facts with source citations.',
    noReportFacts: 'No report facts.',
    aiTitle: 'Permission-gated AI assistance',
    aiHint: 'AI explains cited facts only; deterministic modules own every amount and posting.',
    aiTool: 'Tool',
    aiQuestion: 'Question',
    runAi: 'Run AI tool',
    aiUnavailable: 'The AI tool is unavailable or permission was denied.',
    queryWorkforce: 'Query workforce',
    explainCommission: 'Explain commission',
    draftDisputeResponse: 'Draft dispute response',
    registerHint: 'Persisted tenant records; financial transitions enforce current versions and permissions.',
    noProfileRecords: 'No records match the selected workforce profile.',
    partnerAgreementsTitle: 'Partner agreements',
    noPartnerAgreements: 'No partner agreements',
    agreement: 'Agreement',
    basis: 'Basis',
    rate: 'Rate',
    commissionEventsTitle: 'Commission events',
    noCommissionEvents: 'No commission events',
    commissionAmount: 'Commission',
    salesAttributionTitle: 'Sales attribution',
    noSalesAttribution: 'No sales attribution',
    grossSales: 'Gross sales',
    collected: 'Collected',
    attribution: 'Attribution',
    partnerAgreementSetup: 'Partner agreement setup',
    partnerAgreementSetupHint: 'Commission terms are versioned, effective-dated, and activated separately.',
    noContractAgreementHint: 'Create a governed contract before configuring commission terms.',
    noContractAvailable: 'No persisted contract',
    agreementCode: 'Agreement code',
    agreementKind: 'Agreement kind',
    operatingModel: 'Operating model',
    commissionBasis: 'Commission basis',
    ratePercent: 'Rate percent',
    attributionPercent: 'Attribution percent',
    effectiveFrom: 'Effective from',
    policyJson: 'Policy JSON',
    createAgreementDraft: 'Create agreement draft',
    partnerKind: { SalesAgent: 'Sales agent', Reseller: 'Reseller', ReferralPartner: 'Referral partner' },
    operatingModelValue: {
      Agent: 'Agent', BuySell: 'Buy / sell', Marketplace: 'Marketplace',
      Consignment: 'Consignment', Referral: 'Referral', WhiteLabel: 'White label',
    },
    commissionBasisValue: {
      OrderConfirmed: 'Order confirmed', Shipment: 'Shipment', InvoiceIssued: 'Invoice issued',
      CollectedRevenue: 'Collected revenue', GrossMargin: 'Gross margin', Referral: 'Referral',
    },
    revenueEvidenceTitle: 'Commission and sales evidence',
    revenueEvidenceHint: 'Commission and sales facts are drafts until separately approved.',
    noRelationshipRevenueHint: 'Create a workforce relationship before recording revenue evidence.',
    commissionEvent: 'Commission event',
    activeAgreement: 'Active agreement',
    noActiveAgreement: 'No active agreement',
    recognizedRevenue: 'Recognized revenue',
    collectedRevenue: 'Collected revenue',
    directCost: 'Direct cost',
    recordCommissionDraft: 'Record commission draft',
    recordSalesDraft: 'Record sales draft',
    clawBack: 'Claw back',
    clawbackReason: 'Clawback reason',
    claimsTitle: 'Expenses, advances and loans',
    noFinancialClaims: 'No financial claims',
    outstanding: 'Outstanding',
    managerApprove: 'Manager approve',
    financeApprove: 'Finance approve',
    settlementsPostingTitle: 'Settlements and posting preparation',
    settlementNumber: 'Settlement number',
    periodFrom: 'Period from',
    periodTo: 'Period to',
    netPayable: 'Net payable',
    paymentEvidenceTitle: 'Payment evidence',
    noPaymentEvidence: 'No payment evidence',
    occurred: 'Occurred',
    reversal: 'Reversal',
    reverseEvidence: 'Reverse evidence',
    calculateSettlementTitle: 'Calculate settlement',
    calculateSettlementHint: 'Uses every approved, in-period work, commission, and reimbursement fact for the selected relationship and asset.',
    noRelationshipSettlementHint: 'Create a relationship before calculating a settlement.',
    eligibleSources: 'Eligible approved sources: {{count}}',
    calculatePersistedSettlement: 'Calculate persisted settlement',
    markStale: 'Mark stale',
    preparePostingIntegrations: 'Prepare posting and integrations',
    recordPaymentEvidence: 'Record payment evidence',
    paidAmount: 'Paid amount',
    createAdjustment: 'Create adjustment',
    adjustmentReason: 'Adjustment reason',
    adjustmentNumber: 'Adjustment settlement number',
    disputesAdjustmentsTitle: 'Worker disputes and adjustments',
    disputeNumber: 'Dispute',
    subject: 'Subject',
    beginManagerReview: 'Begin manager review',
    escalateFinanceHr: 'Escalate to finance / HR',
    resolve: 'Resolve',
    resolutionEvidence: 'Resolution and adjustment evidence',
    portalEyebrow: 'Workforce',
    myWorkforce: 'My workforce',
    portalIdentityHint: 'Workforce records linked to your verified identity.',
    noWorkforceLinks: 'No workforce links',
    noWorkforceLinksHint: 'No active organization has linked a workforce profile to your identity.',
    portalDescription: 'Your private workforce records at {{organization}}. Payment and accounting stay owned by the responsible tenant modules.',
    organization: 'Organization',
    portalSummary: 'Workforce summary',
    contracts: 'Contracts',
    latestNetEarnings: 'Latest net earnings',
    latestSettlement: 'Latest settlement',
    portalActionFailed: 'The action could not be completed. Review the record version and try again.',
    relationshipsContracts: 'Relationships and contracts',
    verifiedProfile: 'Profile {{number}} \\u00b7 read-only verified records',
    noWorkforceRelationships: 'No workforce relationships',
    noWorkforceRelationshipsHint: 'No relationship or contract has been published for your identity.',
    record: 'Record',
    effectivePeriod: 'Effective period',
    roles: 'Roles',
    acknowledge: 'Acknowledge',
    acceptOffer: 'Accept offer',
    earningsCommissions: 'Earnings and commissions',
    earningsHint: 'Finalized calculation evidence; accounting remains owned by Payroll and Payables.',
    noEarnings: 'No earnings statements',
    noEarningsHint: 'Approved earnings and commission statements will appear here.',
    periodEvent: 'Period / event',
    grossBasis: 'Gross or basis',
    deductions: 'Deductions',
    netCommission: 'Net / commission',
    commission: 'Commission',
    settlements: 'Settlements',
    settlementsHint: 'Cutoff status is explicit; finalized statements change only through adjustments.',
    noSettlements: 'No settlements',
    noSettlementsHint: 'Approved settlement statements will appear here.',
    credits: 'Credits',
    debits: 'Debits',
    net: 'Net',
    cutoff: 'Cutoff',
    payment: 'Payment',
    expensesAdvancesLoans: 'Expenses, advances and loans',
    privateClaimsHint: 'Only claims linked to your verified workforce profile are shown.',
    noExpensesRecoveries: 'No expenses or recoveries',
    noExpensesRecoveriesHint: 'Your submitted expenses, advances, and loans will appear here.',
    documents: 'Documents',
    documentsHint: 'Secure documents published to your VII.',
    noDocuments: 'No documents',
    noDocumentsHint: 'Published workforce documents will appear here.',
    openDocument: 'Open',
    submissionsDisputes: 'Submissions and disputes',
    submissionsHint: 'Submit your own evidence. Managers and finance review it before any financial effect.',
    noRelationshipAvailable: 'No relationship available',
    noRelationshipAvailableHint: 'An active or invited relationship is required before submitting evidence.',
    timeOrLeave: 'Time or leave',
    type: 'Type',
    time: 'Time',
    leave: 'Leave',
    expense: 'Expense',
    settlementDispute: 'Settlement dispute',
    settlement: 'Settlement',
    noSettlementAvailable: 'No settlement available',
    reviewQuestion: 'What should be reviewed?',
    openDispute: 'Open dispute',
    noDisputes: 'No disputes',
    noDisputesHint: 'Your disputes and their adjustment-backed resolutions will appear here.',
    dispute: 'Dispute',
    category: 'Category',
    resolution: 'Resolution',
    acceptResolution: 'Accept resolution',
  },
  integrationControl: {
    title: 'Integration control center',
    subtitle: 'Onboard partners, inspect exactly-once event processing, approve accounting plans, and operate delivery safeguards.',
    actionFailed: 'The integration action could not be completed.',
    previewUnavailable: 'The accounting preview is not available for this event.',
    partnerCreated: 'Partner created. Its ID was copied into the application form.',
    applicationCreated: 'Application created. Its ID is ready for credential issuance.',
    credentialCreated: 'Credential issued. Copy the secret now.',
    controlsSaved: 'Emergency controls saved.',
    summary: 'Integration operations summary',
    partners: 'Partners',
    applications: 'Applications',
    bindings: 'Active bindings',
    events: 'Events received',
    failures: 'Failed events',
    deadLetters: 'Dead letters',
    outbox: 'Pending outbox',
    webhooks: 'Failed webhooks',
    eventInspector: 'Event inspector',
    eventInspectorHint: 'Inspect lineage, errors, deterministic accounting plans, and replay eligibility.',
    allStatuses: 'All statuses',
    eventId: 'External event',
    type: 'Event type',
    attempts: 'Attempts',
    error: 'Last error',
    rule: 'Rule version',
    planHash: 'Approved plan hash',
    document: 'Planned document',
    debit: 'Debit',
    credit: 'Credit',
    auditReason: 'Audit reason (required for changes)',
    replay: 'Replay safely',
    replayQueued: 'Replay queued.',
    approved: 'The exact accounting plan was approved.',
    newPartner: 'New partner',
    code: 'Partner code',
    displayName: 'Display name',
    legalName: 'Legal name',
    contact: 'Contact information',
    newApplication: 'New application',
    partnerId: 'Partner ID',
    environment: 'Environment',
    rateLimit: 'Requests per minute',
    scopes: 'OAuth scopes (space-separated)',
    webhookEvents: 'Allowed webhook events',
    credentials: 'Client credential',
    applicationId: 'Application ID',
    issueCredential: 'Issue credential',
    copyNow: 'Shown once — store this secret in the partner secret manager.',
    emergency: 'Emergency controls',
    disableApplication: 'Disable application',
    pauseProcessing: 'Pause event processing',
    pauseWebhooks: 'Pause webhook delivery',
  },
  auditLog: {
    control: 'Financial control',
    title: 'Audit log',
    tenantSubtitle: 'Immutable changes for this organization, with actor, time, and redacted before/after evidence.',
    platformTitle: 'Platform audit log',
    platformSubtitle: 'Immutable changes to Varza tenants, identity, sessions, and platform operations.',
    verify: 'Verify hash chain',
    chainValid: 'Audit chain verified.',
    chainInvalid: 'Audit chain verification failed.',
    checkedEvents: '{{count}} cryptographically chained events checked.',
    filterTarget: 'Target type',
    operation: 'Operation',
    allOperations: 'All operations',
    time: 'Date and time',
    actor: 'User or service',
    target: 'Document or record',
    fields: 'Changed fields',
    noEvents: 'No audit events',
    noEventsHint: 'No changes match the selected filters.',
    selectEvent: 'Select an event to inspect its evidence.',
    correlation: 'Correlation ID',
    hash: 'SHA-256 hash',
    before: 'Before',
    after: 'After',
    AdditiveReversal: 'Correct with a linked reversal; posted history is immutable.',
    ValidatedRecreation: 'Recreate only through the owning module validation.',
    ValidatedSnapshotRestore: 'Restore only through the owning module validation.',
  },
  app: {
    name: 'Varza',
    tagline: 'Intelligent Business Management System',
    product: 'Accounting & Inventory',
    workspace: 'Financial workspace',
    skipToContent: 'Skip to main content',
    poweredBy: 'Powered by Exirsoft',
  },
  common: {
    actions: 'Actions',
    add: 'Add',
    all: 'All',
    approve: 'Approve',
    back: 'Back',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    date: 'Date',
    delete: 'Delete',
    edit: 'Edit',
    export: 'Export',
    filters: 'Filters',
    more: 'More',
    new: 'New',
    next: 'Next',
    previous: 'Previous',
    refresh: 'Refresh',
    reset: 'Reset',
    save: 'Save',
    saving: 'Saving…',
    saveDraft: 'Save draft',
    search: 'Search',
    select: 'Select…',
    status: 'Status',
    total: 'Total',
    submit: 'Submit',
    view: 'View',
    viewAll: 'View all',
    notAvailable: 'Not available',
    loading: 'Loading workspace…',
    retry: 'Try again',
    noResults: 'No matching results',
    lastUpdated: 'Last updated',
    currency: 'Currency',
    amount: 'Amount',
    owner: 'Owner',
    reference: 'Reference',
    description: 'Description',
    details: 'Details',
    activate: 'Activate',
    deactivate: 'Deactivate',
    remove: 'Remove',
    reject: 'Reject',
    yes: 'Yes',
    no: 'No',
  },
  nav: {
    overview: 'Overview',
    workspace: 'Workspace',
    operations: 'Operations',
    accounting: 'Accounting',
    baseData: 'Base data',
    dashboard: 'Dashboard',
    journals: 'Journals',
    ledger: 'General ledger',
    accounts: 'Chart of accounts',
    persons: 'Persons',
    customers: 'Customers',
    suppliers: 'Suppliers',
    interOrganizationIdentity: 'Inter-organization identity',
    exchangeRates: 'Exchange rates',
    currencyAdministration: 'Currency administration',
    tax: 'Tax',
    fixedAssets: 'Fixed assets',
    budgeting: 'Budgeting',
    costing: 'Cost & profitability',
    projects: 'Projects & engagements',
    personalFinance: 'Personal finance',
    inventory: 'Inventory',
    reports: 'Reports',
    receivables: 'Receivables',
    payables: 'Payables',
    treasury: 'Cash & bank',
    purchasing: 'Purchasing',
    purchaseOrders: 'Purchase orders',
    sales: 'Sales',
    salesOrders: 'Sales orders',
    automation: 'Document automation',
    workforce: 'Workforce',
    payroll: 'Payroll',
    settings: 'Settings',
    access: 'Users & access',
    shortLedger: 'Ledger',
    collapse: 'Collapse navigation',
    expand: 'Expand navigation',
    openMenu: 'Open navigation',
    closeMenu: 'Close navigation',
    quickNav: 'Quick navigation',
    homepage: 'Varza homepage',
  },
  professionalServices: {
    title: 'Professional services operations',
    subtitle: 'Authoritative time, reimbursable expenses, WIP billing, staff cost, and profitability for this engagement.',
    tabs: {
      performance: 'Performance',
      time: 'Time',
      expenses: 'Expenses',
      wip: 'WIP & invoices',
      'staff-allocations': 'Staff cost',
    },
    timeTitle: 'Time by project',
    timeHint: 'Tenant-owned time is frozen at the engagement rate and can be billed only once.',
    noTime: 'No time entries for this engagement',
    date: 'Date',
    activity: 'Activity',
    teamMember: 'Team member',
    hours: 'Hours',
    value: 'Billable value',
    status: 'Status',
    actions: 'Actions',
    void: 'Void',
    timeVoided: 'The unbilled time entry was voided.',
    expensesTitle: 'Expense recovery',
    expensesHint: 'Receipt-backed project costs with employee-payable journal evidence and frozen client-rebill eligibility.',
    noExpenses: 'No reimbursable expenses for this engagement',
    category: 'Category',
    amount: 'Amount',
    clientRecovery: 'Client recovery',
    evidence: 'Accounting evidence',
    wipTitle: 'WIP invoices',
    wipHint: 'Frozen time, expenses, milestones, discount, tax, currency, and exchange-rate evidence linked to Receivables.',
    noWip: 'No WIP invoices for this engagement',
    invoice: 'Invoice',
    revenue: 'Revenue before tax',
    tax: 'Tax',
    due: 'Due date',
    staffTitle: 'Staff cost allocations',
    staffHint: 'Posted payroll cost allocated exactly once to effective engagement team assignments.',
    noStaff: 'No posted staff cost allocations for this engagement',
    period: 'Period',
    method: 'Method',
    payrollRun: 'Payroll run',
    performanceTitle: 'Utilization, realization, and profitability',
    performanceHint: 'Currency-safe engagement metrics derived from posted Projects, Payroll, Receivables, and ledger evidence.',
    utilization: 'Utilization',
    realization: 'Realization',
    unbilledWip: 'Unbilled WIP',
    profit: 'Project profit',
    receivablesAging: 'Receivables aging',
    retainerBalance: 'Retainer balance',
    billedRevenue: 'Billed revenue',
    postedExpenses: 'Posted expenses',
    expenseRecovery: 'Recovered expenses',
    allocatedStaffCost: 'Allocated staff cost',
    authoritativeFootnote: 'Authoritative records in the displayed currency',
  },
  customers: {
    title: 'Customers',
    subtitle: 'Sales customers, their credit settings, and contact details.',
    newCustomer: 'New customer',
    editCustomer: 'Edit customer',
    formHint: 'Maintain the customer details used by sales orders and receivables.',
    person: 'Canonical person',
    code: 'Customer code',
    name: 'Customer name',
    currency: 'Currency',
    creditLimit: 'Credit limit',
    taxRegistrationNumber: 'Tax registration number',
    email: 'Email',
    phone: 'Phone',
    billingAddress: 'Billing address',
    active: 'Customer is active',
    contact: 'Contact',
    searchPlaceholder: 'Search customers by code, name, email, or phone',
    showInactive: 'Show inactive',
    noCustomers: 'No customers yet',
    noCustomersHint: 'Add the first customer to use it in sales orders and invoices.',
    onHold: 'Credit hold',
  },
  suppliers: {
    title: 'Suppliers',
    subtitle: 'Purchase suppliers, their tax settings, and remittance details.',
    newSupplier: 'New supplier',
    editSupplier: 'Edit supplier',
    formHint: 'Maintain the supplier details used by purchase orders and payables.',
    person: 'Canonical person',
    code: 'Supplier code',
    name: 'Supplier name',
    currency: 'Currency',
    withholdingTaxPercent: 'Withholding tax rate',
    taxRegistrationNumber: 'Tax registration number',
    email: 'Email',
    phone: 'Phone',
    remitToAddress: 'Remit-to address',
    active: 'Supplier is active',
    contact: 'Contact',
    searchPlaceholder: 'Search suppliers by code, name, email, or phone',
    showInactive: 'Show inactive',
    noSuppliers: 'No suppliers yet',
    noSuppliersHint: 'Add the first supplier to use it in purchase orders and bills.',
  },
  workspace: {
    canvas: 'Document workspace',
    pane: 'Workspace pane',
    openDocuments: 'Open documents',
    documentMenu: 'Document commands',
    paneMenu: 'Pane commands',
    documentList: 'All documents in this pane',
    closeDocument: 'Close',
    closeNamedDocument: 'Close {{name}}',
    closeOthers: 'Close the others',
    closeAll: 'Close all',
    closePane: 'Close this pane',
    pin: 'Pin this tab',
    unpin: 'Unpin this tab',
    duplicate: 'Open a second copy',
    splitBeside: 'Split beside',
    splitBelow: 'Split below',
    floatDocument: 'Open in a window',
    dockDocument: 'Dock into the workspace',
    dockWindow: 'Dock this window',
    back: 'Back',
    forward: 'Forward',
    maximizePane: 'Fill the workspace',
    restorePane: 'Restore this pane',
    maximize: 'Maximize',
    restoreWindow: 'Restore',
    minimize: 'Minimize',
    closeWindow: 'Close window',
    floatingWindow: 'Window',
    minimizedWindows: 'Minimized windows',
    resizePanes: 'Resize the panes',
    dropAsTab: 'Release to add as a tab',
    dropAsSplit: 'Release to split this pane',
    dropAtEdge: 'Release to dock along this edge',
    dropAsWindow: 'Release for a window of its own',
    dropNowhere: 'Nowhere to drop here',
    startTitle: 'Nothing open here',
    startSubtitle: 'Open as many registers as the work needs, then arrange them side by side.',
    quickOpen: 'Open',
    recent: 'Recently open',
    hintDrag: 'Drag a tab to the edge of a pane to put two documents side by side.',
    hintFloat: 'Drag a tab off the workspace — or hold Ctrl — for a window you can move and resize.',
    hintKeys: 'Alt+W closes, Alt+M fills the workspace, Alt with the arrow keys moves between tabs.',
    openCount_one: '{{count}} document open',
    openCount_other: '{{count}} documents open',
    floatingCount_one: '{{count}} in a window',
    floatingCount_other: '{{count}} in windows',
    documents: {
      journal: 'Journal',
    },
  },
  context: {
    tenant: 'Tenant',
    tenantName: 'Raha Trading Co.',
    branch: 'Branch',
    branchName: 'Head office',
    allBranches: 'All branches',
    period: 'Fiscal period',
    periodName: 'August 2026',
    functionalCurrency: 'Functional currency',
    currencyMenu: 'Open currency details',
    enabledCurrencies: 'Enabled currencies',
    disabledCurrency: 'Not enabled',
    viewExchangeRates: 'View exchange rates',
    language: 'Language',
    english: 'English',
    persian: 'فارسی',
    arabic: 'العربية',
    notifications: 'Notifications',
    pendingApprovals: '{{count}} pending approvals',
    userMenu: 'Open user menu',
    userName: 'Sara Ahmadi',
    userRole: 'Finance manager',
    openPeriod: 'Open period',
    softClosedPeriod: 'Soft-closed period',
    hardClosedPeriod: 'Hard-closed period',
    scopeUnavailable: 'Workspace scope unavailable',
    switchingTenant: 'Switching tenant…',
    scope: 'Workspace scope',
    editScope: 'Change workspace scope',
  },
  auth: {
    loginTitle: 'Sign in to Varza',
    loginSubtitle: 'Use your account to open an authorized financial workspace.',
    username: 'Username',
    verifyTitle: 'Verify your identity',
    verifySubtitle: 'Complete the additional verification required for this account.',
    email: 'Email',
    password: 'Password',
    verificationMethod: 'Verification method',
    verificationCode: 'Verification code',
    authenticatorHint: 'Enter the current six-digit code from your authenticator app.',
    deliveredCodeHint: 'Request a code, then enter the six digits you receive.',
    sendCode: 'Send verification code',
    codeSent: 'A new verification code was sent.',
    developmentCode: 'Development code: {{code}}',
    orContinueWith: 'or continue with',
    continueGoogle: 'Continue with Google',
    continueMicrosoft: 'Continue with Microsoft',
    external_failed: 'The external sign-in could not be completed.',
    external_not_linked: 'That provider account is not linked to a Varza user. Sign in with your password and link it in Settings, or create an account with the provider.',
    external_email_taken: 'A Varza account already uses that email address. Sign in with your password, then link the provider in Settings.',
    external_email_unusable: 'The provider did not return a verified email address, so no account could be created.',
    totpCode: 'Authenticator code (optional)',
    totpHint: 'Required only when multi-factor authentication is enabled.',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    verify: 'Verify',
    mfaPrompt: 'Multi-factor authentication is required for this account.',
    credentialsError: 'The email, password, or verification code was not accepted.',
    lockedError: 'This account is temporarily locked. Try again later.',
    connectionError: 'Varza could not contact the authentication service. Try again.',
    captchaError: 'Varza could not confirm this request came from a person. Reload the page and try again.',
    captchaUnavailableError: 'The human-verification service is unreachable. Try again in a moment.',
    selectTenantTitle: 'Choose a workspace',
    selectTenantSubtitle: 'Select one of the organizations assigned to your account.',
    selecting: 'Opening…',
    openTenant: 'Open workspace',
    noTenants: 'No active organization memberships are assigned to this account.',
    tenantError: 'The organization could not be selected. Your previous workspace is unchanged.',
    logout: 'Sign out',
    mfaSession: 'MFA-protected session',
    passwordSession: 'Password session',
    forgotLink: 'Forgot your password?',
    createAccount: 'Register with an invitation',
    forgotTitle: 'Reset your password',
    forgotSubtitle: 'Enter your account email and we will send a secure, single-use reset link.',
    recoverySentTitle: 'Check your email',
    recoverySentBody: 'If that address belongs to an account, a password reset link is on its way.',
    recoverySentHint: 'For your security, the link expires and works only once.',
    developmentRecovery: 'Development build:',
    resetNow: 'reset now',
    backToSignIn: 'Back to sign in',
    sendRecovery: 'Send reset link',
    recoveryFailed: 'The reset request could not be completed. Try again.',
    recoveryRateLimited: 'Too many attempts. Wait a moment and try again.',
    resetTitle: 'Choose a new password',
    resetSubtitle: 'Use the secure token from your email and choose a strong new password.',
    recoveryToken: 'Reset token',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordLengthHint: 'Use at least {{count}} characters.',
    passwordTooShort: 'Use at least {{count}} characters.',
    passwordMismatch: 'The two passwords do not match.',
    resetInvalid: 'This reset link is invalid, expired, or has already been used.',
    resetFailed: 'The password could not be reset. Try again.',
    resetAction: 'Set new password',
    requestNewLink: 'Request a new reset link',
    resetCompleteTitle: 'Password updated',
    resetCompleteBody: 'Your new password is ready to use.',
    resetSessionsEnded: 'Other sessions have been ended. Sign in again with your new password.',
    adminConsole: 'System administration',
    invitation_invalid: 'This registration invitation is invalid, expired, revoked, or already used.',
  },
  administration: {
    console: 'System administration',
    workspace: 'Tenant workspace',
    systemScope: 'System scope',
    title: 'Tenant control panel',
    subtitle: 'Provision and govern every accounting organization from one register.',
    newTenant: 'New tenant',
    createFailed: 'The tenant could not be provisioned.',
    stateFailed: 'The tenant state could not be changed.',
    provisioningUnavailable: 'Database provisioning is not configured.',
    provisioningHint: ' Add the administrator and tenant runtime database connections to enable creation.',
    summary: 'Tenant summary',
    totalTenants: 'Total tenants',
    activeTenants: 'Active tenants',
    suspendedTenants: 'Suspended tenants',
    totalUsers: 'Tenant users',
    available: 'Available for sign-in',
    restricted: 'Workspace access stopped',
    memberships: 'Active memberships',
    tenantRegister: 'Tenant register',
    tenantRegisterSubtitle: 'Lifecycle, database, and membership status',
    noTenants: 'No tenants yet',
    noTenantsHint: 'Create the first accounting organization.',
    organization: 'Organization',
    database: 'Database',
    users: 'Users',
    created: 'Created',
    open: 'Open',
    suspend: 'Suspend',
    activate: 'Activate',
    createTitle: 'Create tenant',
    createSubtitle: 'A separate accounting database and administrator role will be provisioned.',
    provisioning: 'Provisioning…',
    create: 'Create tenant',
    displayName: 'Organization name',
    slug: 'Tenant slug',
    language: 'Default language',
    databasePreview: 'Database convention',
    systemUsers: 'System users',
    systemUsersHint: 'Choose which accounts may create registration invitations. Super administrators always can.',
    noSystemUsers: 'No system users',
    noSystemUsersHint: 'Accounts appear here after they are provisioned.',
    user: 'User',
    canInvite: 'May create invitations',
    superAdminImplicit: 'Always allowed',
    invitationAllowed: 'Allowed',
    invitationNotAllowed: 'Not allowed',
    invitationPermissionFailed: 'The invitation permission could not be changed.',
  },
  invitations: {
    title: 'Registration invitations',
    subtitle: 'Create and revoke the single-use codes that admit new Varza accounts.',
    back: 'Back',
    governance: 'Account governance',
    createTitle: 'Create an invitation',
    createHint: 'Each code admits one account. Give it only to the intended person.',
    oneTimeTitle: 'Save this code now',
    oneTimeHint: 'This is the only time the complete code is shown. Varza stores only a one-way hash.',
    rawCode: 'Invitation code',
    registrationLink: 'Shareable registration link',
    copied: 'Copied',
    copyCode: 'Copy code',
    copyLink: 'Copy link',
    copyFailed: 'The value could not be copied. Select it and copy it manually.',
    savedCode: 'I have saved the code',
    label: 'Label',
    labelHint: 'Optional context visible only to invitation managers.',
    expiresIn: 'Expires in days',
    expiryInvalid: 'Enter a whole number from 1 to {{count}} days.',
    createAction: 'Create invitation',
    createFailed: 'The invitation could not be created.',
    revokeFailed: 'The invitation could not be revoked.',
    never: 'Never',
    registerTitle: 'Invitation register',
    registerHint: 'Only the safe code hint remains after creation; complete codes cannot be retrieved.',
    emptyTitle: 'No invitations yet',
    emptyHint: 'Create the first single-use registration invitation.',
    codeHint: 'Code hint',
    created: 'Created',
    expires: 'Expires',
    unlabelled: 'Unlabelled invitation',
    status: {
      active: 'Active',
      used: 'Used',
      revoked: 'Revoked',
      expired: 'Expired',
      unknown: 'Unavailable',
    },
    revoke: 'Revoke',
  },
  aiControl: {
    eyebrow: 'AI governance',
    title: 'AI Control Center',
    subtitle: 'Govern knowledge, agents, models, policies, evaluations, and tenant-safe execution from one audited workspace.',
    health: 'AI platform health',
    publishedPackages: 'Published knowledge',
    indexedChunks: 'Indexed chunks',
    operations24h: 'Operations / 24h',
    failed: 'failed',
    tokens24h: 'Tokens / 24h',
    supervision: 'Supervision',
    graphCandidates: 'graph candidates',
    areas: 'Control areas',
    monitoring: 'Monitoring & ingestion',
    knowledgeHealth: 'Knowledge intelligence health',
    knowledgeHealthHint: 'Live catalog, embedding, graph, review, and ingestion readiness. Metrics refresh automatically.',
    syncCatalog: 'Synchronize catalog',
    synchronizing: 'Synchronizing...',
    degradedEmbedding: 'Degraded deterministic embeddings are active; configure a production embedding endpoint.',
    productionEmbedding: 'Production semantic embeddings active',
    semanticCatalog: 'Semantic catalog',
    knowledgeGraph: 'Knowledge graph',
    relations: 'relations',
    entities: 'entities',
    approvedRelations: 'approved relations',
    reviewQueue: 'Review queue',
    packagesInReview: 'packages in review',
    indexDrift: 'Embedding drift',
    failedSources: 'failed sources',
    ingestDocument: 'Ingest document into knowledge graph',
    ingestDocumentHint: 'Creates a governed package, extracts and embeds citeable chunks, builds provenance-backed graph relations, and routes high-impact facts to human review.',
    sourceFile: 'Source document',
    documentTitle: 'Document title',
    ingestIntoGraph: 'Ingest into knowledge graph',
    ingesting: 'Ingesting...',
    ingestionRuns: 'Document ingestion runs',
    ingestionRunsHint: 'Durable progress and output counts for every administrator-triggered ingestion.',
    stage: 'Pipeline stage',
    graphOutput: 'Graph output',
    reviewRequired: 'review required',
    knowledgeJurisdictionCode: 'Tenant knowledge jurisdiction code',
    knowledgeJurisdictionHint: 'Empty means global knowledge only; a code such as IR allows that jurisdiction plus global sources.',
    tabs: { resources: 'Resource management', knowledge: 'Knowledge packages', graph: 'Knowledge graph', agents: 'Agents', models: 'Models', behavior: 'Behavior', operations: 'Operations & traces', conversations: 'Assistant conversations', evaluations: 'Evaluations' },
    newPackage: 'New knowledge package',
    code: 'Code',
    name: 'Name',
    packageType: 'Package type',
    domain: 'Domain',
    languages: 'Languages',
    jurisdiction: 'Jurisdiction',
    globalJurisdiction: 'Global / not jurisdiction-specific',
    authority: 'Source authority',
    supersedes: 'Supersedes package',
    none: 'None',
    newJurisdiction: 'Create jurisdiction',
    nameEn: 'English name',
    nameFa: 'Farsi name',
    effectiveFrom: 'Effective from',
    effectiveTo: 'Effective to',
    packages: 'Knowledge packages',
    packageLifecycle: 'Draft, process, review, publish, supersede, or roll back with a complete audit trail.',
    process: 'Process / re-index',
    publish: 'Publish',
    rollback: 'Roll back',
    sourceUri: 'Official source URL',
    upload: 'Upload source',
    documents: 'documents',
    chunks: 'chunks',
    citationPreview: 'Chunk, embedding, and citation preview',
    embedded: 'embedded',
    graphReview: 'Graph candidate review',
    graphReviewHint: 'High-impact facts remain excluded from retrieval until a reviewer approves their source-backed relation.',
    fact: 'Candidate fact',
    confidence: 'Confidence',
    highImpact: 'High impact',
    newDefinition: 'New versioned definition',
    definitionType: 'Definition type',
    version: 'Version',
    jsonDefinition: 'JSON definition',
    definitionKinds: { prompt: 'Prompt template', agent: 'Agent', model: 'Model profile', policy: 'Policy' },
    responsibility: 'Responsibility and escalation rules',
    modelProfile: 'Model profile code',
    allowedTools: 'Allowed tools JSON',
    autonomyLevel: 'Maximum autonomy level (0–4)',
    tenantOverrides: 'Tenant policy overrides',
    tenantOverridesHint: 'Apply audited budget, supervision, autonomy, execution, and retention limits to one physical tenant database.',
    policySaved: 'Tenant policy saved',
    dailyTokens: 'Daily token budget',
    monthlyCost: 'Monthly cost budget',
    maxModelCalls: 'Maximum model calls',
    maxToolCalls: 'Maximum tool calls',
    timeoutSeconds: 'Execution timeout (seconds)',
    retentionDays: 'Trace retention (days)',
    functionalCurrency: 'Functional currency for robot-staged documents',
    receiptCashAccount: 'Receipt cash ledger account ID',
    receiptReceivableAccount: 'Receivable control account ID',
    receiptUnappliedAccount: 'Unapplied receipt account ID',
    receiptFxGainAccount: 'Realized FX gain account ID',
    receiptFxLossAccount: 'Realized FX loss account ID',
    invoiceReceivableAccount: 'Invoice receivable account ID',
    invoiceRevenueAccount: 'Invoice revenue account ID',
    supplierPaymentCashAccount: 'Supplier-payment cash ledger account ID',
    supplierPaymentPayableAccount: 'Supplier-payment payable account ID',
    supplierPaymentPrepaymentAccount: 'Supplier prepayment account ID',
    supplierPaymentBankAccount: 'Treasury bank account ID (choose bank or cash box)',
    supplierPaymentCashBox: 'Treasury cash box ID (choose bank or cash box)',
    sensitiveDocumentAutomation: 'Allow AI to stage sensitive documents',
    automationEnabled: 'Enabled · AI may create robot-marked drafts only; human verification is mandatory.',
    automationDisabled: 'Disabled · AI cannot create or modify accounting documents.',
    policyChangeReason: 'Audited reason for this change',
    provider: 'Provider',
    modelId: 'Provider model ID',
    purpose: 'Routing purpose',
    capabilities: 'Capability tags JSON',
    fallbackChain: 'Fallback profile codes JSON',
    contextLimit: 'Context limit',
    inputCost: 'Input cost / million',
    outputCost: 'Output cost / million',
    structuredOutput: 'Supports structured output',
    scope: 'Policy scope',
    versionedRegistry: 'Versioned registry',
    registryHint: 'Publishing is explicit; historical versions remain immutable for trace replay.',
    operations: 'Cross-tenant operation index',
    operationsHint: 'Only metadata is global. Opening tenant trace content requires a recorded access reason.',
    conversations: 'Assistant conversations',
    conversationsHint: 'Anonymous live-help and registered dashboard sessions. Message content stays hidden until an audited access reason is submitted.',
    searchConversations: 'Search user, tenant, visitor, session, page, or device',
    surface: 'Surface',
    allSurfaces: 'All chat surfaces',
    publicLiveHelp: 'Public live help',
    dashboardChat: 'Registered dashboard',
    participantKind: 'Participant type',
    unknownUsers: 'Unknown users',
    registeredUsers: 'Registered users',
    lastActivity: 'Last activity',
    participant: 'Participant',
    device: 'Device',
    network: 'Masked network',
    turns: 'Turns',
    unknownUser: 'Unknown user',
    chatDetail: 'Chat session detail',
    chatAccessWarning: 'Opening message content records your identity, reason, session, tenant, correlation ID, and access time in the privileged audit log.',
    inspectChat: 'Record reason and inspect',
    identity: 'Identity',
    sessionDuration: 'Session duration',
    clientDetails: 'Client details and stable device hash',
    messages: 'Messages',
    userMessage: 'User message',
    assistantMessage: 'Assistant reply',
    accessReason: 'Reason for privileged access',
    started: 'Started',
    tenant: 'Tenant',
    intent: 'Intent',
    agent: 'Agent',
    usage: 'Usage',
    risk: 'Risk',
    duration: 'Duration',
    modelCalls: 'Model calls',
    toolCalls: 'Tool calls',
    request: 'User request',
    policyDecision: 'Policy decision',
    evidence: 'Evidence',
    actionProposal: 'Action proposal',
    supervisorReview: 'Supervisor review',
    reviewLabel: 'Review label',
    supervisorNote: 'Supervisor note',
    correct: 'Correct',
    incorrect: 'Incorrect',
    needsReview: 'Needs review',
    saveReview: 'Save audited review',
    reviewSaved: 'Review saved',
    promoteEvaluation: 'Promote to evaluation',
    promoted: 'Promoted',
    newEvaluation: 'New evaluation case',
    suite: 'Suite',
    inputJson: 'Input JSON',
    expectedJson: 'Expected result JSON',
    tagsJson: 'Tags JSON',
    runEvaluation: 'Run evaluation',
    runningEvaluation: 'Running…',
    releaseTarget: 'Release target',
    traceReplayOnly: 'Trace replay only',
    minimumScore: 'Minimum suite score',
    evaluationRuns: 'Evaluation runs',
    criticalFailures: 'critical failures',
    evaluationCorpus: 'Bilingual evaluation corpus',
    evaluationHint: 'Curate repeatable accounting, tax, safety, citation, and regression cases.',
  },
  access: {
    security: 'Tenant security',
    title: 'Users & access',
    subtitle: 'Manage tenant memberships, roles, and effective permissions.',
    newUser: 'New user',
    newRole: 'New role',
    users: 'users',
    roles: 'roles',
    saveFailed: 'The access change could not be saved.',
    removeConfirm: 'Remove {{name}} from this tenant?',
    userRegister: 'User register',
    userRegisterSubtitle: 'Accounts with access to this tenant',
    noUsers: 'No tenant users',
    noUsersHint: 'Create the first user and assign a role.',
    user: 'User',
    assignedRoles: 'Assigned roles',
    unknownRole: 'Unknown role',
    noRole: 'No role',
    roleRegister: 'Role catalog',
    roleRegisterSubtitle: 'Reusable access levels for this tenant',
    systemRole: 'System',
    permissionCount: '{{count}} permissions',
    protected: 'Protected role',
    editRole: 'Edit role',
    editUserTitle: 'Edit user access',
    editUserSubtitle: 'Change the roles assigned in this tenant.',
    createUserTitle: 'Create tenant user',
    createUserSubtitle: 'The account receives access only to the selected tenant.',
    displayName: 'Display name',
    passwordHint: 'Use at least 12 characters.',
    permissions: 'permissions',
    createRoleTitle: 'Create access level',
    editRoleTitle: 'Edit access level',
    roleFormSubtitle: 'Select the exact operations this role can perform.',
    roleName: 'Role name',
    roleKey: 'Role key',
  },
  dashboard: {
    generatedAt: 'Generated {{date}}',
    title: 'Financial dashboard',
    subtitle: 'Live ledger balances, workflow, and recent journal activity for this tenant.',
    databaseBacked: 'Live database',
    summary: 'Financial summary',
    debitMovement: 'Debit movement',
    creditMovement: 'Credit movement',
    journalCount: 'Journal count',
    accountCount: 'Account count',
    fromLedger: 'From the general ledger',
    availableReports: 'Available reports',
    availableReportsHint: 'Standard reports authorized for your role, ready to run.',
    monthlyActivity: 'Monthly ledger activity',
    monthlyActivitySubtitle: 'Posted debit and credit by month',
    workflow: 'Journal workflow',
    workflowSubtitle: 'Journals by current state',
    balanceConfirmed: 'Trial balance is balanced',
    balanceMismatch: 'Trial balance needs attention',
    closingTotals: 'Closing debit {{debit}} · closing credit {{credit}}',
    approvals: 'Pending approvals',
    noApprovals: 'No approvals are waiting',
    noApprovalsHint: 'Submitted and approved journals will appear here.',
    recentActivity: 'Recent activity',
    noActivity: 'No journal activity yet',
    noActivityHint: 'Create a journal to begin the tenant activity stream.',
  },
  voice: {
    title: 'Local voice commands',
    startListening: 'Start local voice command',
    stopListening: 'Stop listening',
    holdToSpeak: 'Hold the mark to speak',
    holdHint: 'Press and hold for three seconds to open the microphone.',
    transcriptTitle: 'What this device heard',
    localPrivacy: 'Private and local',
    localPrivacyHint: 'Microphone audio and transcripts stay in this browser. Only the structured command is sent to Varza for authorization and audit.',
    installTitle: 'Install local voice models',
    installHint: 'Varza downloads the pinned model package from this Varza server and keeps it on this device for offline use.',
    installBasic: 'Install basic models',
    installEnhanced: 'Install enhanced parser',
    installed: 'Basic models installed',
    enhancedInstalled: 'Enhanced parser installed',
    removeModels: 'Remove local models',
    enable: 'Enable voice commands',
    enableHint: 'Voice is off by default and the microphone opens only when you press its button.',
    language: 'Spoken language',
    autoLanguage: 'Follow the interface language',
    speechModel: 'Speech recognition model',
    speechTurbo: 'Whisper Large v3 Turbo — best Persian',
    speechSmall: 'Whisper Small — smaller download',
    speechModelHint: 'Turbo is markedly more accurate in Persian and needs a GPU. Small is a quarter of the download and runs anywhere.',
    speechModelNoGpu: 'This browser has no usable WebGPU, so Whisper Small is the only option here.',
    speechModelNoF16: 'This graphics adapter has no half-precision shaders, so Turbo runs as its larger full-precision build. Same accuracy, a bigger download.',
    enhanced: 'Enhanced local parsing',
    enhancedHint: 'Use the optional Qwen model only when deterministic and semantic routing are ambiguous.',
    models: 'On-device model packages',
    diagnostics: 'Device diagnostics',
    runtime: 'Selected runtime',
    halfPrecision: 'GPU half precision (fp16)',
    cpuThreads: 'Logical processors',
    testMicrophone: 'Test microphone permission',
    microphoneReady: 'The microphone is available. No recording was retained.',
    microphoneDenied: 'Microphone access is blocked in this browser.',
    confirm: 'Confirm command',
    confirmHint: 'Review the resolved command before continuing.',
    runTypedCommand: 'Run “{{command}}” as a local command',
    state: {
      disabled: 'Voice commands are off',
      initializing: 'Preparing local models…',
      idle: 'Ready',
      listening: 'Listening on this device…',
      transcribing: 'Transcribing locally…',
      transcribed: 'Heard on this device',
      understanding: 'Understanding locally…',
      clarifying: 'More detail is needed',
      preparing: 'Checking permissions and resolving records…',
      confirming: 'Review and confirm',
      executing: 'Executing approved command…',
      success: 'Command completed',
      error: 'Voice command failed',
    },
  },
  assistant: {
    askVarza: 'Ask Varza',
    title: 'Varza accounting assistant',
    name: 'Varza',
    subtitle: 'Intelligent accountant',
    close: 'Close Varza',
    resize: 'Resize the assistant panel',
    chat: 'Chat',
    history: 'History',
    historyHint: 'Every conversation you have had with Varza, newest first.',
    historySearch: 'Search conversations',
    historyEmpty: 'No conversations yet',
    historyEmptyHint: 'Ask Varza something and the thread will be kept here for you to return to.',
    openConversation: 'Open this conversation',
    activeConversation: 'Open now',
    schedules: 'Schedules',
    advisories: 'Advisories',
    advisoryTitle: 'Financial advisories',
    advisoryHint: 'Read-only alerts detected from overdue balances, stock, cash limits, reconciliations, and possible duplicates.',
    advisoryFailed: 'Advisories could not be loaded',
    noAdvisories: 'No active advisories',
    noAdvisoriesHint: 'Varza has not detected a condition that needs attention.',
    dismissAdvisory: 'Dismiss',
    advisorySeverity: { Info: 'Information', Warning: 'Warning', Critical: 'Critical' },
    advisoryCopy: {
      OverdueReceivablesTitle: 'Overdue receivables · {{currencyCode}}',
      OverdueReceivablesMessage: '{{count}} invoices are overdue for {{amount}} {{currencyCode}}; the oldest is {{maximumDaysOverdue}} days overdue.',
      OverduePayablesTitle: 'Overdue payables · {{currencyCode}}',
      OverduePayablesMessage: '{{count}} supplier bills are overdue for {{amount}} {{currencyCode}}; {{heldOrDisputed}} are held or disputed.',
      LowStockTitle: 'Low stock · {{productName}}',
      LowStockMessage: '{{productSku}} has {{available}} {{unitCode}} available in {{warehouseCode}} and {{incoming}} incoming.',
      CashLimitTitle: 'Cash limit exceeded · {{code}}',
      CashLimitMessage: 'The balance is {{balance}} {{currencyCode}}, exceeding the {{maximumBalance}} limit by {{excessAmount}}.',
      ReconciliationExceptionTitle: 'Bank reconciliation exception',
      ReconciliationExceptionMessage: 'Reconciliation {{id}} has an adjusted difference of {{adjustedDifference}} {{currencyCode}}.',
      PossibleDuplicateSupplierBillTitle: 'Possible duplicate supplier bill',
      PossibleDuplicateSupplierBillMessage: '{{count}} bills share the same supplier, date, currency, and amount of {{totalAmount}} {{currencyCode}}.',
    },
    noAccess: 'Assistant access is not enabled',
    noAccessHint: 'Ask a tenant administrator to add the appropriate assistant permissions to your role.',
    conversations: 'Conversations',
    newConversation: 'New conversation',
    previewMode: 'Local preview',
    connected: 'Connected',
    pendingJournal: 'Modified by Varza Robot · human verification required',
    pendingReceipt: 'Modified by Varza Robot · human verification required',
    pendingInvoice: 'Invoice modified by Varza Robot · human verification required',
    pendingSupplierPayment: 'Supplier payment modified by Varza Robot · human verification required',
    pendingBankTransfer: 'Bank transfer modified by Varza Robot · human verification required',
    pendingCashTransaction: 'Cash transaction modified by Varza Robot · human verification required',
    pendingStatementImport: 'Bank statement import modified by Varza Robot · human verification required',
    pendingPerson: 'New person proposed · confirmation required',
    aiProposalHint: 'Human verification sends it into the normal approval workflow; the robot cannot approve or post it.',
    confirmProposal: 'Verify robot document',
    confirmPerson: 'Create person',
    speak: 'Read this response aloud',
    thinking: 'Varza is reviewing the tenant data…',
    placeholder: 'Describe a transaction, ask a question, or request a report…',
    voice: 'Record a voice message',
    audioReply: 'Read reply aloud',
    elevenLabsAudio: 'ElevenLabs voice',
    transcribing: 'Transcribing your recording and answering it…',
    voiceEmpty: 'No speech was detected. Please try again.',
    voiceFailed: 'The audio message could not be processed. Please try again.',
    microphoneDenied: 'Microphone access was refused. Allow it in your browser settings, then try again.',
    recording: 'Recording',
    recordingLimit: 'Recording stops on its own after 2 minutes.',
    stopAndSend: 'Stop and send',
    discardRecording: 'Discard recording',
    speechFailed: 'The spoken response could not be played.',
    reviewNotice: 'AI entries remain pending until an authorized person confirms them.',
    send: 'Send',
    sending: 'Sending message',
    sendFailed: 'The message could not be sent. Your text is still here; please try again.',
    sendHint: 'Enter sends · Shift + Enter adds a line',
    welcome: 'How can I help with the books?',
    welcomeHint: 'I can explain balances, prepare an HTML report, or propose a balanced journal for your review.',
    promptTransaction: 'Record a 12,500,000 IRR cash office expense',
    promptReport: 'Show this month’s trial-balance summary',
    promptReview: 'Which journals still need attention?',
    scheduleName: 'Schedule name',
    schedulePrompt: 'Task for Varza',
    interval: 'Run frequency',
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    createSchedule: 'Create schedule',
    pause: 'Pause',
    resume: 'Resume',
    memory: 'Memory',
    knowledge: 'Knowledge',
    evidence: 'Evidence & reasoning',
    intent: 'Intent',
    risk: 'Risk',
    duration: 'Duration',
    tokens: 'Tokens',
    actionProposal: 'Proposed action',
    sources: 'Sources and citations',
    noEvidence: 'No external evidence was used for this response.',
    execution: 'Execution details',
    budget: 'Tenant AI budget',
    reviewerRejected: 'Rejected by the human reviewer.',
    memoryTitle: 'Governed memory',
    memoryHint: 'Only approved, scoped memories are supplied to future conversations. You can revoke them at any time.',
    knowledgeTitle: 'Tenant knowledge',
    knowledgeHint: 'Upload citeable tenant policies and procedures, review the extracted result, then publish it for retrieval.',
    knowledgeDocumentTitle: 'Document title',
    knowledgeLanguage: 'Language',
    knowledgeFile: 'Source file',
    uploadKnowledge: 'Upload and process',
    processingKnowledge: 'Processing…',
    publishKnowledge: 'Publish',
    knowledgeUploadFailed: 'The source could not be processed. Check its format or text layer.',
    characters: 'characters',
    chunks: 'chunks',
  },
  journals: {
    title: 'Journals',
    subtitle: 'Create, review, approve, post, and reverse double-entry journals.',
    newJournal: 'New journal',
    editJournal: 'Edit draft',
    searchPlaceholder: 'Search journal number, description, or document',
    allStates: 'All states',
    allTypes: 'All types',
    state: 'State',
    type: 'Type',
    sort: 'Sort',
    from: 'From',
    to: 'To',
    clearFilters: 'Clear filters',
    journalNumber: 'Journal no.',
    postingDate: 'Posting date',
    totalDebit: 'Total debit',
    totalCredit: 'Total credit',
    lineCount: 'Lines',
    unbalanced: 'Unbalanced',
    resultCount_one: '{{count}} journal',
    resultCount_other: '{{count}} journals',
    noResultsHint: 'No journal matches these filters. Widen the date window or clear them.',
    emptyRegisterHint: 'Nothing has been recorded yet. Create the first journal to open the register.',
    createTitle: 'Create journal',
    createSubtitle: 'Build a balanced entry. The server validates every posting decision.',
    editTitle: 'Edit journal',
    editSubtitle: 'Correct the draft before it is submitted for approval.',
    onlyDraftEditable: 'Only a draft journal can be edited. Reverse a posted journal instead.',
    numberIssuedByServer: 'The journal number is issued by the tenant’s own series when the draft is saved.',
    journalDetails: 'Journal details',
    descriptionPlaceholder: 'Explain the business purpose',
    descriptionTooShort: 'Describe the entry in at least four characters.',
    lines: 'Journal lines',
    linesHint: 'Every line carries one debit or one credit, never both.',
    linesDetailHint: 'The entries this journal writes to the ledger when it is posted.',
    line: 'Line',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    functionalDebit: 'Functional debit',
    functionalCredit: 'Functional credit',
    addLine: 'Add line',
    removeLine: 'Remove line {{number}}',
    balanceLine: 'Fill this line with the remaining difference',
    balanceLineOn: 'Balance the entry on line {{number}}',
    totals: 'Totals',
    difference: 'Difference',
    balancedHint: 'Debits equal credits. The server confirms this again before posting.',
    unbalancedHint: 'Debits must equal credits before this entry can be saved.',
    serverAuthority: 'Varza never treats browser calculations as authoritative.',
    saveAndSubmit: 'Save and submit',
    memo: 'Line memo',
    submit: 'Submit',
    approve: 'Approve',
    reject: 'Reject',
    post: 'Post',
    reverse: 'Reverse',
    discard: 'Discard draft',
    validate: 'Check',
    validationPassed: 'The server accepts this journal as it stands.',
    rejectHint: 'The preparer sees this reason on the draft when it comes back to them.',
    reverseHint: 'A reversal is a new posted journal. The original stays on the record untouched.',
    reversalDate: 'Reversal posting date',
    reason: 'Reason',
    reasonPlaceholder: 'Explain the decision in one or two sentences',
    discardHint: 'Journal {{number}} will be removed from the register.',
    discardIrreversible: 'A draft has never touched the ledger, so discarding it leaves nothing behind. A posted journal must be reversed instead.',
    returnedForCorrection: 'Returned for correction',
    reversedBy: 'This journal has been fully reversed.',
    openReversal: 'Open the reversal',
    reversalOf: 'This journal reverses an earlier posting.',
    openOriginal: 'Open the original',
    sourceModule: 'Source module',
    sourceDocument: 'Source document',
    functionalCurrency: 'Functional currency',
    exchangeRate: 'Exchange rate',
    postingRule: 'Posting rule',
    auditTrail: 'Audit trail',
    auditTrailHint: 'Who moved this journal through its lifecycle, and when.',
    created: 'Created',
    submitted: 'Submitted',
    rejected: 'Returned',
    approved: 'Approved',
    posted: 'Posted',
    notYet: 'Not yet',
    stateLabel: {
      Draft: 'Draft',
      AiPendingApproval: 'Modified by Varza Robot · verification required',
      Submitted: 'Submitted',
      Approved: 'Approved',
      Posted: 'Posted',
      Reversed: 'Reversed',
    },
    typeLabel: {
      Standard: 'Standard',
      Adjustment: 'Adjustment',
      Opening: 'Opening',
      Closing: 'Closing',
      Reversal: 'Reversal',
    },
    sortLabel: {
      PostingDateDescending: 'Newest first',
      PostingDateAscending: 'Oldest first',
      NumberDescending: 'Number, descending',
      NumberAscending: 'Number, ascending',
      AmountDescending: 'Largest amount',
      AmountAscending: 'Smallest amount',
    },
    lineProblem: {
      accountRequired: 'Choose an account.',
      personRequired: 'This account is analysed by counterparty, so the line must name one.',
      amountInvalid: 'That amount is not a number.',
      amountNegative: 'Amounts cannot be negative; use the other side instead.',
      oneSideRequired: 'Enter either a debit or a credit.',
      bothSides: 'A line carries one side only, never both.',
    },
  },
  ledger: {
    title: 'General ledger',
    subtitle: 'Read what the posted journals did to each account, and prove the books tie out.',
    accountLedger: 'Account ledger',
    trialBalance: 'Trial balance',
    account: 'Account',
    selectAccount: 'Choose an account',
    includeSubAccounts: 'Include sub-accounts',
    includingSubAccounts: 'including sub-accounts',
    openingBalance: 'Opening balance',
    closingBalance: 'Closing balance',
    runningBalance: 'Balance',
    broughtForward: 'Brought forward',
    carriedForward: 'Carried forward',
    opening: 'Opening',
    movement: 'Movement',
    closing: 'Closing',
    balanced: 'In balance',
    outOfBalance: 'Out of balance',
    noEntries: 'No posted entries',
    noEntriesHint: 'Only posted journals reach the ledger. Drafts and submissions are not counted here.',
    ledgerNote: 'A summary account reports the whole subtree, because it holds no entries of its own.',
    trialBalanceNote: 'Opening, movement, and closing are reported on the side each balance lands on.',
    trialBalanceHint: 'Every account with a balance or a movement in the selected window.',
  },
  accounts: {
    title: 'Chart of accounts',
    subtitle: 'A tenant-owned hierarchy with posting controls and effective dates.',
    searchPlaceholder: 'Search code or account name',
    class: {
      Asset: 'Assets',
      Liability: 'Liabilities',
      Equity: 'Equity',
      Revenue: 'Revenue',
      Expense: 'Expenses',
    },
    accountCode: 'Account code',
    accountName: 'Account name',
    accountType: 'Account class',
    normalBalance: 'Normal balance',
    debit: 'Debit',
    credit: 'Credit',
    parentAccount: 'Parent account',
    noParent: 'Top level',
    balance: 'Own balance',
    rollupBalance: 'Including sub-accounts',
    effectiveFrom: 'Effective from',
    effectiveTo: 'Effective to',
    postedActivity: 'Posted activity',
    postingAllowed: 'Direct posting',
    enabled: 'Yes',
    disabled: 'No',
    accountDetail: 'Account details',
    selectAccount: 'Select an account to inspect its controls.',
    newAccount: 'New account',
    editAccount: 'Edit account',
    deleteAccount: 'Delete account',
    deleteConfirm: 'This account has no postings and no sub-accounts, so it can be removed from the chart. This cannot be undone.',
    addChild: 'Add sub-account',
    activate: 'Activate',
    deactivate: 'Deactivate',
    showInactive: 'Show inactive',
    expand: 'Expand',
    collapse: 'Collapse',
    dialogHint: 'The server owns every accounting rule; this form only states what the account should be.',
    codeLocked: 'The code stays as it is — the postings and reports already filed under it must keep agreeing with the chart.',
    postedActivityLocked: 'Class and normal balance are fixed once the account carries posted entries.',
    parentNotPostable: 'A parent account holds its sub-accounts’ totals and cannot take journal lines itself.',
    leafHint: 'A posting account takes journal lines directly and reports only its own balance.',
    requiresPerson: 'Analysed by person',
    requiresPersonHint: 'Every line posted to this account must name the person it belongs to, so its balance breaks down per counterparty.',
    requiresPersonLocked: 'Person analysis is fixed once the account carries posted entries.',
  },
  persons: {
    title: 'Persons',
    subtitle: 'Counterparties the ledger is analysed by, each with its own subsidiary ledger.',
    searchPlaceholder: 'Search code, name or national identifier',
    kind: 'Person type',
    kindLabel: {
      Real: 'Natural person',
      Legal: 'Legal entity',
    },
    allKinds: 'Every type',
    personCode: 'Person code',
    name: 'Name',
    tradeName: 'Trade name',
    nationalId: 'National ID',
    nationalNumber: 'National number',
    nationalIdLength: 'A national ID is 10 digits.',
    nationalNumberLength: 'A national number is 11 digits.',
    identifierChecksum: 'The check digit does not match the rest of the number.',
    economicCode: 'Economic code',
    economicCodeLength: 'An economic code is 12 or 14 digits.',
    registrationNumber: 'Registration number',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    notes: 'Notes',
    postedActivity: 'Posted activity',
    owesUs: 'Owes us',
    weOwe: 'We owe',
    newPerson: 'New person',
    editPerson: 'Edit person',
    deletePerson: 'Delete person',
    deleteConfirm: 'This person has no postings, so the record can be removed from the directory. This cannot be undone.',
    activate: 'Activate',
    deactivate: 'Deactivate',
    showInactive: 'Show inactive',
    selectPerson: 'Select a person to see their details and ledger.',
    dialogHint: 'The server owns every accounting rule; this form only states who the counterparty is.',
    codeLocked: 'The code stays as it is — the ledger and documents already filed under it must keep agreeing with the directory.',
    postedActivityLocked: 'The person type is fixed once the record carries posted entries.',
    postedHint: 'This person carries posted entries, so the record can be retired but not deleted.',
    noPostingsHint: 'No entry names this person yet. Name them on a journal line for a control account to open their ledger.',
    ledgerTitle: 'Person ledger',
    ledgerSubtitle: 'Every posted entry filed under this person, oldest first.',
    from: 'From',
    to: 'To',
    openingBalance: 'Opening balance',
    totalDebit: 'Total debit',
    totalCredit: 'Total credit',
    closingBalance: 'Closing balance',
    runningBalance: 'Balance',
    document: 'Document',
    account: 'Account',
    noEntries: 'No entries in this range',
    noEntriesHint: 'Widen the date range, or post a journal line that names this person.',
    source: {
      GeneralLedger: 'Manual journal',
      Receivables: 'Receivables',
      Payables: 'Payables',
      Treasury: 'Cash & bank',
      Sales: 'Sales',
      Purchasing: 'Purchasing',
    },
    none: 'No person',
    required: 'Person required',
  },
  exchange: {
    title: 'Exchange rates',
    subtitle: 'Effective-dated directional rates with approval and usage history.',
    newRate: 'New rate',
    fromCurrency: 'From currency',
    toCurrency: 'To currency',
    rate: 'Rate',
    rateType: 'Rate type',
    source: 'Source',
    validFrom: 'Valid from',
    approvedBy: 'Approved by',
    direction: 'Direction',
    directPreview: 'Direct preview',
    inversePreview: 'Inverse preview',
    usage: 'Document usage',
    history: 'Rate history',
    rateEditor: 'Directional rate editor',
    editorHint: 'Direction is explicit and never inferred from field position.',
    submitApproval: 'Submit for approval',
    resultCount: '{{count}} persisted rate(s)',
    noRates: 'No exchange rates',
    noRatesHint: 'Create the first directional rate for this tenant.',
    createTitle: 'Create exchange rate',
    rateDetails: 'Rate details',
    spot: 'Spot',
    closing: 'Closing',
    actionFailed: 'The exchange-rate action could not be completed.',
    invalidRate: 'Choose two different currencies and enter a rate greater than zero.',
    fixedRialTomanRate: 'Rial and toman always use the fixed 10-to-1 conversion; create a dollar rate instead.',
    sourceType: {
      Manual: 'Manual',
      Provider: 'Provider',
      Contract: 'Contract',
      Bank: 'Bank',
      Import: 'Import',
    },
    noRecalculation: 'Posted documents retain their original rate evidence.',
  },
  inventory: {
    title: 'Inventory',
    subtitle: 'Live availability, valuation, movement, transfer, and count workflows.',
    stock: 'Stock',
    products: 'Products',
    warehouses: 'Warehouses',
    lots: 'Lots',
    reservations: 'Reservations',
    transfers: 'Transfers',
    counts: 'Counts',
    movements: 'Movements',
    productSearch: 'Search product, SKU, or barcode',
    allWarehouses: 'All warehouses',
    lowStockOnly: 'Low stock only',
    onHand: 'On hand',
    available: 'Available',
    reserved: 'Reserved',
    incoming: 'Incoming',
    valuation: 'Valuation',
    activeSkus: 'Active SKUs',
    negativeStock: 'Negative stock',
    provisionalCost: 'Provisional cost',
    product: 'Product',
    sku: 'SKU',
    warehouse: 'Warehouse',
    stockStatus: 'Stock status',
    healthy: 'Healthy',
    low: 'Low',
    unitCost: 'Unit cost',
    unitCostHint: 'Entered in {{currency}}, the functional currency this stock is valued in.',
    quantity: 'Quantity',
    direction: 'Direction',
    reason: 'Reason',
    manualEntry: 'Manual entry',
    adjustmentDefaultReason: 'Manual stock adjustment',
    noStock: 'No stock has been received into this workspace yet.',
    noLots: 'No lots have been registered for the lot-tracked products.',
    noMovements: 'No stock movements have been recorded yet.',
    noReservations: 'No stock is reserved right now.',
    noTransfers: 'No warehouse transfers have been raised yet.',
    noCounts: 'No stock counts have been started yet.',
    receive: 'Receive',
    issue: 'Issue',
    adjust: 'Adjust',
    release: 'Release',
    quarantineAction: 'Quarantine',
    recall: 'Recall',
    recallReference: 'Recall raised from the inventory workspace',
    recordReceipt: 'Record receipt',
    recordIssue: 'Record issue',
    recordAdjustment: 'Record adjustment',
    movementHint: 'Quantities are entered in the product base unit; cost is drawn from the valuation layers on an issue.',
    adjustmentGain: 'Gain — stock found',
    adjustmentLoss: 'Loss — stock written off',
    newProduct: 'New product',
    editProduct: 'Edit product',
    productHint: 'SKU is unique per tenant and cannot change once the product exists.',
    productLockedHint: 'This product has movement history, so its base unit and valuation method are locked.',
    productName: 'Name',
    baseUnit: 'Base unit',
    productType: 'Product type',
    trackingPolicy: 'Tracking policy',
    valuationMethod: 'Valuation method',
    valuationLocked: 'Locked by movement history.',
    barcode: 'Barcode',
    category: 'Category',
    reorderPoint: 'Reorder point',
    safetyStock: 'Safety stock',
    effectiveFrom: 'Effective from',
    newWarehouse: 'New warehouse',
    editWarehouse: 'Edit warehouse',
    warehouseHint: 'The code identifies the warehouse on every movement and cannot change afterwards.',
    warehouseName: 'Name',
    code: 'Code',
    branch: 'Branch',
    address: 'Address',
    negativeStockAllowed: 'Negative stock allowed',
    negativeStockHint: 'When allowed, an over-issue is carried at a provisional cost until a receipt corrects it.',
    locations: 'Locations',
    newLocation: 'New location',
    locationHint: 'A quarantine location cannot also be pickable.',
    locationName: 'Name',
    location: 'Location',
    zone: 'Zone',
    pickable: 'Pickable',
    receivable: 'Receivable',
    quarantine: 'Quarantine',
    inTransit: 'In transit',
    locationFlags: 'Flags',
    newLot: 'New lot',
    lotHint: 'Lot numbers are unique per product and drive expiry-first allocation.',
    lot: 'Lot',
    lotNumber: 'Lot number',
    supplierLot: 'Supplier lot',
    manufacturedOn: 'Manufactured on',
    expiresOn: 'Expires on',
    expiryRequired: 'This product tracks expiry, so a date is required.',
    lotRequiredHint: 'This product is lot tracked, so every movement names a lot.',
    noTrackedProducts: 'No lot-tracked product exists yet. Set a product tracking policy to Lot first.',
    movementType: 'Movement',
    sourceDocument: 'Source document',
    requiredDate: 'Required date',
    requested: 'Requested',
    allocated: 'Allocated',
    consumed: 'Consumed',
    newTransfer: 'New transfer',
    transferHint: 'Issuing the source leaves stock in transit until the destination receives it.',
    documentNumber: 'Document number',
    sourceWarehouse: 'From warehouse',
    destinationWarehouse: 'To warehouse',
    addLine: 'Add line',
    lines: 'Lines',
    route: 'Route',
    inTransitQuantity: 'In transit',
    issueTransfer: 'Issue',
    receiveTransfer: 'Receive',
    receiveTransferHint: 'Receive up to the quantity still in transit on each line.',
    newCount: 'New count',
    countHint: 'The system quantity is frozen onto the sheet when it is generated.',
    countScope: 'Products to count',
    blindCount: 'Blind count',
    blindCountHint: 'This is a blind count, so the system quantity is hidden from the counter.',
    blindCountShort: 'Blind',
    countSheet: 'Count sheet',
    countSheetHint: 'Enter the quantity observed on the shelf for each line.',
    systemQuantity: 'System',
    variance: 'Variance',
    enterCount: 'Enter count',
    submit: 'Submit',
    approve: 'Approve',
    recount: 'Recount',
    post: 'Post',
    productType_StockItem: 'Stock item',
    productType_NonStockItem: 'Non-stock item',
    productType_Service: 'Service',
    productType_Bundle: 'Bundle',
    tracking_None: 'None',
    tracking_Lot: 'Lot',
    tracking_Serial: 'Serial',
    tracking_LotAndExpiry: 'Lot and expiry',
    valuation_MovingAverage: 'Moving average',
    valuation_Fifo: 'FIFO',
    lotStatus_Quarantined: 'Quarantined',
    lotStatus_Released: 'Released',
    lotStatus_Blocked: 'Blocked',
    lotStatus_Recalled: 'Recalled',
    movement_Opening: 'Opening',
    movement_PurchaseReceipt: 'Purchase receipt',
    movement_SalesIssue: 'Sales issue',
    movement_CustomerReturn: 'Customer return',
    movement_SupplierReturn: 'Supplier return',
    movement_TransferIssue: 'Transfer issue',
    movement_TransferReceipt: 'Transfer receipt',
    movement_AdjustmentGain: 'Adjustment gain',
    movement_AdjustmentLoss: 'Adjustment loss',
    movement_CountVariance: 'Count variance',
    movement_StatusChange: 'Status change',
    movement_CostAdjustment: 'Cost adjustment',
    serials: 'Serials',
    units: 'Units',
    reconciliation: 'Reconciliation',
    newSerial: 'New serial',
    serialNumber: 'Serial number',
    serialHint: 'A serial is one unit, so the registry decides whether it can be received or issued.',
    noSerials: 'No serials have been registered yet.',
    noSerialisedProducts: 'No serial-tracked product exists yet. Set a product tracking policy to Serial first.',
    scrap: 'Scrap',
    newUnit: 'New unit',
    unitName: 'Name',
    unitHint: 'The dimension is what stops a mass being converted into a count.',
    dimension: 'Dimension',
    dimensionHint: 'Conversion is only defined between units of the same dimension.',
    newConversion: 'New conversion',
    conversionHint: 'Factors are exact decimals and apply in both directions.',
    fromUnit: 'From unit',
    toUnit: 'To unit',
    factor: 'Factor',
    factorHint: 'One {{from}} equals this many {{to}}.',
    scope: 'Scope',
    scopeHint: 'A product-specific conversion overrides the global one.',
    allProducts: 'All products',
    entered: 'Entered',
    newReservation: 'New reservation',
    reservationHint: 'Availability is locked while the request is measured, so two requests cannot take the same stock.',
    allocationStrategy: 'Allocation strategy',
    allowPartial: 'Accept a partial allocation',
    untracked: 'Untracked',
    strategy_Fifo: 'FIFO — oldest receipt first',
    strategy_Fefo: 'FEFO — nearest expiry first',
    strategy_SpecificLot: 'Specific lot',
    strategyHint_Fifo: 'Draws from the lot that arrived first.',
    strategyHint_Fefo: 'Draws from the lot that expires soonest, which is what perishable stock needs.',
    strategyHint_SpecificLot: 'Draws only from the lot you name and never spills into another.',
    controlAccount: 'Inventory control account',
    subledgerValue: 'Stock ledger value',
    noReconciliation: 'No stock resolves to an inventory account yet. Set a default account mapping first.',
    unmappedProducts: '{{count}} product(s) hold stock but resolve to no inventory account, so the tie-out is incomplete.',
    dimension_Count: 'Count',
    dimension_Mass: 'Mass',
    dimension_Volume: 'Volume',
    dimension_Length: 'Length',
    dimension_Area: 'Area',
    dimension_Time: 'Time',
  },
  reports: {
    title: 'Reports',
    centerSubtitle: 'Run the authorized standard reports, inspect reproducible totals, and keep analytical views in one place.',
    standardReports: 'Standard reports',
    standardReportsHint: 'Database-backed accounting reports available to your current role.',
    standardReportsUnavailable: 'The report catalog could not be loaded. Apply the reporting migration and confirm role permissions.',
    noAuthorizedReports: 'No standard reports are available',
    noAuthorizedReportsHint: 'Ask an administrator to grant the report module and report-specific permissions.',
    savedAnalyticalReports: 'Saved analytical reports',
    readOnly: 'Read only',
    runReport: 'Run report',
    running: 'Running…',
    readyToRun: 'Ready to run',
    readyToRunHint: 'Choose filters above, then run the report against posted accounting data.',
    requiredFilter: '{{filter}} is required.',
    from: 'From',
    to: 'To',
    functionalCurrency: 'Functional currency',
    fiscalYearNumber: 'Fiscal year {{number}}',
    multiSelectHint: 'Hold Ctrl or Command to select more than one value.',
    defaultPosted: 'Leave blank to use the report default (posted only where applicable).',
    commaSeparated: 'Comma-separated values',
    reproducibleResult: 'Audited, reproducible result',
    rowsCount: '{{count}} rows',
    categories: {
      Accounting: 'Accounting',
      Audit: 'Audit and control',
    },
    standard: {
      'chart-of-accounts': {
        name: 'Chart of accounts',
        description: 'Account hierarchy, classifications, posting rules, and effective dates.',
      },
      'general-ledger': {
        name: 'General ledger',
        description: 'Posted journal lines with running balances and source-document references.',
      },
      'trial-balance': {
        name: 'Trial balance',
        description: 'Opening, period, and closing debit and credit by account.',
      },
      'journal-register': {
        name: 'Journal entry register',
        description: 'Journal status, line totals, source modules, and document references.',
      },
    },
    parameters: {
      'date-range': 'Date range',
      'fiscal-year': 'Fiscal year',
      'accounting-period': 'Accounting period',
      accounts: 'Accounts',
      'account-range': 'Account code range',
      persons: 'Persons',
      currency: 'Currency',
      'posting-status': 'Posting status',
      'journal-types': 'Journal types',
      'source-modules': 'Source modules',
      search: 'Search',
    },
    columns: {
      'account-code': 'Account code',
      'account-name': 'Account name',
      'parent-account-code': 'Parent account',
      'account-class': 'Account class',
      'normal-balance': 'Normal balance',
      'is-active': 'Active',
      'posting-allowed': 'Posting allowed',
      'requires-person': 'Person required',
      'effective-from': 'Effective from',
      'effective-to': 'Effective to',
      'posting-date': 'Posting date',
      'journal-number': 'Journal number',
      'source-document-number': 'Source document',
      'person-name': 'Person',
      description: 'Description',
      debit: 'Debit',
      credit: 'Credit',
      'running-balance': 'Running balance',
      'opening-debit': 'Opening debit',
      'opening-credit': 'Opening credit',
      'period-debit': 'Period debit',
      'period-credit': 'Period credit',
      'closing-debit': 'Closing debit',
      'closing-credit': 'Closing credit',
      'journal-type': 'Journal type',
      'journal-state': 'Journal state',
      'line-count': 'Lines',
      'source-module': 'Source module',
    },
    builderSubtitle: 'Build reusable charts from posted journal data and export the current result.',
    exportCsv: 'Export CSV',
    savedReports: 'Saved reports',
    noSavedReports: 'No saved reports',
    noSavedReportsHint: 'Create the first analytical view below.',
    createReport: 'Create a report',
    reportName: 'Report name',
    dimensionLabel: 'Group by',
    measureLabel: 'Measure',
    chartTypeLabel: 'Chart type',
    dimension: {
      Month: 'Month',
      State: 'Journal state',
      JournalType: 'Journal type',
    },
    measure: {
      Debit: 'Debit',
      Credit: 'Credit',
      JournalCount: 'Journal count',
    },
    chartType: {
      Bar: 'Bar chart',
      Line: 'Line chart',
      Pie: 'Pie chart',
    },
    createFailed: 'The report could not be saved.',
    nameRequired: 'Enter a report name.',
    creating: 'Creating…',
    saveAndRun: 'Save and run',
    selectOrCreate: 'Select or create a report',
    selectOrCreateHint: 'Saved reports run directly against posted journal data.',
    generatedAt: 'Generated {{date}}',
    noReportData: 'No posted data for this report',
    noReportDataHint: 'Post journals or choose a different grouping or measure.',
    group: 'Group',
  },
  modules: {
    receivablesTitle: 'Accounts receivable',
    receivablesDescription: 'Customer balances, open items, collections, and aging.',
    payablesTitle: 'Accounts payable',
    payablesDescription: 'Supplier bills, three-way matching, payment runs, and aging.',
    purchasingTitle: 'Purchasing',
    purchasingDescription: 'Purchase orders, receipts, matching, and supplier returns.',
    salesTitle: 'Sales',
    salesDescription: 'Quotes, orders, fulfillment, invoices, and customer returns.',
    availableSoon: 'Workspace foundation ready',
    foundationMessage: 'Navigation, permissions, localization, and route boundaries are in place for this module.',
    openItems: 'Open items',
    dueSoon: 'Due soon',
    activeDocuments: 'Active documents',
    exceptionQueue: 'Exception queue',
  },
  docStatus: {
    Draft: 'Draft',
    AiPendingVerification: 'Modified by Varza Robot · verification required',
    Approved: 'Approved',
    Posted: 'Posted',
    Calculated: 'Calculated',
    PartiallyPaid: 'Partially paid',
    Paid: 'Paid',
    Voided: 'Voided',
    Reversed: 'Reversed',
    Applied: 'Applied',
    Released: 'Released',
    Settled: 'Settled',
    Submitted: 'Submitted',
    Confirmed: 'Confirmed',
    Rejected: 'Rejected',
    Superseded: 'Superseded',
    Cancelled: 'Cancelled',
    Open: 'Open',
    InReview: 'In review',
    Closed: 'Closed',
    Active: 'Active',
    Inactive: 'Inactive',
    Frozen: 'Frozen',
    Suspended: 'Suspended',
    PendingVerification: 'Pending verification',
    Verified: 'Verified',
    Sealed: 'Sealed',
    InTransit: 'In transit',
    Deposited: 'Deposited',
    Counted: 'Counted',
    Staged: 'Staged',
    Validated: 'Validated',
    Accepted: 'Accepted',
    Duplicate: 'Duplicate',
    Unmatched: 'Unmatched',
    Matched: 'Matched',
    Excluded: 'Excluded',
    Receipt: 'Receipt',
    Disbursement: 'Disbursement',
    Transfer: 'Transfer',
    Adjustment: 'Adjustment',
    OutstandingDeposit: 'Deposit in transit',
    OutstandingPayment: 'Outstanding payment',
    BankOnlyCharge: 'Bank-only charge',
    BankOnlyCredit: 'Bank-only credit',
    LedgerError: 'Ledger error',
    BankError: 'Bank error',
    Issued: 'Issued',
    PartiallyReceived: 'Partially received',
    Received: 'Received',
    Counting: 'Counting',
    PendingApproval: 'Pending approval',
    Quarantined: 'Quarantined',
    Blocked: 'Blocked',
    Recalled: 'Recalled',
    FullyAllocated: 'Fully allocated',
    PartiallyAllocated: 'Partially allocated',
    Consumed: 'Consumed',
    Expired: 'Expired',
    Registered: 'Registered',
    InStock: 'In stock',
    Scrapped: 'Scrapped',
  },
  subledger: {
    customers: 'Customers',
    suppliers: 'Suppliers',
    openItems: 'Open items',
    receipts: 'Receipts',
    payments: 'Payments',
    creditNotes: 'Credit notes',
    debitNotes: 'Debit notes',
    bills: 'Bills',
    paymentRuns: 'Payment runs',
    aging: 'Aging',
    proposal: 'Payment proposal',
    code: 'Code',
    name: 'Name',
    creditLimit: 'Credit limit',
    contact: 'Contact',
    documentNumber: 'Document',
    invoiceDate: 'Invoice date',
    billDate: 'Bill date',
    dueDate: 'Due date',
    total: 'Total',
    open: 'Open',
    applied: 'Applied',
    unapplied: 'Unapplied',
    daysOverdue: 'Days overdue',
    onHold: 'On hold',
    disputed: 'Disputed',
    matchException: 'Match exception',
    withholdingTax: 'Withholding tax',
    discount: 'Discount',
    net: 'Net',
    exchangeRate: 'Rate',
    realizedFx: 'Realized FX',
    reason: 'Reason',
    asOf: 'As of',
    bucketCurrent: 'Current',
    bucketOverdue: '{{range}} days',
    controlAccount: 'Control account',
    difference: 'Difference',
    reconciled: 'Ties to the control account',
    outOfBalance: 'Does not tie to the control account',
    eligible: 'Eligible',
    ineligible: 'Ineligible',
    noRecords: 'No documents match this view yet.',
    functionalTotal: 'Functional total',
    prepayment: 'Prepayment',
  },
  commerce: {
    orderNumber: 'Order number',
    customer: 'Customer',
    supplier: 'Supplier',
    paymentTerms: 'Payment terms',
    deliveryAddress: 'Delivery address',
    unitPrice: 'Unit price',
    tax: 'Tax',
    lines: 'Lines',
    shipmentNumber: 'Shipment number',
    inventoryAccount: 'Inventory account',
    cogsAccount: 'Cost of goods sold account',
    expectedDate: 'Expected date',
    receiptNumber: 'Receipt number',
    grniAccount: 'Goods received not invoiced account',
  },
  sales: {
    title: 'Sales',
    subtitle: 'Customer orders, approvals, stock reservations, and shipment posting in one register.',
    newOrder: 'New sales order',
    orderHint: 'Create a customer order with its commercial terms and first product line.',
    postShipment: 'Post shipment',
    shipmentHint: 'Issue the outstanding reserved quantities and recognize their inventory cost.',
    orders: 'Orders',
    fulfillment: 'Fulfillment',
    noOrders: 'No sales orders have been created yet.',
    orderValue: 'Order value',
    openOrders: 'Open orders',
    awaitingApproval: 'Awaiting approval',
    readyToShip: 'Ready to ship',
    confirm: 'Confirm',
    reserve: 'Reserve stock',
    ship: 'Ship',
    issueInvoice: 'Issue invoice',
  },
  purchasing: {
    title: 'Purchasing',
    subtitle: 'Supplier orders, approvals, receiving, and inventory accruals in one register.',
    newOrder: 'New purchase order',
    orderHint: 'Create a supplier order with its expected date and first product line.',
    postReceipt: 'Post goods receipt',
    receiptHint: 'Receive the outstanding order quantities into inventory and accrue the supplier liability.',
    orders: 'Orders',
    receiving: 'Receiving',
    noOrders: 'No purchase orders have been created yet.',
    orderValue: 'Order value',
    openOrders: 'Open orders',
    awaitingApproval: 'Awaiting approval',
    readyToReceive: 'Ready to receive',
    receive: 'Receive',
    issueBill: 'Issue supplier bill',
  },
  automation: {
    title: 'Document automation',
    subtitle: 'Control cross-module document generation, approvals, posting, retries, reversals, and audit lineage.',
    salesInvoiceHint: 'Create the customer invoice from shipped quantities. The workflow will wait for approval or post automatically according to policy.',
    supplierBillHint: 'Create the supplier bill from accepted receipt quantities with three-way match and GRNI clearing.',
    documentNumber: 'Document number',
    dueDate: 'Due date',
    receivableAccount: 'Receivable account',
    revenueAccount: 'Revenue account',
    outputTaxAccount: 'Output tax account',
    payableAccount: 'Payable account',
    varianceAccount: 'Purchase price variance account',
    inputTaxAccount: 'Input tax account',
    policy: 'Automation policy',
    processQueue: 'Process queue',
    noProcesses: 'No document automation has been queued yet.',
    source: 'Source document',
    target: 'Generated document',
    attempts: 'Attempts',
    error: 'Last error',
    autoApproveSales: 'Automatically approve sales invoices',
    autoPostSales: 'Automatically post sales invoices',
    autoApprovePurchasing: 'Automatically approve supplier bills',
    autoPostPurchasing: 'Automatically post supplier bills',
    publishToVii: 'Publish posted documents to VII when consent allows it',
    functionalCurrency: 'Functional currency',
    maximumFailures: 'Maximum retry failures',
    savePolicy: 'Save policy',
    retry: 'Retry',
    cancel: 'Cancel process',
    reverse: 'Reverse',
    reverseReason: 'Reason for reversal',
  },
  payroll: {
    title: 'Payroll & labor',
    subtitle: 'Employee compensation, configurable deductions, payroll calculation, payslips, approval, and ledger posting.',
    employees: 'Employees',
    runs: 'Payroll runs',
    payslips: 'Payslips',
    newEmployee: 'New employee',
    employeeHint: 'Link a real person to an employee number and define their compensation and rates.',
    newRun: 'New payroll run',
    runHint: 'Choose the period, accounts, and employee adjustments; Varza calculates each payslip before approval.',
    noEmployees: 'No employees have been configured yet.',
    noRuns: 'No payroll runs have been calculated yet.',
    noPayslips: 'Calculate a payroll run to review payslips.',
    noAvailablePersons: 'Every active real person is already linked to payroll. Add another person before creating an employee.',
    noCurrencyEmployees: 'No active employees use this currency.',
    activeEmployees: 'Active employees',
    monthlyBase: 'Base monthly compensation',
    pendingRuns: 'Runs awaiting posting',
    latestNet: 'Latest net payroll',
    employeeNumber: 'Employee number',
    employee: 'Employee',
    hireDate: 'Hire date',
    baseSalary: 'Base salary',
    housingAllowance: 'Housing allowance',
    foodAllowance: 'Food allowance',
    employeeInsuranceRate: 'Employee insurance (%)',
    employerInsuranceRate: 'Employer insurance (%)',
    incomeTaxRate: 'Income tax (%)',
    bankAccountNumber: 'Bank account number / IBAN',
    insuranceRates: 'Insurance (employee / employer)',
    terminate: 'Terminate',
    terminateConfirm: 'Terminate this employee as of today?',
    calculate: 'Calculate payroll',
    runNumber: 'Run number',
    period: 'Pay period',
    periodStart: 'Period start',
    periodEnd: 'Period end',
    paymentDate: 'Payment date',
    taxExemption: 'Tax exemption per employee',
    payrollAccounts: 'Ledger accounts',
    salaryExpense: 'Salary expense',
    employerContributionExpense: 'Employer contribution expense',
    payrollPayable: 'Net payroll payable',
    insurancePayable: 'Insurance payable',
    taxPayable: 'Tax payable',
    deductionsPayable: 'Other deductions payable',
    currencyEmployeesHint: 'Only active employees paid in {{currency}} are included.',
    overtimeHours: 'Overtime hours',
    overtimeHoursFor: 'Overtime hours for {{name}}',
    overtimeRate: 'Overtime hourly rate',
    bonus: 'Bonus',
    otherAllowances: 'Other allowances',
    otherDeductions: 'Other deductions',
    grossEarnings: 'Gross earnings',
    employeeInsurance: 'Employee insurance',
    employerInsurance: 'Employer insurance',
    incomeTax: 'Income tax',
    netPay: 'Net pay',
    selectRun: 'Payroll run',
    post: 'Post to ledger',
  },
  receivables: {
    title: 'Accounts receivable',
    subtitle: 'Customer credit, open items, receipts, credit notes, and aging that ties to the control account.',
    newReceipt: 'New receipt',
    totalOpen: 'Open receivables',
    overdue: 'Overdue',
    unappliedReceipts: 'Unapplied receipts',
    creditHolds: 'Credit holds',
    holdReason: 'Hold reason',
    creditStatus: 'Credit status',
    available: 'Available credit',
    writeOffs: 'Write-offs',
    collections: 'Collections',
    newInvoice: 'New invoice',
    newCreditNote: 'New credit note',
    newWriteOff: 'New write-off',
    newActivity: 'New activity',
    approve: 'Approve',
    post: 'Post',
    apply: 'Apply',
    unapply: 'Unapply',
    reverse: 'Reverse',
    voidDocument: 'Void',
    hold: 'Place on hold',
    releaseHold: 'Release hold',
    statement: 'Statement',
    clearSmallBalance: 'Clear small balance',
    complete: 'Complete',
    cancelActivity: 'Cancel',
    invoiceNumber: 'Invoice number',
    receiptNumber: 'Receipt number',
    creditNoteNumber: 'Credit-note number',
    writeOffNumber: 'Write-off number',
    customer: 'Customer',
    lineDescription: 'Description',
    netAmount: 'Net amount',
    taxAmount: 'Tax amount',
    taxRate: 'Tax rate',
    taxInclusive: 'Price includes tax',
    addLine: 'Add line',
    removeLine: 'Remove line {{number}}',
    revenueAccount: 'Revenue account',
    outputTaxAccount: 'Output tax account',
    receivableAccount: 'Receivable control account',
    cashAccount: 'Bank or cash account',
    advanceAccount: 'Customer advance account',
    expenseAccount: 'Bad-debt expense account',
    fxGainAccount: 'Realized FX gain account',
    fxLossAccount: 'Realized FX loss account',
    amount: 'Amount',
    paymentMethod: 'Payment method',
    paymentMethodBank: 'Bank transfer',
    paymentMethodCash: 'Cash',
    paymentMethodCheque: 'Cheque',
    approvalThreshold: 'Approval threshold',
    smallBalanceThreshold: 'Small-balance threshold',
    elevatedApproval: 'Apply elevated authorization',
    creditOverride: 'Request a credit override',
    overrideReason: 'Override reason',
    applyTo: 'Apply to open item',
    activityKind: 'Activity type',
    activityDate: 'Activity date',
    followUpDate: 'Follow-up date',
    promisedAmount: 'Promised amount',
    promisedDate: 'Promised date',
    templateCode: 'Template',
    notes: 'Notes',
    outcome: 'Outcome',
    openOnly: 'Open activities only',
    from: 'From',
    to: 'To',
    openingBalance: 'Opening balance',
    closingBalance: 'Closing balance',
    overdueBalance: 'Overdue balance',
    exposure: 'Exposure',
    openInvoices: 'Open invoices',
    openOrders: 'Open orders',
    unbilledShipments: 'Unbilled shipments',
    earlyDiscount: 'Early-settlement discount',
    blocked: 'Blocked',
    allowed: 'Allowed',
    saved: 'Saved.',
    noAccountsConfigured: 'Enter the ledger account identifiers this document should post to.',
  },
  payables: {
    title: 'Accounts payable',
    subtitle: 'Supplier master data, bills with three-way matching, payments, payment runs, and aging.',
    newPayment: 'New payment',
    totalOpen: 'Open payables',
    dueThisWeek: 'Due within seven days',
    heldBills: 'Held or disputed',
    runsAwaitingApproval: 'Runs awaiting approval',
    withholdingRate: 'Withholding %',
    bankAccount: 'Bank account',
    selected: 'Selected',
    fileReference: 'File reference',
    breakdown: 'Balance breakdown',
    breakdownHint: 'Items that sit alongside the aged open bills. They are reported separately so the payables control account can be explained line by line.',
    advances: 'Supplier advances',
    advancesHint: 'Prepayments and unapplied payments still owed back by the supplier.',
    debitBalances: 'Debit balances',
    debitBalancesHint: 'Suppliers whose net position is a receivable. They are reclassified out of payables for statement presentation.',
    disputedBills: 'Disputed bills',
    disputedBillsHint: 'Open bills flagged as disputed and excluded from payment proposals until resolved.',
    receiptAccruals: 'Goods received not invoiced',
    receiptAccrualsHint: 'Receipts accrued against bills that have not been matched or approved yet.',
    unappliedAmount: 'Unapplied',
    accruedAmount: 'Accrued',
    functionalAmount: 'Functional',
    exchangeRate: 'Rate',
    paymentFile: 'Payment file',
    paymentFileHint: 'Generate the bank instruction file for a confirmed run. The stored hash proves the file that was released to the bank.',
    fileFormat: 'File format',
    generateFile: 'Generate file',
    download: 'Download',
    fileHash: 'File hash',
    instructionCount: 'Instructions',
    noBreakdownItems: 'Nothing to report in this section.',
  },
  treasury: {
    title: 'Cash & bank',
    subtitle: 'Bank accounts, cash boxes, deposits, statement imports, and bank reconciliation.',
    bankAccounts: 'Bank accounts',
    cashBoxes: 'Cash boxes',
    cashTransactions: 'Cash transactions',
    deposits: 'Deposits',
    statements: 'Statement imports',
    reconciliation: 'Reconciliation',
    accountNumber: 'Account number',
    bankName: 'Bank',
    iban: 'IBAN',
    lastReconciled: 'Reconciled through',
    neverReconciled: 'Never reconciled',
    float: 'Float',
    maximumBalance: 'Maximum balance',
    kind: 'Kind',
    signedAmount: 'Movement',
    fileName: 'File',
    format: 'Format',
    period: 'Period',
    lines: 'Lines',
    duplicates: 'Duplicates',
    balanceDrift: 'Balance drift',
    openingBalance: 'Opening',
    closingBalance: 'Closing',
    statementBalance: 'Statement closing',
    ledgerBalance: 'Ledger closing',
    outstandingDeposits: 'Deposits in transit',
    outstandingPayments: 'Outstanding payments',
    bankOnlyItems: 'Bank-only items',
    errorItems: 'Error corrections',
    adjustedDifference: 'Adjusted difference',
    matchedTotal: 'Matched',
    balanced: 'Explained in full',
    unbalanced: 'Unexplained difference',
    reconcilingItems: 'Reconciling items',
    cleared: 'Cleared',
    outstanding: 'Outstanding',
    cashOnHand: 'Cash on hand',
    depositsInTransit: 'Deposits in transit',
    openReconciliations: 'Open reconciliations',
    frozenAccounts: 'Frozen accounts',
    automation: 'Automation',
    mappingProfiles: 'Mapping profiles',
    mappingProfilesHint: 'Column maps that translate a bank export into statement lines. CSV and XLSX imports require a profile; MT940, CAMT.053, and OFX are self-describing.',
    dateFormat: 'Date format',
    culture: 'Culture',
    amountConvention: 'Amount convention',
    signedAmountConvention: 'Single signed column',
    debitCreditConvention: 'Separate debit and credit columns',
    scopedToAccount: 'Scoped account',
    allAccounts: 'All accounts',
    journalProposals: 'Journal proposals',
    journalProposalsHint: 'Draft entries suggested for unmatched statement lines. Review the classification, then accept to post or reject with a reason.',
    proposalDate: 'Proposal date',
    category: 'Category',
    counterAccount: 'Counter account',
    matchedRule: 'Rule',
    suspense: 'Suspense',
    moneyIn: 'Money in',
    moneyOut: 'Money out',
    accept: 'Accept',
    reject: 'Reject',
    reclassify: 'Reclassify',
    postedJournal: 'Journal',
    rejectionReason: 'Rejection reason',
    noProposals: 'No draft proposals are waiting for review.',
    cashLimitAlerts: 'Cash limit alerts',
    cashLimitAlertsHint: 'Cash boxes holding more than their approved ceiling. Deposit the excess to bring the box back within its limit.',
    balance: 'Balance',
    excessAmount: 'Excess over limit',
    custodian: 'Custodian',
    noCashLimitAlerts: 'Every cash box is within its approved ceiling.',
    supportedFormats: 'Supported formats',
    requiresProfile: 'Needs mapping profile',
    selfDescribing: 'Self-describing',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Tenant localization, currency preferences, and account security.',
    localization: 'Localization',
    localizationHint: 'Choose the interface language, calendar, and appearance.',
    financial: 'Financial settings',
    financialHint: 'Set the tenant default currency and the current dollar conversion rate.',
    security: 'Security & sessions',
    securityHint: 'Manage external sign-in and two-factor authentication.',
    voice: 'AI & voice',
    voiceHint: 'Install and manage private, on-device Persian and English voice commands.',
    device: 'This device',
    deviceHint: 'Install Varza here, and decide whether this device keeps an offline copy.',
    backup: 'Backup',
    backupHint: 'Download a complete, tenant-scoped system and database backup.',
    defaultLanguage: 'Default language',
    displayCalendar: 'Display calendar',
    gregorian: 'Gregorian',
    solarHijri: 'Solar Hijri',
    islamicHijri: 'Islamic Hijri',
    theme: 'Appearance',
    calendarFollowsLanguage: 'Calendar choice is independent of the interface language and is saved on this device.',
    digitSystem: 'Display digits',
    latinDigits: 'Latin (0123456789)',
    persianDigits: 'Persian (۰۱۲۳۴۵۶۷۸۹)',
    arabicIndicDigits: 'Arabic-Indic (٠١٢٣٤٥٦٧٨٩)',
    iranCurrencyDisplay: 'Iran currency display',
    iranCurrencyDisplayRial: 'Rial',
    iranCurrencyDisplayToman: 'Toman',
    iranCurrencyDisplayBoth: 'Rial and Toman',
    iranCurrencyDisplayHint: 'Only display changes; the stored accounting denomination and original amounts stay unchanged.',
    dualCalendarHint: 'Dates are stored canonically as ISO values; forms also show their equivalents in the other calendars.',
    supportedCurrencies: 'System currencies',
    supportedCurrenciesHint: 'The tenant can use only these enabled currencies.',
    enabled: 'Enabled',
    defaultCurrency: 'Tenant default currency',
    defaultCurrencyHint: 'Saved as the preferred currency for this tenant.',
    usdToIrrRate: 'Dollar to rial rate',
    usdRateHint: 'Enter the number of rials equal to one dollar.',
    currentUsdRate: 'Current rate: 1 $ (Dollar) = {{rate}} ریال',
    rialsPerToman: 'Rials per toman',
    rialTomanFactorHint: 'Enter the number of rials equal to one toman for this tenant.',
    currentRialTomanFactor: 'Current denomination policy: 1 تومان = {{factor}} ریال',
    currencySaved: 'Currency settings were saved.',
    currencySaveFailed: 'Currency settings could not be saved.',
    usdRateInvalid: 'Enter a dollar rate greater than zero.',
    rialTomanFactorInvalid: 'Enter a Rial/Toman factor greater than zero.',
    externalSignIn: 'External sign-in',
    externalSignInHint: 'Link a provider only while signed in. Future logins use the provider\'s stable account identifier.',
    providerLinked: 'Linked to your Varza account',
    providerNotLinked: 'Not linked',
    link: 'Link account',
    unlink: 'Unlink',
    notConfigured: 'Not configured',
    twoFactor: 'Two-factor authentication',
    twoFactorHint: 'Activate one or more methods for your own account. Authenticator apps are recommended over SMS or email.',
    authenticatorApp: 'Authenticator app',
    googleAuthenticator: 'Google Authenticator',
    microsoftAuthenticator: 'Microsoft Authenticator',
    authenticatorHint: 'Works with standard time-based codes.',
    emailCode: 'Email code',
    emailCodeHint: 'Send a one-time code to your account email.',
    smsCode: 'SMS code',
    smsCodeHint: 'Send a one-time code to a verified mobile number.',
    phoneNumber: 'Mobile number in international format',
    activate: 'Activate',
    disable: 'Disable',
    finishEnrollment: 'Finish activation',
    manualKeyHint: 'Open Google Authenticator or Microsoft Authenticator and enter this setup key manually.',
    openAuthenticator: 'Open in authenticator app',
    confirmActivate: 'Verify and activate',
    mfaEnabled: 'Two-factor authentication was activated.',
    invalidVerificationCode: 'The verification code was not accepted.',
    securityLoadFailed: 'Security settings could not be loaded.',
    securitySaveFailed: 'The security change could not be saved.',
    fullTenantBackup: 'Full tenant backup',
    fullTenantBackupHint: 'Creates an on-demand archive of the selected tenant only. Other organizations and the platform control database are never included.',
    backupDatabase: 'Complete PostgreSQL tenant database',
    backupConfiguration: 'Tenant configuration and permissions',
    backupAudit: 'Audit, integration, and background-job data',
    backupChecksum: 'Manifest and SHA-256 integrity checksum',
    backupSensitive: 'The downloaded archive contains sensitive business data. Store it encrypted and share it only through an approved secure channel.',
    backupPermissionRequired: 'Your role does not have permission to download tenant backups.',
    backupPermissionFailed: 'Backup permission could not be checked.',
    getBackup: 'Get full backup',
    confirmBackup: 'Create and download this tenant backup?',
    confirmBackupHint: 'Generation can take several minutes for a large database. Keep this page open until the download begins.',
    downloadBackup: 'Create & download',
    preparingBackup: 'Preparing backup…',
    backupReady: 'The tenant backup was prepared and sent to your downloads.',
    backupFailed: 'The tenant backup could not be prepared. Check your permission and try again.',
  },
  documents: {
    printExport: 'Print & export',
    print: 'Print',
    printDocument: 'Print this document',
    preview: 'Print preview',
    printPdf: 'Print / Save PDF',
    exportAs: 'Export document as',
    word: 'Word',
    excel: 'Excel',
    noData: 'There are no list rows or form fields to include on this screen.',
  },
  theme: {
    light: 'Light',
    dark: 'Dark',
    system: 'Match system',
    switchToDark: 'Switch to dark theme',
    switchToLight: 'Switch to light theme',
  },
  security: {
    // Google requires this attribution wherever the reCAPTCHA badge is hidden.
    recaptchaNotice: 'This site is protected by reCAPTCHA and the Google <privacy>Privacy Policy</privacy> and <terms>Terms of Service</terms> apply.',
  },
  /*
   * Public homepage. The plan prices and the support address are commercial
   * placeholders: they are grouped here so a single edit publishes real terms.
   */
  landing: {
    nav: {
      about: 'About',
      identity: 'Inter-organization identity',
      plans: 'Plans & pricing',
      support: 'Support',
      policy: 'Policy',
      sections: 'Homepage sections',
    },
    signIn: 'Sign in',
    openWorkspace: 'Open console',
    explore: 'Read more',
    about: {
      title: 'About Varza',
      body: 'Varza holds the general ledger, inventory, receivables, payables, treasury, purchasing, and sales in one modular system. Each business gets its own database, every posting keeps its audit trail, and the whole interface works in English and Persian.',
      ledgerTitle: 'Double-entry core',
      ledgerBody: 'Ledger, multicurrency, and period close behave as one balanced chain.',
      inventoryTitle: 'Inventory that ties out',
      inventoryBody: 'Stock value reconciles to the ledger, down to lot and serial.',
      bilingualTitle: 'Bilingual by design',
      bilingualBody: 'English left-to-right and Persian right-to-left, both first class.',
      tenantTitle: 'Separated tenants',
      tenantBody: 'One physical database per business, reached by least-privileged roles.',
    },
    identity: {
      title: 'Varza Inter-organization Identity',
      body: 'A VII is one identifier for a person or a company across the whole of Varza, in the spirit of an IBAN. Register with an email address, confirm it, and the code is yours — free, and independent of any business that uses it.',
      codeTitle: 'One code, check digits and all',
      codeBody: 'Twenty characters carrying their own check digits, so a dictated or mistyped code is refused rather than mistaken for someone else.',
      inboxTitle: 'Your documents, in one place',
      inboxBody: 'Invoices, receipts, and statements that any Varza business issues to you arrive in a light portal you can read but never edit.',
      shareTitle: 'Handed over on your terms',
      shareBody: 'Share the card by email or QR, decide whether the directory may list you, and close a business channel to you whenever you choose.',
      action: 'Create your free VII',
      actionHint: 'An email address is all it takes.',
    },
    plans: {
      title: 'Plans & pricing',
      body: 'Start with a free Varza Inter-organization Identity and move up only when a business needs the full ledger.',
      note: 'Prices exclude local taxes. Annual billing is available on the paid plans.',
      popular: 'Most chosen',
      identityAction: 'Create a free VII',
      contactAction: 'Contact us',
      identityName: 'Inter-organization identity',
      identityPrice: 'Free',
      identityCadence: 'no card required',
      identitySummary: 'One identity, for a person or a company.',
      identityFirst: 'Your own VII code and QR card',
      identitySecond: 'A light portal, with no data entry',
      identityThird: 'Every document a business issues to you',
      identityFourth: 'Share your code by email or by scan',
      personalName: 'Personal',
      personalPrice: '$5',
      personalCadence: 'per month',
      personalSummary: 'Personal use and a small business.',
      personalFirst: 'The complete double-entry ledger and inventory',
      personalSecond: 'Connect up to 5 Varza Global Identities',
      personalThird: 'Issue documents to the people you work with',
      personalFourth: 'Without the AI features',
      professionalName: 'Professional',
      professionalPrice: '$25',
      professionalCadence: 'per month',
      professionalSummary: 'A growing business with real operations.',
      professionalFirst: 'Everything in Personal, plus every module',
      professionalSecond: 'Connect unlimited Varza Global Identities',
      professionalThird: 'AI features within a monthly allowance',
      professionalFourth: 'Two-factor sign-in and access control',
      enterpriseName: 'Enterprise',
      enterprisePrice: 'Tailored',
      enterpriseCadence: 'agreed per contract',
      enterpriseSummary: 'Many entities, one accounting policy.',
      enterpriseFirst: 'Unlimited companies and tenant databases',
      enterpriseSecond: 'Single sign-on and a dedicated environment',
      enterpriseThird: 'A named support engineer',
    },
    support: {
      title: 'Support',
      body: 'Reach someone who knows the product and your configuration.',
      email: 'support@varza.app',
      emailTitle: 'Support desk',
      emailBody: 'Answered on business days, in English or Persian.',
      docsTitle: 'Documentation',
      docsBody: 'Runbooks for setup, migration, and period close ship with the product.',
      statusTitle: 'Service status',
      statusBody: 'Planned maintenance and incidents are announced before they affect you.',
    },
    policy: {
      title: 'Policy',
      body: 'What Varza does with your data, stated plainly.',
      dataTitle: 'Data isolation',
      dataBody: 'Each tenant keeps a physically separate database; no query crosses that line.',
      securityTitle: 'Security',
      securityBody: 'Sessions are server-issued, two-factor is available, and access follows the permissions you grant.',
      privacyTitle: 'Privacy',
      privacyBody: 'Your accounting records are yours. They are never sold, shared, or mined.',
      termsTitle: 'Terms',
      termsBody: 'The complete privacy and service documents are issued with your service agreement.',
    },
    footer: {
      rights: '© {{year}} Varza. All rights reserved.',
    },
  },
  /*
   * Varza Inter-organization Identity: the holder's own card and inbox, and the business side that connects to a
   * holder and publishes document copies to them.
   */
  vii: {
    eyebrow: 'Varza Inter-organization Identity',
    pageTitle: 'Inter-organization identity',
    pageSubtitle: 'Your own VII card, the documents businesses have issued to you, and the holders this company is connected to.',
    tabIdentity: 'My identity',
    tabInbox: 'Issued to me',
    tabNetwork: 'Connected holders',
    cardEyebrow: 'Varza Inter-organization Identity',
    code: 'VII code',
    copyCode: 'Copy the code',
    codeCopied: 'The code was copied.',
    holder: 'Holder',
    holderKind: 'Kind of holder',
    holderKindHint: 'A real person holds a national ID; a legal person is a registered company.',
    email: 'Email address',
    issuedOn: 'Issued on',
    dueOn: 'Due on',
    amount: 'Amount',
    currency: 'Currency',
    business: 'Business',
    connectedOn: 'Connected on',
    documents: 'Documents',
    issuer: 'Issued by',
    qrLabel: 'QR code for the Varza Inter-organization Identity {{code}}',
    qrHint: 'Scan to read the code. It identifies a holder; it does not sign anyone in.',
    kind: {
      real: 'Real person',
      legal: 'Legal person',
    },
    state: {
      pending: 'Awaiting confirmation',
      active: 'Active',
      suspended: 'Suspended',
    },
    claimTitle: 'You do not hold a VII yet',
    claimBody: 'Your account is already confirmed, so a code can be issued now. It stays yours even if you leave this company.',
    claimAction: 'Issue my VII',
    claimFailed: 'The identity could not be issued.',
    shareTitle: 'Share your card',
    shareBody: 'Send the code to a business that needs to issue documents to you.',
    shareEmail: 'Send to',
    shareNote: 'Note (optional)',
    shareAction: 'Send the card',
    shareSent: 'The card was sent to {{email}}.',
    shareFailed: 'The card could not be sent.',
    shareCooldown: 'Wait a moment before sending another card.',
    discoverabilityTitle: 'Directory listing',
    discoverabilityBody: 'Your exact code always resolves. Listing also lets a business find you by name or email address.',
    discoverableOn: 'Listed in the directory',
    discoverableOff: 'Reachable only by the exact code',
    discoverabilityFailed: 'The directory setting could not be saved.',
    myConnectionsTitle: 'Businesses connected to me',
    myConnectionsBody: 'Each one may issue documents to your identity until you close the connection.',
    noConnectionsTitle: 'No business is connected yet',
    noConnectionsBody: 'Share your card, and the documents issued to you appear here.',
    revokeAccess: 'Close',
    summaryTotal: 'Documents',
    summaryUnread: 'Unread',
    summaryOpen: 'Open',
    summaryOverdue: 'Overdue',
    openBalance: 'Open amount',
    settledTotal: 'Settled',
    filterLabel: 'Filter by state',
    filterAll: 'All',
    markRead: 'Mark as read',
    noDocumentsTitle: 'Nothing has been issued to you yet',
    noDocumentsBody: 'When a connected business publishes an invoice or a receipt, it appears here.',
    documentKind: {
      invoice: 'Invoice',
      creditnote: 'Credit note',
      receipt: 'Receipt',
      statement: 'Statement',
      contract: 'Contract',
      notice: 'Notice',
      other: 'Document',
    },
    documentState: {
      issued: 'Open',
      settled: 'Settled',
      cancelled: 'Cancelled',
    },
    directoryTitle: 'Find a holder',
    directoryBody: 'Search by the exact VII code, or by name and email for holders who asked to be listed.',
    directoryQuery: 'Code, name, or email',
    directoryPlaceholder: 'VI49 1234 5678 9012 3456',
    directoryHint: 'At least three characters. A code is accepted with or without spaces.',
    tenantPerson: 'Local person',
    tenantPersonHint: 'Choose the canonical person this VII represents in your company.',
    personRequired: 'Choose a local person before connecting.',
    personKindMismatch: 'The local person and VII must both be real persons or both be legal entities.',
    noMatchTitle: 'No holder matched',
    noMatchBody: 'Check the code, or ask the holder to share their card with you.',
    connectAction: 'Connect',
    alreadyConnected: 'Connected',
    connectFailed: 'The connection could not be made.',
    codeNotFound: 'No active identity holds that code.',
    tenantConnectionsTitle: 'Connected holders',
    tenantConnectionsBody: 'The identities this company may publish documents to.',
    noTenantConnectionsTitle: 'No holder is connected yet',
    noTenantConnectionsBody: 'Search above for the code a customer or a supplier gave you.',
    publishAction: 'Publish',
    disconnect: 'Disconnect',
    publishTitle: 'Publish to {{name}}',
    publishBody: 'Only the heading is shared. The accounting record stays in this company.',
    publishConfirm: 'Publish the document',
    publishFailed: 'The document could not be published.',
    publishDuplicate: 'A document of this kind and number is already published.',
    amountInvalid: 'Enter an amount of zero or more.',
    documentKindLabel: 'Kind',
    documentNumber: 'Number',
    documentTitle: 'Title',
    documentStateLabel: 'State',
    documentSummary: 'Summary (optional)',
    publishedTitle: 'Already published',
    publishedEmpty: 'Nothing has been published through this connection yet.',
    readByHolder: 'read',
    withdraw: 'Withdraw',
    registerTitle: 'Create a Varza Inter-organization Identity',
    registerSubtitle: 'Use your invitation to create one identity that stays yours across every business on Varza.',
    registerName: 'Full name or company name',
    registerAction: 'Create my VII',
    registerFailed: 'The registration could not be completed.',
    invitationCode: 'Invitation code',
    invitationHint: 'Registration is private. Enter the single-use code shared with you.',
    invitationInvalid: 'This invitation is invalid, expired, revoked, or already used.',
    passwordHint: 'At least {{count}} characters.',
    passwordTooShort: 'Use at least {{count}} characters.',
    tooManyAttempts: 'Too many attempts. Try again in a minute.',
    alreadyHave: 'Already have an account?',
    orSignUpWith: 'or sign up with',
    signUpGoogle: 'Sign up with Google',
    signUpMicrosoft: 'Sign up with Microsoft',
    benefit: {
      free: 'Free, and independent of any company',
      documents: 'Read every document issued to you',
      portable: 'Keep the same code wherever you work',
    },
    checkEmailTitle: 'Check your email',
    checkEmailBody: 'If {{email}} can be registered, a confirmation link is on its way.',
    checkEmailHint: 'The link is valid for 24 hours and can be used once.',
    developmentToken: 'Development build:',
    confirmNow: 'confirm now',
    resend: 'Send the link again',
    backToSignIn: 'Back to sign in',
    confirmingTitle: 'Confirming your email',
    confirmingBody: 'One moment while the link is checked.',
    confirmedTitle: 'Your identity is live',
    confirmedBody: 'This code is now yours across every business on Varza.',
    confirmedHint: 'Sign in to see your card, share it, and read what businesses issue to you.',
    yourCode: 'Your VII code',
    signInNow: 'Sign in',
    confirmFailedTitle: 'This link no longer works',
    confirmFailedBody: 'A confirmation link expires after 24 hours and can be used only once.',
    confirmFailedHint: 'Ask for a new link, or register again with the same address.',
    registerAgain: 'Register again',
    publicTitle: 'A Varza Inter-organization Identity',
    publicBody: 'This code is well formed and its check digits agree.',
    publicHint: 'Sign in to connect to this holder, or create an identity of your own.',
    publicConnect: 'Sign in to connect',
    publicRegister: 'Create my own VII',
    publicInvalidTitle: 'That is not a Varza Inter-organization Identity',
    publicInvalidBody: 'The check digits do not agree with the rest of the code.',
    publicInvalidHint: 'A code reads as VI followed by eighteen digits, printed in groups of four.',
  },
  portal: {
    name: 'Identity portal',
    overview: 'Overview',
    documents: 'Documents',
    workforce: 'My work',
    identity: 'My identity',
    welcome: 'Welcome, {{name}}',
    overviewSubtitle: 'What businesses on Varza have issued to your inter-organization identity.',
    readOnlyTitle: 'A reading room, not a ledger',
    readOnlyBody: 'Nothing here is entered or edited by you. Every document is the copy an issuing business published, and it stays their record.',
    manageIdentity: 'Manage my identity',
    recentTitle: 'Recent documents',
    recentBody: 'The five most recent copies issued to you.',
    seeAll: 'See all documents',
    documentsTitle: 'Documents issued to me',
    documentsSubtitle: 'Every open, settled, and cancelled document, from every business connected to your identity.',
    identityTitle: 'My identity',
    identitySubtitle: 'Your card, who may reach you, and which businesses may publish to you.',
  },
  calendar: {
    solarHijriField: 'Solar Hijri (yyyy/mm/dd)',
    gregorianField: 'Gregorian (yyyy/mm/dd)',
    islamicHijriField: 'Islamic Hijri (yyyy/mm/dd)',
    gregorianEquivalent: 'Gregorian equivalent',
    equivalent: '{{calendar}} equivalent',
    system: {
      persian: 'Solar Hijri',
      gregorian: 'Gregorian',
      islamic: 'Islamic Hijri',
    },
    openCalendar: 'Open calendar',
    closeCalendar: 'Close calendar',
    chooseDate: 'Choose a date',
    chooseMonth: 'Choose a month',
    chooseYear: 'Choose a year',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    previousYear: 'Previous year',
    nextYear: 'Next year',
    previousYears: 'Previous years',
    nextYears: 'Next years',
    today: 'Today',
    clear: 'Clear',
    noDate: 'No date selected',
  },
  status: {
    draft: 'Draft',
    aiPendingApproval: 'Modified by Varza Robot · verification required',
    submitted: 'Submitted',
    approved: 'Approved',
    posted: 'Posted',
    reversed: 'Reversed',
    closed: 'Closed',
    active: 'Active',
    pending: 'Pending',
    blocked: 'Blocked',
  },
  errors: {
    pageTitle: 'We could not load this page',
    pageMessage: 'Check the connection and try again. Unsaved work has not been discarded.',
    notFoundTitle: 'Page not found',
    notFoundMessage: 'The requested workspace does not exist or you do not have access.',
    returnDashboard: 'Return to dashboard',
  },
  offline: {
    chip: {
      label: 'Connection and unsent work',
      offline: 'Offline',
      synced: 'Up to date',
      waiting: 'To send: {{count}}',
      attention: 'Needs attention: {{count}}',
    },
    status: {
      online: 'Connected to Varza',
      offline: 'No connection to Varza',
      notEnabled: 'This device keeps no offline copy.',
      sealed: 'The offline copy is sealed until you sign in with your password.',
      copyConfirmed: 'Offline copy confirmed {{when}}.',
    },
    state: {
      pending: 'Waiting',
      failed: 'Retrying',
      rejected: 'Refused',
      uncertain: 'Unconfirmed',
    },
    pause: {
      offline: 'waiting for a connection',
      sealed: 'the offline copy is sealed',
      authentication: 'sign in again to send',
      rejected: 'the server refused an entry',
      server: 'the server could not answer',
      uncertain: 'one entry needs confirming',
    },
    error: {
      unknown: 'The server did not accept it.',
      unreadable: 'This entry can no longer be read on this device.',
      no_reply: 'Sent, but no reply arrived. Check the register before sending it again.',
      needsConnection: 'This needs a connection to the Varza server.',
    },
    queue: {
      empty: 'Nothing is waiting to be sent.',
      lines_one: '{{count}} line',
      lines_other: '{{count}} lines',
      journalCreate: 'New journal entry',
      journalUpdate: 'Journal entry changes',
      journalDelete: 'Journal entry deletion',
      journalSubmit: 'Journal entry sent for approval',
    },
    lastSync: 'Last attempt {{when}}',
    syncNow: 'Send now',
    syncing: 'Sending…',
    discard: 'Discard',
    manageDevice: 'Device settings',
    notice: {
      connectionLost: 'Working offline',
      offlineSession: 'Signed in offline',
      copyAge: 'Figures are from a copy taken {{when}}.',
      copyUnknown: 'Figures come from the copy held on this device.',
      draftsQueued: 'New drafts are kept here and sent when the connection returns.',
      readOnly: 'Nothing can be saved until the connection returns.',
      waiting: 'To send: {{count}}',
    },
    unlock: {
      eyebrow: 'No connection',
      title: 'Sign in offline',
      subtitle: 'Varza cannot reach the server. Your password opens the copy held on this device.',
      account: 'Account',
      copyTaken: 'Copy taken',
      windowCloses: 'Offline access ends',
      passwordHint: 'The same password you use to sign in to Varza.',
      action: 'Open the offline copy',
      opening: 'Opening…',
      passwordError: 'That password does not open the copy on this device.',
      lockedError: 'Too many attempts. Wait before trying again.',
      expiredError: 'This copy has expired. Sign in with a connection to renew it.',
      failedError: 'The offline copy could not be opened on this device.',
      barred: 'Too many attempts. Try again in {{seconds}} seconds.',
      scope: 'You will see the figures as they stood when the copy was taken. Draft entries are kept here and sent once you are back online; posting, approving and payments need a connection.',
      tryConnection: 'Try the connection again',
    },
    forget: {
      start: 'Forget this device',
      action: 'Forget it',
      confirm: 'The offline copy and its password will be removed from this device.',
      confirmWithQueue: 'Unsent entries on this device will be lost. Waiting to send: {{count}}.',
    },
    login: {
      remember: 'Keep working offline on this device',
      rememberHint: 'Varza keeps an encrypted copy of your workspace here, opened by your password, and works for up to {{days}} days without a connection.',
    },
    device: {
      title: 'Offline access',
      hint: 'Keep an encrypted copy of this workspace on this device and work for up to {{days}} days without a connection.',
      unsupported: 'This browser cannot protect an offline copy. Varza needs a secure (HTTPS) origin and browser storage to encrypt one.',
      explain: 'Your password seals the copy. It is never stored — Varza keeps a key wrapped under it, so nothing on this device can be read without you.',
      passwordLabel: 'Your Varza password',
      passwordHint: 'Used only to lock and unlock the copy on this device.',
      showPassword: 'Show',
      hidePassword: 'Hide',
      enable: 'Keep an offline copy',
      enabled: 'This device now keeps an offline copy.',
      enableFailed: 'The offline copy could not be created on this device.',
      state: 'State',
      open: 'Open',
      sealed: 'Sealed',
      account: 'Account',
      confirmed: 'Last confirmed by the server',
      expires: 'Offline access ends',
      held: 'Held on this device',
      heldValue: '{{count}} pages · {{size}}',
      persistence: 'Storage',
      persistenceGranted: 'The browser has promised to keep it',
      persistenceRequest: 'Ask the browser to keep it',
      clearCache: 'Clear the stored pages',
      queueTitle: 'Waiting to be sent',
      queueHint: 'Entries written on this device while it had no connection.',
      waiting: 'Waiting to send',
      attention: 'Needs attention',
      lastSync: 'Last attempt',
      scopeTitle: 'What works without a connection',
      scopeHint: 'Deliberately narrow: everything that decides something about the books needs a live server.',
      scopeReads: 'Reading the pages you have already opened — dashboard, ledger, accounts, journals, inventory, receivables, payables and treasury',
      scopeDrafts: 'Writing, changing and submitting draft journal entries',
      scopePosting: 'Posting, approving, reversing, receipts and payments',
      scopeLive: 'The assistant, reports, exports and anything administrative',
    },
    dataset: {
      session: 'Account and organizations',
      dashboard: 'Dashboard',
      scope: 'Branches, periods and currencies',
      exchangeRates: 'Exchange rates',
      accounts: 'Chart of accounts',
      ledger: 'Ledgers and trial balance',
      persons: 'People and counterparties',
      journals: 'Journal entries',
      inventory: 'Inventory',
      receivables: 'Receivables',
      payables: 'Payables',
      treasury: 'Treasury',
      reports: 'Report definitions',
    },
    journal: {
      queuedTitle: 'Saved on this device',
      queuedBody: 'There is no connection, so this entry has no number yet. Varza sends it as soon as you are back online, and the server numbers and checks it then.',
    },
  },
  pwa: {
    install: {
      title: 'Install Varza',
      subtitle: 'Open it from your desktop or home screen, in its own window, and keep working without a connection.',
      action: 'Install',
      how: 'How to install',
      dismiss: 'Not now',
      never: 'Don’t show this again',
      reminderOff: 'The install reminder is turned off on this device.',
      reminderRestore: 'Show it again',
      iosSteps: 'Tap the Share button, then choose "Add to Home Screen".',
      safariSteps: 'Open the File menu, then choose "Add to Dock".',
      sectionTitle: 'Install on this device',
      sectionHint: 'An installed Varza opens in its own window, keeps its own icon, and starts even with no connection.',
      alreadyInstalled: 'Varza is installed on this device.',
      unavailable: 'This browser does not offer installation. Chrome, Edge and Safari all do.',
    },
    update: {
      available: 'A new version of Varza is ready.',
      reload: 'Update now',
      applying: 'Updating…',
    },
  },
} as const;

type TranslationShape<T> = {
  [Key in keyof T]: T[Key] extends string ? string : TranslationShape<T[Key]>;
};

export const fa: TranslationShape<typeof en> = {
  workforce: {
    ...en.workforce,
    title: 'نیروی کار',
    description: 'افراد مرجع، روابط مؤثر، قراردادهای تحت حاکمیت، شواهد عملیاتی و جبران خدمات قابل توضیح.',
    summary: 'خلاصه نیروی کار',
    profilesTab: 'پروفایل‌ها',
    engagementsTab: 'روابط و قراردادها',
    compensationTab: 'جبران خدمات',
    timeTab: 'زمان و مرخصی',
    revenueTab: 'کمیسیون و فروش',
    claimsTab: 'مطالبات و وام‌ها',
    settlementsTab: 'تسویه و پرداخت',
    disputesTab: 'اختلاف‌ها',
    reportsTab: 'گزارش‌ها و هوش مصنوعی',
    selectProfile: 'یک پروفایل نیروی کار انتخاب کنید',
    selectProfileHint: 'سوابق عملیاتی همیشه به یک پروفایل تأییدشده نیروی کار محدود می‌شوند.',
    workflowFailed: 'عملیات گردش‌کار انجام نشد. رکورد را تازه‌سازی کنید و با نسخه فعلی دوباره بکوشید.',
    activeProfiles: 'پروفایل‌های فعال',
    relationships: 'روابط',
    roleAssignments: 'انتساب نقش‌ها',
    persistedContracts: 'قراردادهای ذخیره‌شده',
    newProfile: 'پروفایل جدید',
    addRelationship: 'افزودن رابطه',
    newContract: 'قرارداد جدید',
    newPlan: 'طرح جدید',
    calculate: 'محاسبه',
    profilesTitle: 'پروفایل‌های نیروی کار',
    profilesHint: 'برای هر شخص حقیقی مرجع، یک هویت نیروی کار نگهداری می‌شود.',
    noProfiles: 'هیچ پروفایل نیروی کاری وجود ندارد',
    noProfilesHint: 'برای ایجاد رابطه، قرارداد و جبران خدمات یک پروفایل بسازید.',
    numberPerson: 'شماره / شخص',
    externalReference: 'مرجع بیرونی',
    viewProfile: 'مشاهده پروفایل',
    profileLoadFailed: 'جزئیات پروفایل بارگیری نشد.',
    profileVersion: 'پروفایل {{number}} · نسخه خوش‌بینانه {{version}}',
    canonicalPerson: 'شخص مرجع',
    relationshipsRolesTitle: 'روابط و انتساب نقش‌ها',
    engagementsFor: 'تعامل‌های مؤثر نیروی کار برای {{name}}.',
    selectProfileEngagement: 'پیش از ایجاد تعامل، یک پروفایل انتخاب کنید.',
    noRelationships: 'هیچ رابطه‌ای وجود ندارد',
    noRelationshipsHint: 'یک پروفایل انتخاب و نخستین رابطه مؤثر آن را اضافه کنید.',
    actions: 'عملیات',
    assignRole: 'انتساب نقش',
    contractsTitle: 'قراردادها',
    contractsHint: 'قراردادهای ذخیره‌شده و تحت حاکمیت برای پروفایل منتخب.',
    noContracts: 'هیچ قراردادی وجود ندارد',
    noContractsHint: 'برای یکی از روابط پروفایل منتخب قرارداد بسازید.',
    profileRelationship: 'پروفایل / رابطه',
    termsFingerprint: 'اثر انگشت شرایط',
    compensationWorkbenchTitle: 'میزکار طرح جبران خدمات',
    compensationWorkbenchHint: 'تغییرات طرح نسخه‌دار هستند؛ محاسبه یک هسته فقط‌خواندنی و قابل توضیح است.',
    addVersion: 'افزودن نسخه',
    addComponent: 'افزودن مؤلفه',
    noPlan: 'هیچ طرحی در این میزکار نیست',
    noPlanHint: 'یک طرح بسازید تا نسخه مؤثر و مؤلفه‌های جبران خدمات را اضافه کنید.',
    plan: 'طرح',
    planVersion: 'نسخه طرح',
    versions: 'نسخه‌ها',
    component: 'مؤلفه',
    kindMethod: 'نوع / روش',
    multiplier: 'ضریب',
    latestCalculationTitle: 'آخرین محاسبه',
    latestCalculationHint: 'محاسبه هیچ ثبت حقوق یا دفتر کل ایجاد نمی‌کند؛ اثر انگشت، ورودی و نتیجه را اثبات می‌کند.',
    noCalculation: 'هیچ نتیجه محاسبه‌ای وجود ندارد',
    noCalculationHint: 'یک طرح فعال جبران خدمات را برای پروفایل و رابطه محاسبه کنید.',
    grossEarnings: 'درآمد ناخالص',
    netAmount: 'مبلغ خالص',
    fingerprint: 'اثر انگشت',
    newWorkforceProfileTitle: 'پروفایل جدید نیروی کار',
    newWorkforceProfileHint: 'دقیقاً یک شخص حقیقی فعال را از فهرست اشخاص مرجع ورزا پیوند دهید.',
    createProfile: 'ایجاد پروفایل',
    profileCreated: 'پروفایل نیروی کار ایجاد شد.',
    addRelationshipTitle: 'افزودن رابطه',
    addRelationshipFor: 'برای {{name}} یک رابطه مؤثر ایجاد کنید.',
    relationshipAdded: 'رابطه اضافه شد.',
    assignRoleTitle: 'انتساب نقش',
    assignRoleFor: 'یک نقش مؤثر به رابطه {{number}} اختصاص دهید.',
    roleAssigned: 'نقش اختصاص یافت.',
    newWorkforceContractTitle: 'قرارداد جدید نیروی کار',
    newWorkforceContractHint: 'شرایط اولیه به‌صورت یک تصویر JSON مرجع و تغییرناپذیر ذخیره می‌شوند.',
    createContract: 'ایجاد قرارداد',
    contractCreated: 'قرارداد ایجاد شد.',
    newCompensationPlanTitle: 'طرح جدید جبران خدمات',
    newCompensationPlanHint: 'پیش از افزودن نسخه مؤثر و مؤلفه‌های محاسبه، سربرگ طرح را ایجاد کنید.',
    createPlan: 'ایجاد طرح',
    planCreated: 'طرح جبران خدمات ایجاد شد.',
    addPlanVersionTitle: 'افزودن نسخه طرح جبران خدمات',
    planVersionAdded: 'نسخه طرح اضافه شد.',
    addCompensationComponentTitle: 'افزودن مؤلفه جبران خدمات',
    componentHint: 'مؤلفه‌ها می‌توانند درآمد یا کسور ثابت، زمان‌محور یا قطعه‌محور باشند.',
    componentAdded: 'مؤلفه جبران خدمات اضافه شد.',
    calculateCompensationTitle: 'محاسبه جبران خدمات',
    calculateCompensationHint: 'محاسبه فقط‌خواندنی و قطعی است و طرح منتخب باید نسخه مؤثر فعال داشته باشد.',
    compensationCalculated: 'جبران خدمات محاسبه شد و هیچ ثبت حسابداری ایجاد نشد.',
    relationshipNumber: 'شماره رابطه',
    effectiveTo: 'مؤثر تا',
    canonicalPersonId: 'شناسه شخص مرجع',
    personGuid: 'شناسه GUID شخص',
    workforceNumber: 'شماره نیروی کار',
    displayName: 'نام نمایشی',
    roleCode: 'کد نقش',
    roleCodePlaceholder: 'EMPLOYEE، CONTRACTOR، RESELLER',
    contractNumber: 'شماره قرارداد',
    startsOn: 'شروع در',
    endsOn: 'پایان در',
    termsSnapshot: 'تصویر شرایط',
    planCode: 'کد طرح',
    planName: 'نام طرح',
    currencyAssetCode: 'کد ارز / دارایی',
    currencyPrecision: 'دقت ارز',
    componentCode: 'کد مؤلفه',
    componentName: 'نام مؤلفه',
    method: 'روش',
    expenseAccountId: 'شناسه حساب هزینه',
    liabilityAccountId: 'شناسه حساب بدهی',
    profile: 'پروفایل',
    profileId: 'شناسه پروفایل',
    activePlanId: 'شناسه طرح فعال',
    relationshipId: 'شناسه رابطه',
    approvalCutoff: 'زمان برش تأیید',
    approvedSources: 'منابع تأییدشده زمانی یا قطعه‌ای',
    sourcesArrayError: 'منابع باید یک آرایه JSON باشند.',
    sourceObjectError: 'منبع {{index}} باید یک شیء باشد.',
    sourceMethodError: 'روش منبع {{index}} نامعتبر است.',
    compensationKind: { Earning: 'درآمد', Deduction: 'کسور' },
    compensationMethod: { Fixed: 'ثابت', Time: 'زمانی', Piece: 'قطعه‌ای' },
    date: 'تاریخ',
    kind: 'نوع',
    quantity: 'مقدار',
    status: 'وضعیت',
    version: 'نسخه',
    action: 'عملیات',
    amount: 'مبلغ',
    asset: 'دارایی',
    relationship: 'رابطه',
    contract: 'قرارداد',
    period: 'دوره',
    open: 'باز کردن',
    openEnded: 'باز',
    submit: 'ارسال',
    approve: 'تأیید',
    reverse: 'برگشت',
    activate: 'فعال‌سازی',
    close: 'بستن',
    reversalReason: 'دلیل برگشت',
    timeTitle: 'شواهد زمان، حضور، مرخصی و خروجی کار',
    timeDescription: 'شواهد عملیاتی ذخیره می‌شوند و تا ارسال و تأیید در وضعیت پیش‌نویس می‌مانند.',
    noWorkEvidence: 'شواهد کاری وجود ندارد',
    noWorkEvidenceHint: 'شیفت، حضور، کارکرد، مرخصی، خروجی، دستور کار یا زمان پروژه را ثبت کنید.',
    recordWork: 'ثبت شواهد کار',
    recordWorkHint: 'نوع شاهد را انتخاب کنید؛ واحد معتبر آن به‌صورت خودکار اعمال می‌شود.',
    noRelationship: 'رابطه‌ای وجود ندارد',
    noRelationshipWorkHint: 'پیش از ثبت شواهد کار، یک رابطه نیروی کار ایجاد کنید.',
    projectOptional: 'شناسه پروژه (اختیاری)',
    workOrderOptional: 'شناسه دستور کار (اختیاری)',
    costCenterOptional: 'شناسه مرکز هزینه (اختیاری)',
    recordDraft: 'ثبت پیش‌نویس',
    workKind: {
      Shift: 'شیفت', Attendance: 'حضور', Timesheet: 'کارکرد', Leave: 'مرخصی',
      WorkOutput: 'خروجی کار', WorkOrder: 'دستور کار', ProjectTime: 'زمان پروژه',
    },
    workUnit: {
      Hours: 'ساعت', Days: 'روز', Shifts: 'شیفت', Units: 'واحد', Jobs: 'کار', Kilometers: 'کیلومتر',
    },
    createClaim: 'ایجاد مطالبه مالی',
    createClaimHint: 'هزینه‌ها قابل بازپرداخت‌اند؛ مساعده، برداشت کمیسیون و وام به سقف و برنامه بازیافت صریح نیاز دارند.',
    noRelationshipClaimHint: 'پیش از ثبت مطالبه، یک رابطه نیروی کار ایجاد کنید.',
    claimKind: {
      ExpenseReimbursement: 'بازپرداخت هزینه', Advance: 'مساعده',
      CommissionDraw: 'برداشت کمیسیون', Loan: 'وام',
    },
    recoverySchedule: 'برنامه بازیافت',
    recoveryCap: 'حداکثر بازیافت در هر تسویه',
    recoveryDue: 'تاریخ سررسید',
    installmentAmount: 'مبلغ قسط',
    removeInstallment: 'حذف قسط',
    addInstallment: 'افزودن قسط',
    recoveryTotal: 'جمع برنامه: {{total}} / مبلغ مطالبه: {{amount}}',
    createClaimDraft: 'ایجاد پیش‌نویس مطالبه',
    jurisdictionTitle: 'وضعیت بسته حوزه قضایی',
    jurisdictionDescription: 'بسته سیاست مؤثر و دارای استناد را بدون تعبیه فرض‌های قانونی در نیروی کار بررسی کنید.',
    jurisdictionCode: 'کد حوزه قضایی',
    effectiveOn: 'تاریخ اثر',
    checkJurisdiction: 'بررسی بسته پیکربندی‌شده',
    jurisdictionMissing: 'برای این کد و تاریخ، بسته حوزه قضایی مؤثری پیکربندی نشده است.',
    jurisdictionConfigured: 'بسته {{pack}} نسخه {{version}} پیکربندی شده و معتبر است.',
    jurisdictionNoAssumptions: 'بسته نمونه عمومی است و هیچ ادعای قانونی مالیاتی، کار یا حقوق و دستمزد ندارد.',
    jurisdictionUnchecked: 'برای بررسی وضعیت، کد حوزه و تاریخ را وارد کنید. بسته EXAMPLE غیرقانونی و نمونه است.',
    reportsTitle: 'گزارش‌های نیروی کار',
    reportsHint: 'واقعیت‌های قطعی و خصوصی مستأجر همراه با استناد منبع.',
    noReportFacts: 'داده‌ای برای گزارش وجود ندارد.',
    aiTitle: 'دستیار هوش مصنوعی با کنترل مجوز',
    aiHint: 'هوش مصنوعی فقط واقعیت‌های مستند را توضیح می‌دهد؛ مالک هر مبلغ و ثبت، ماژول قطعی مربوط است.',
    aiTool: 'ابزار',
    aiQuestion: 'پرسش',
    runAi: 'اجرای ابزار هوش مصنوعی',
    aiUnavailable: 'ابزار هوش مصنوعی در دسترس نیست یا مجوز رد شده است.',
    queryWorkforce: 'پرس‌وجوی نیروی کار',
    explainCommission: 'توضیح کمیسیون',
    draftDisputeResponse: 'پیش‌نویس پاسخ اختلاف',
    registerHint: 'سوابق مستأجر ذخیره شده‌اند؛ گذارهای مالی نسخه جاری و مجوزها را اعمال می‌کنند.',
    noProfileRecords: 'رکوردی با پروفایل نیروی کار انتخاب‌شده منطبق نیست.',
    partnerAgreementsTitle: 'توافق‌نامه‌های همکار',
    noPartnerAgreements: 'توافق‌نامه همکاری وجود ندارد',
    agreement: 'توافق‌نامه',
    basis: 'مبنا',
    rate: 'نرخ',
    commissionEventsTitle: 'رویدادهای کمیسیون',
    noCommissionEvents: 'رویداد کمیسیونی وجود ندارد',
    commissionAmount: 'کمیسیون',
    salesAttributionTitle: 'انتساب فروش',
    noSalesAttribution: 'انتساب فروشی وجود ندارد',
    grossSales: 'فروش ناخالص',
    collected: 'وصول‌شده',
    attribution: 'انتساب',
    partnerAgreementSetup: 'تنظیم توافق‌نامه همکار',
    partnerAgreementSetupHint: 'شرایط کمیسیون نسخه‌دار و تاریخ‌اثر هستند و جداگانه فعال می‌شوند.',
    noContractAgreementHint: 'پیش از تنظیم شرایط کمیسیون، یک قرارداد تحت حاکمیت ایجاد کنید.',
    noContractAvailable: 'قرارداد ذخیره‌شده‌ای وجود ندارد',
    agreementCode: 'کد توافق‌نامه',
    agreementKind: 'نوع توافق‌نامه',
    operatingModel: 'مدل عملیاتی',
    commissionBasis: 'مبنای کمیسیون',
    ratePercent: 'درصد نرخ',
    attributionPercent: 'درصد انتساب',
    effectiveFrom: 'تاریخ شروع اثر',
    policyJson: 'سیاست JSON',
    createAgreementDraft: 'ایجاد پیش‌نویس توافق‌نامه',
    partnerKind: { SalesAgent: 'نماینده فروش', Reseller: 'فروشنده مجدد', ReferralPartner: 'همکار ارجاع' },
    operatingModelValue: {
      Agent: 'نمایندگی', BuySell: 'خرید / فروش', Marketplace: 'بازارگاه',
      Consignment: 'امانی', Referral: 'ارجاع', WhiteLabel: 'برچسب سفید',
    },
    commissionBasisValue: {
      OrderConfirmed: 'تأیید سفارش', Shipment: 'ارسال', InvoiceIssued: 'صدور صورتحساب',
      CollectedRevenue: 'درآمد وصول‌شده', GrossMargin: 'حاشیه ناخالص', Referral: 'ارجاع',
    },
    revenueEvidenceTitle: 'شواهد کمیسیون و فروش',
    revenueEvidenceHint: 'حقایق کمیسیون و فروش تا تأیید جداگانه در وضعیت پیش‌نویس می‌مانند.',
    noRelationshipRevenueHint: 'پیش از ثبت شواهد درآمد، یک رابطه نیروی کار ایجاد کنید.',
    commissionEvent: 'رویداد کمیسیون',
    activeAgreement: 'توافق‌نامه فعال',
    noActiveAgreement: 'توافق‌نامه فعالی وجود ندارد',
    recognizedRevenue: 'درآمد شناسایی‌شده',
    collectedRevenue: 'درآمد وصول‌شده',
    directCost: 'هزینه مستقیم',
    recordCommissionDraft: 'ثبت پیش‌نویس کمیسیون',
    recordSalesDraft: 'ثبت پیش‌نویس فروش',
    clawBack: 'بازپس‌گیری',
    clawbackReason: 'دلیل بازپس‌گیری',
    claimsTitle: 'هزینه‌ها، مساعده‌ها و وام‌ها',
    noFinancialClaims: 'مطالبه مالی وجود ندارد',
    outstanding: 'مانده',
    managerApprove: 'تأیید مدیر',
    financeApprove: 'تأیید مالی',
    settlementsPostingTitle: 'تسویه‌ها و آماده‌سازی ثبت',
    settlementNumber: 'شماره تسویه',
    periodFrom: 'شروع دوره',
    periodTo: 'پایان دوره',
    netPayable: 'خالص پرداختنی',
    paymentEvidenceTitle: 'شواهد پرداخت',
    noPaymentEvidence: 'شاهد پرداختی وجود ندارد',
    occurred: 'زمان وقوع',
    reversal: 'برگشت',
    reverseEvidence: 'برگشت شاهد',
    calculateSettlementTitle: 'محاسبه تسویه',
    calculateSettlementHint: 'همه حقایق تأییدشده کار، کمیسیون و بازپرداخت در دوره را برای رابطه و دارایی انتخاب‌شده به‌کار می‌گیرد.',
    noRelationshipSettlementHint: 'پیش از محاسبه تسویه، یک رابطه ایجاد کنید.',
    eligibleSources: 'منابع تأییدشده واجد شرایط: {{count}}',
    calculatePersistedSettlement: 'محاسبه تسویه ذخیره‌شده',
    markStale: 'علامت‌گذاری منقضی',
    preparePostingIntegrations: 'آماده‌سازی ثبت و یکپارچه‌سازی‌ها',
    recordPaymentEvidence: 'ثبت شاهد پرداخت',
    paidAmount: 'مبلغ پرداخت‌شده',
    createAdjustment: 'ایجاد تعدیل',
    adjustmentReason: 'دلیل تعدیل',
    adjustmentNumber: 'شماره تسویه تعدیلی',
    disputesAdjustmentsTitle: 'اختلاف‌ها و تعدیلات نیروی کار',
    disputeNumber: 'اختلاف',
    subject: 'موضوع',
    beginManagerReview: 'شروع بررسی مدیر',
    escalateFinanceHr: 'ارجاع به مالی / منابع انسانی',
    resolve: 'حل‌وفصل',
    resolutionEvidence: 'شواهد حل‌وفصل و تعدیل',
    portalEyebrow: 'نیروی کار',
    myWorkforce: 'نیروی کار من',
    portalIdentityHint: 'سوابق نیروی کار پیوندخورده به هویت تأییدشده شما.',
    noWorkforceLinks: 'پیوند نیروی کار وجود ندارد',
    noWorkforceLinksHint: 'هیچ سازمان فعالی پروفایل نیروی کار را به هویت شما پیوند نداده است.',
    portalDescription: 'سوابق خصوصی نیروی کار شما در {{organization}}. پرداخت و حسابداری در مالکیت ماژول‌های مسئول سازمان می‌ماند.',
    organization: 'سازمان',
    portalSummary: 'خلاصه نیروی کار',
    contracts: 'قراردادها',
    latestNetEarnings: 'آخرین درآمد خالص',
    latestSettlement: 'آخرین تسویه',
    portalActionFailed: 'عملیات انجام نشد. نسخه رکورد را بررسی کنید و دوباره بکوشید.',
    relationshipsContracts: 'روابط و قراردادها',
    verifiedProfile: 'پروفایل {{number}} · سوابق تأییدشده فقط‌خواندنی',
    noWorkforceRelationships: 'رابطه نیروی کار وجود ندارد',
    noWorkforceRelationshipsHint: 'هیچ رابطه یا قراردادی برای هویت شما منتشر نشده است.',
    record: 'رکورد',
    effectivePeriod: 'دوره اعتبار',
    roles: 'نقش‌ها',
    acknowledge: 'تأیید دریافت',
    acceptOffer: 'پذیرش پیشنهاد',
    earningsCommissions: 'درآمد و کمیسیون',
    earningsHint: 'شواهد محاسبه نهایی؛ حسابداری همچنان در مالکیت حقوق و دستمزد و پرداختنی‌ها است.',
    noEarnings: 'صورت‌حساب درآمدی وجود ندارد',
    noEarningsHint: 'درآمدها و صورت‌حساب‌های کمیسیون تأییدشده اینجا نمایش داده می‌شوند.',
    periodEvent: 'دوره / رویداد',
    grossBasis: 'ناخالص یا مبنا',
    deductions: 'کسورات',
    netCommission: 'خالص / کمیسیون',
    commission: 'کمیسیون',
    settlements: 'تسویه‌ها',
    settlementsHint: 'وضعیت برش صریح است؛ صورت‌حساب‌های نهایی فقط با تعدیل تغییر می‌کنند.',
    noSettlements: 'تسویه‌ای وجود ندارد',
    noSettlementsHint: 'صورت‌حساب‌های تسویه تأییدشده اینجا نمایش داده می‌شوند.',
    credits: 'بستانکار',
    debits: 'بدهکار',
    net: 'خالص',
    cutoff: 'برش',
    payment: 'پرداخت',
    expensesAdvancesLoans: 'هزینه‌ها، مساعده‌ها و وام‌ها',
    privateClaimsHint: 'فقط مطالبات پیوندخورده به پروفایل تأییدشده شما نمایش داده می‌شوند.',
    noExpensesRecoveries: 'هزینه یا بازیافتی وجود ندارد',
    noExpensesRecoveriesHint: 'هزینه‌ها، مساعده‌ها و وام‌های ارسالی شما اینجا نمایش داده می‌شوند.',
    documents: 'اسناد',
    documentsHint: 'اسناد امن منتشرشده در VII شما.',
    noDocuments: 'سندی وجود ندارد',
    noDocumentsHint: 'اسناد نیروی کار منتشرشده اینجا نمایش داده می‌شوند.',
    openDocument: 'باز کردن',
    submissionsDisputes: 'ارسال‌ها و اختلاف‌ها',
    submissionsHint: 'شواهد خود را ارسال کنید. مدیران و امور مالی پیش از هر اثر مالی آن را بررسی می‌کنند.',
    noRelationshipAvailable: 'رابطه‌ای در دسترس نیست',
    noRelationshipAvailableHint: 'برای ارسال شواهد، یک رابطه فعال یا دعوت‌شده لازم است.',
    timeOrLeave: 'زمان یا مرخصی',
    type: 'نوع',
    time: 'زمان',
    leave: 'مرخصی',
    expense: 'هزینه',
    settlementDispute: 'اختلاف تسویه',
    settlement: 'تسویه',
    noSettlementAvailable: 'تسویه‌ای در دسترس نیست',
    reviewQuestion: 'چه موردی باید بررسی شود؟',
    openDispute: 'ایجاد اختلاف',
    noDisputes: 'اختلافی وجود ندارد',
    noDisputesHint: 'اختلاف‌های شما و حل‌وفصل مبتنی بر تعدیل اینجا نمایش داده می‌شوند.',
    dispute: 'اختلاف',
    category: 'دسته‌بندی',
    resolution: 'راه‌حل',
    acceptResolution: 'پذیرش راه‌حل',
  },
  integrationControl: {
    title: 'مرکز کنترل یکپارچه‌سازی',
    subtitle: 'پذیرش شریک‌ها، بررسی پردازش دقیقاً یک‌بار رویدادها، تأیید طرح حسابداری و کنترل ایمن تحویل.',
    actionFailed: 'عملیات یکپارچه‌سازی انجام نشد.',
    previewUnavailable: 'پیش‌نمایش حسابداری برای این رویداد در دسترس نیست.',
    partnerCreated: 'شریک ایجاد شد و شناسه آن در فرم برنامه قرار گرفت.',
    applicationCreated: 'برنامه ایجاد شد و شناسه آن برای صدور اعتبارنامه آماده است.',
    credentialCreated: 'اعتبارنامه صادر شد؛ رمز را اکنون کپی کنید.',
    controlsSaved: 'کنترل‌های اضطراری ذخیره شد.',
    summary: 'خلاصه عملیات یکپارچه‌سازی',
    partners: 'شریک‌ها',
    applications: 'برنامه‌ها',
    bindings: 'اتصال‌های فعال',
    events: 'رویدادهای دریافتی',
    failures: 'رویدادهای ناموفق',
    deadLetters: 'رویدادهای متوقف‌شده',
    outbox: 'خروجی‌های در انتظار',
    webhooks: 'وب‌هوک‌های ناموفق',
    eventInspector: 'بازرس رویداد',
    eventInspectorHint: 'تبار، خطا، طرح حسابداری قطعی و امکان بازپخش را بررسی کنید.',
    allStatuses: 'همه وضعیت‌ها',
    eventId: 'رویداد خارجی',
    type: 'نوع رویداد',
    attempts: 'تلاش‌ها',
    error: 'آخرین خطا',
    rule: 'نسخه قاعده',
    planHash: 'هش طرح تأییدشده',
    document: 'سند برنامه‌ریزی‌شده',
    debit: 'بدهکار',
    credit: 'بستانکار',
    auditReason: 'دلیل حسابرسی (برای تغییرات الزامی)',
    replay: 'بازپخش ایمن',
    replayQueued: 'بازپخش در صف قرار گرفت.',
    approved: 'طرح دقیق حسابداری تأیید شد.',
    newPartner: 'شریک جدید',
    code: 'کد شریک',
    displayName: 'نام نمایشی',
    legalName: 'نام قانونی',
    contact: 'اطلاعات تماس',
    newApplication: 'برنامه جدید',
    partnerId: 'شناسه شریک',
    environment: 'محیط',
    rateLimit: 'درخواست در دقیقه',
    scopes: 'دامنه‌های OAuth با فاصله',
    webhookEvents: 'رویدادهای مجاز وب‌هوک',
    credentials: 'اعتبارنامه کارخواه',
    applicationId: 'شناسه برنامه',
    issueCredential: 'صدور اعتبارنامه',
    copyNow: 'فقط یک‌بار نمایش داده می‌شود؛ رمز را در مخزن امن شریک ذخیره کنید.',
    emergency: 'کنترل‌های اضطراری',
    disableApplication: 'غیرفعال‌کردن برنامه',
    pauseProcessing: 'توقف پردازش رویداد',
    pauseWebhooks: 'توقف تحویل وب‌هوک',
  },
  auditLog: {
    control: 'کنترل مالی',
    title: 'گزارش حسابرسی',
    tenantSubtitle: 'سابقه تغییرات تغییرناپذیر این سازمان، همراه با عامل، زمان و شواهد پیش و پس از تغییر با اطلاعات حساس پوشانده‌شده.',
    platformTitle: 'گزارش حسابرسی سکو',
    platformSubtitle: 'سابقه تغییرات تغییرناپذیر مستأجران ورزا، هویت‌ها، نشست‌ها و عملیات سکو.',
    verify: 'اعتبارسنجی زنجیره هش',
    chainValid: 'زنجیره حسابرسی معتبر است.',
    chainInvalid: 'اعتبارسنجی زنجیره حسابرسی ناموفق بود.',
    checkedEvents: '{{count}} رویداد زنجیره‌شده رمزنگاری‌شده بررسی شد.',
    filterTarget: 'نوع هدف',
    operation: 'عملیات',
    allOperations: 'همه عملیات',
    time: 'تاریخ و زمان',
    actor: 'کاربر یا سرویس',
    target: 'سند یا رکورد',
    fields: 'فیلدهای تغییرکرده',
    noEvents: 'رویداد حسابرسی وجود ندارد',
    noEventsHint: 'هیچ تغییری با فیلترهای انتخاب‌شده مطابقت ندارد.',
    selectEvent: 'برای بررسی شواهد، یک رویداد را انتخاب کنید.',
    correlation: 'شناسه همبستگی',
    hash: 'هش SHA-256',
    before: 'پیش از تغییر',
    after: 'پس از تغییر',
    AdditiveReversal: 'اصلاح فقط با یک سند برگشتی پیوندخورده انجام می‌شود؛ سوابق ثبت‌شده تغییرناپذیرند.',
    ValidatedRecreation: 'ایجاد مجدد فقط با اعتبارسنجی ماژول مالک انجام می‌شود.',
    ValidatedSnapshotRestore: 'بازیابی فقط با اعتبارسنجی ماژول مالک انجام می‌شود.',
  },
  voice: {
    title: 'فرمان صوتی محلی',
    startListening: 'شروع فرمان صوتی محلی',
    stopListening: 'پایان شنیدن',
    holdToSpeak: 'برای گفتن، نشان را نگه دارید',
    holdHint: 'سه ثانیه نگه دارید تا میکروفون باز شود.',
    transcriptTitle: 'آنچه این دستگاه شنید',
    localPrivacy: 'خصوصی و محلی',
    localPrivacyHint: 'صدای میکروفون و متن آن در همین مرورگر می‌ماند. فقط فرمان ساختاریافته برای مجوزدهی و ممیزی به ورزا فرستاده می‌شود.',
    installTitle: 'نصب مدل‌های صوتی محلی',
    installHint: 'ورزا بستهٔ مدل سنجاق‌شده را از همین سرور ورزا دریافت و برای استفادهٔ برون‌خط روی این دستگاه نگهداری می‌کند.',
    installBasic: 'نصب مدل‌های پایه',
    installEnhanced: 'نصب تحلیل‌گر پیشرفته',
    installed: 'مدل‌های پایه نصب شده‌اند',
    enhancedInstalled: 'تحلیل‌گر پیشرفته نصب شده است',
    removeModels: 'حذف مدل‌های محلی',
    enable: 'فعال‌سازی فرمان صوتی',
    enableHint: 'صدا به‌صورت پیش‌فرض خاموش است و میکروفون فقط با فشردن دکمه باز می‌شود.',
    language: 'زبان گفتار',
    autoLanguage: 'پیروی از زبان محیط',
    speechModel: 'مدل تشخیص گفتار',
    speechTurbo: 'Whisper Large v3 Turbo — بهترین کیفیت فارسی',
    speechSmall: 'Whisper Small — دریافت کوچک‌تر',
    speechModelHint: 'تربو در فارسی به‌مراتب دقیق‌تر است و به پردازندهٔ گرافیکی نیاز دارد. Small یک‌چهارم حجم دارد و همه‌جا کار می‌کند.',
    speechModelNoGpu: 'این مرورگر WebGPU قابل‌استفاده ندارد، پس تنها گزینه Whisper Small است.',
    speechModelNoF16: 'این پردازندهٔ گرافیکی شیدر نیم‌دقتی ندارد، پس تربو با نسخهٔ کامل‌دقت و بزرگ‌ترش اجرا می‌شود. دقت یکسان، دریافت بزرگ‌تر.',
    enhanced: 'تحلیل پیشرفتهٔ محلی',
    enhancedHint: 'مدل اختیاری Qwen فقط هنگام ابهام در مسیریابی قطعی و معنایی استفاده می‌شود.',
    models: 'بسته‌های مدل روی دستگاه',
    diagnostics: 'عیب‌یابی دستگاه',
    runtime: 'موتور انتخاب‌شده',
    halfPrecision: 'نیم‌دقت گرافیکی (fp16)',
    cpuThreads: 'پردازنده‌های منطقی',
    testMicrophone: 'آزمایش مجوز میکروفون',
    microphoneReady: 'میکروفون در دسترس است و هیچ صدایی نگهداری نشد.',
    microphoneDenied: 'دسترسی میکروفون در مرورگر مسدود است.',
    confirm: 'تأیید فرمان',
    confirmHint: 'پیش از ادامه، فرمان حل‌شده را بررسی کنید.',
    runTypedCommand: 'اجرای «{{command}}» به‌عنوان فرمان محلی',
    state: {
      disabled: 'فرمان صوتی خاموش است',
      initializing: 'آماده‌سازی مدل‌های محلی…',
      idle: 'آماده',
      listening: 'در حال شنیدن روی این دستگاه…',
      transcribing: 'تبدیل گفتار به متن به‌صورت محلی…',
      transcribed: 'شنیده‌شده روی این دستگاه',
      understanding: 'درک فرمان به‌صورت محلی…',
      clarifying: 'جزئیات بیشتری لازم است',
      preparing: 'بررسی مجوزها و یافتن رکوردها…',
      confirming: 'بررسی و تأیید',
      executing: 'اجرای فرمان تأییدشده…',
      success: 'فرمان انجام شد',
      error: 'فرمان صوتی انجام نشد',
    },
  },
  assistant: {
    askVarza: 'از ورزا بپرسید',
    title: 'دستیار هوشمند حسابداری ورزا',
    name: 'ورزا',
    subtitle: 'حسابدار هوشمند',
    close: 'بستن ورزا',
    resize: 'تغییر اندازه پنل دستیار',
    chat: 'گفت‌وگو',
    history: 'تاریخچه',
    historyHint: 'همه گفت‌وگوهای شما با ورزا، از تازه‌ترین به قدیمی‌ترین.',
    historySearch: 'جست‌وجوی گفت‌وگوها',
    historyEmpty: 'هنوز گفت‌وگویی ندارید',
    historyEmptyHint: 'از ورزا چیزی بپرسید تا گفت‌وگو اینجا برای مراجعه بعدی نگه داشته شود.',
    openConversation: 'باز کردن این گفت‌وگو',
    activeConversation: 'در حال نمایش',
    // Tab labels sit side by side in a 440px panel; the long form was truncated to «کارهای زمان‌بندی…».
    sending: 'در حال ارسال پیام',
    sendFailed: 'پیام ارسال نشد. متن شما باقی مانده است؛ دوباره تلاش کنید.',
    schedules: 'زمان‌بندی',
    advisories: 'هشدارها',
    advisoryTitle: 'هشدارهای مالی',
    advisoryHint: 'هشدارهای فقط‌خواندنی درباره مانده‌های سررسیدگذشته، موجودی، سقف نقدینگی، مغایرت بانکی و موارد تکراری احتمالی.',
    advisoryFailed: 'بارگذاری هشدارها انجام نشد',
    noAdvisories: 'هشدار فعالی وجود ندارد',
    noAdvisoriesHint: 'ورزا موردی را که نیازمند رسیدگی باشد تشخیص نداده است.',
    dismissAdvisory: 'نادیده گرفتن',
    advisorySeverity: { Info: 'اطلاعاتی', Warning: 'هشدار', Critical: 'بحرانی' },
    advisoryCopy: {
      OverdueReceivablesTitle: 'دریافتنی سررسیدگذشته · {{currencyCode}}',
      OverdueReceivablesMessage: '{{count}} فاکتور به مبلغ {{amount}} {{currencyCode}} سررسید گذشته است؛ قدیمی‌ترین مورد {{maximumDaysOverdue}} روز تأخیر دارد.',
      OverduePayablesTitle: 'پرداختنی سررسیدگذشته · {{currencyCode}}',
      OverduePayablesMessage: '{{count}} صورتحساب تأمین‌کننده به مبلغ {{amount}} {{currencyCode}} سررسید گذشته است؛ {{heldOrDisputed}} مورد متوقف یا مورد اختلاف است.',
      LowStockTitle: 'کمبود موجودی · {{productName}}',
      LowStockMessage: 'از {{productSku}} در انبار {{warehouseCode}} مقدار {{available}} {{unitCode}} قابل‌استفاده و {{incoming}} در راه است.',
      CashLimitTitle: 'عبور از سقف صندوق · {{code}}',
      CashLimitMessage: 'مانده {{balance}} {{currencyCode}} است و به میزان {{excessAmount}} از سقف {{maximumBalance}} بیشتر است.',
      ReconciliationExceptionTitle: 'مغایرت در تطبیق بانکی',
      ReconciliationExceptionMessage: 'تطبیق {{id}} دارای اختلاف تعدیل‌شده {{adjustedDifference}} {{currencyCode}} است.',
      PossibleDuplicateSupplierBillTitle: 'صورتحساب تکراری احتمالی تأمین‌کننده',
      PossibleDuplicateSupplierBillMessage: '{{count}} صورتحساب دارای تأمین‌کننده، تاریخ، ارز و مبلغ یکسان {{totalAmount}} {{currencyCode}} هستند.',
    },
    noAccess: 'دسترسی به دستیار فعال نیست',
    noAccessHint: 'از مدیر سازمان بخواهید مجوزهای مناسب دستیار را به نقش شما اضافه کند.',
    conversations: 'گفت‌وگوها',
    newConversation: 'گفت‌وگوی جدید',
    previewMode: 'حالت محلی',
    connected: 'متصل',
    pendingJournal: 'ویرایش‌شده توسط ربات ورزا · نیازمند تأیید انسان',
    pendingReceipt: 'ویرایش‌شده توسط ربات ورزا · نیازمند تأیید انسان',
    pendingInvoice: 'فاکتور ویرایش‌شده توسط ربات ورزا · نیازمند تأیید انسان',
    pendingSupplierPayment: 'پرداخت تأمین‌کننده ویرایش‌شده توسط ربات ورزا · نیازمند تأیید انسان',
    pendingBankTransfer: 'انتقال بانکی ویرایش‌شده توسط ربات ورزا · نیازمند تأیید انسان',
    pendingCashTransaction: 'تراکنش نقدی ویرایش‌شده توسط ربات ورزا · نیازمند تأیید انسان',
    pendingStatementImport: 'ورود صورت‌حساب بانکی توسط ربات ورزا · نیازمند تأیید انسان',
    pendingPerson: 'شخص جدید پیشنهادی · نیازمند تأیید شما',
    aiProposalHint: 'با تأیید انسانی، سند وارد گردش عادی تصویب می‌شود؛ ربات اجازه تصویب یا ثبت قطعی ندارد.',
    confirmProposal: 'تأیید سند ربات',
    confirmPerson: 'ایجاد شخص',
    speak: 'خواندن این پاسخ',
    thinking: 'ورزا در حال بررسی اطلاعات سازمان است…',
    placeholder: 'یک رویداد مالی را شرح دهید، سؤال بپرسید یا گزارش بخواهید…',
    voice: 'ضبط پیام صوتی',
    audioReply: 'پاسخ را با صدا بخوان',
    elevenLabsAudio: 'صدای ElevenLabs',
    transcribing: 'در حال تبدیل صدای شما به متن و پاسخ به آن…',
    voiceEmpty: 'صدایی تشخیص داده نشد. دوباره تلاش کنید.',
    voiceFailed: 'پردازش پیام صوتی انجام نشد. دوباره تلاش کنید.',
    microphoneDenied: 'دسترسی به میکروفون رد شد. در تنظیمات مرورگر آن را مجاز کنید و دوباره تلاش کنید.',
    recording: 'در حال ضبط',
    recordingLimit: 'ضبط پس از ۲ دقیقه به‌طور خودکار متوقف می‌شود.',
    stopAndSend: 'پایان ضبط و ارسال',
    discardRecording: 'حذف ضبط',
    speechFailed: 'پخش پاسخ صوتی انجام نشد.',
    reviewNotice: 'اسناد هوش مصنوعی تا تأیید شخص مجاز در انتظار باقی می‌مانند.',
    send: 'ارسال',
    sendHint: 'اینتر ارسال می‌کند · شیفت + اینتر خط جدید می‌سازد',
    welcome: 'چطور می‌توانم در امور مالی کمک کنم؟',
    welcomeHint: 'می‌توانم مانده‌ها را توضیح دهم، گزارش HTML بسازم یا یک سند تراز برای بازبینی شما پیشنهاد کنم.',
    promptTransaction: 'هزینه نقدی اداری به مبلغ ۱۲٬۵۰۰٬۰۰۰ ریال ثبت کن',
    promptReport: 'خلاصه تراز آزمایشی این ماه را نشان بده',
    promptReview: 'کدام اسناد هنوز نیاز به رسیدگی دارند؟',
    scheduleName: 'نام برنامه',
    schedulePrompt: 'وظیفه ورزا',
    interval: 'تناوب اجرا',
    hourly: 'هر ساعت',
    daily: 'روزانه',
    weekly: 'هفتگی',
    createSchedule: 'ایجاد برنامه',
    pause: 'توقف',
    resume: 'ادامه',
    memory: 'حافظه',
    knowledge: 'دانش',
    evidence: 'شواهد و استدلال',
    intent: 'قصد',
    risk: 'ریسک',
    duration: 'مدت اجرا',
    tokens: 'توکن‌ها',
    actionProposal: 'عمل پیشنهادی',
    sources: 'منابع و ارجاعات',
    noEvidence: 'برای این پاسخ از شواهد خارجی استفاده نشده است.',
    execution: 'جزئیات اجرا',
    budget: 'بودجه هوش مصنوعی سازمان',
    reviewerRejected: 'توسط بازبین انسانی رد شد.',
    memoryTitle: 'حافظه تحت نظارت',
    memoryHint: 'فقط حافظه‌های تأییدشده و محدود به دامنه در گفت‌وگوهای بعدی استفاده می‌شوند و هر زمان قابل لغو هستند.',
    knowledgeTitle: 'دانش سازمان',
    knowledgeHint: 'سیاست‌ها و رویه‌های قابل استناد را بارگذاری، نتیجه استخراج را بررسی و سپس برای بازیابی منتشر کنید.',
    knowledgeDocumentTitle: 'عنوان سند',
    knowledgeLanguage: 'زبان',
    knowledgeFile: 'فایل منبع',
    uploadKnowledge: 'بارگذاری و پردازش',
    processingKnowledge: 'در حال پردازش…',
    publishKnowledge: 'انتشار',
    knowledgeUploadFailed: 'پردازش منبع انجام نشد. قالب فایل یا لایه متنی آن را بررسی کنید.',
    characters: 'نویسه',
    chunks: 'بخش',
  },
  app: {
    name: 'ورزا',
    tagline: 'سامانه هوشمند مدیریت کسب‌وکار',
    product: 'حسابداری و انبار',
    workspace: 'فضای کاری مالی',
    skipToContent: 'رفتن به محتوای اصلی',
    poweredBy: 'قدرت‌گرفته از Exirsoft',
  },
  common: {
    actions: 'عملیات',
    add: 'افزودن',
    all: 'همه',
    approve: 'تأیید',
    back: 'بازگشت',
    cancel: 'انصراف',
    close: 'بستن',
    create: 'ایجاد',
    date: 'تاریخ',
    delete: 'حذف',
    edit: 'ویرایش',
    export: 'خروجی',
    filters: 'فیلترها',
    more: 'بیشتر',
    new: 'جدید',
    next: 'بعدی',
    previous: 'قبلی',
    refresh: 'به‌روزرسانی',
    reset: 'بازنشانی',
    save: 'ذخیره',
    saving: 'در حال ذخیره…',
    saveDraft: 'ذخیره پیش‌نویس',
    search: 'جست‌وجو',
    select: 'انتخاب کنید…',
    status: 'وضعیت',
    total: 'جمع',
    submit: 'ارسال',
    view: 'مشاهده',
    viewAll: 'مشاهده همه',
    notAvailable: 'در دسترس نیست',
    loading: 'در حال بارگذاری فضای کاری…',
    retry: 'تلاش دوباره',
    noResults: 'نتیجه‌ای یافت نشد',
    lastUpdated: 'آخرین به‌روزرسانی',
    currency: 'ارز',
    amount: 'مبلغ',
    owner: 'مسئول',
    reference: 'مرجع',
    description: 'شرح',
    details: 'جزئیات',
    activate: 'فعال‌سازی',
    deactivate: 'غیرفعال‌سازی',
    remove: 'حذف',
    reject: 'رد',
    yes: 'بله',
    no: 'خیر',
  },
  nav: {
    overview: 'نمای کلی',
    workspace: 'فضای کاری',
    operations: 'عملیات',
    accounting: 'حسابداری',
    baseData: 'اطلاعات پایه',
    dashboard: 'داشبورد',
    journals: 'اسناد روزنامه',
    ledger: 'دفتر کل',
    accounts: 'کدینگ حساب‌ها',
    persons: 'اشخاص',
    customers: 'مشتریان',
    suppliers: 'تأمین‌کنندگان',
    interOrganizationIdentity: 'شناسه بین‌سازمانی',
    exchangeRates: 'نرخ‌های ارز',
    currencyAdministration: 'مدیریت ارزها',
    tax: 'مالیات',
    fixedAssets: 'دارایی‌های ثابت',
    budgeting: 'بودجه‌بندی',
    costing: 'بهای تمام‌شده و سودآوری',
    projects: 'پروژه‌ها و قراردادها',
    personalFinance: 'مالی شخصی و خانواده',
    inventory: 'انبار',
    reports: 'گزارش‌ها',
    receivables: 'دریافتنی‌ها',
    payables: 'پرداختنی‌ها',
    treasury: 'نقد و بانک',
    purchasing: 'خرید',
    purchaseOrders: 'سفارش‌های خرید',
    sales: 'فروش',
    salesOrders: 'سفارش‌های فروش',
    automation: 'اتوماسیون اسناد',
    workforce: 'نیروی کار',
    payroll: 'حقوق و دستمزد',
    settings: 'تنظیمات',
    access: 'کاربران و دسترسی',
    shortLedger: 'دفتر کل',
    collapse: 'جمع‌کردن راهبری',
    expand: 'بازکردن راهبری',
    openMenu: 'بازکردن فهرست راهبری',
    closeMenu: 'بستن فهرست راهبری',
    quickNav: 'راهبری سریع',
    homepage: 'صفحه اصلی ورزا',
  },
  professionalServices: {
    title: 'عملیات خدمات حرفه‌ای',
    subtitle: 'زمان، هزینه قابل بازپرداخت، صورتحساب کار در جریان، هزینه کارکنان و سودآوری معتبر این قرارداد.',
    tabs: {
      performance: 'عملکرد',
      time: 'زمان',
      expenses: 'هزینه‌ها',
      wip: 'کار در جریان و صورتحساب',
      'staff-allocations': 'هزینه کارکنان',
    },
    timeTitle: 'زمان به تفکیک پروژه',
    timeHint: 'زمان متعلق به مستأجر با نرخ قرارداد تثبیت می‌شود و فقط یک‌بار قابل صورتحساب است.',
    noTime: 'برای این قرارداد ثبت زمانی وجود ندارد',
    date: 'تاریخ',
    activity: 'فعالیت',
    teamMember: 'عضو تیم',
    hours: 'ساعت',
    value: 'ارزش قابل صورتحساب',
    status: 'وضعیت',
    actions: 'عملیات',
    void: 'ابطال',
    timeVoided: 'ثبت زمان صورتحساب‌نشده باطل شد.',
    expensesTitle: 'بازیافت هزینه',
    expensesHint: 'هزینه پروژه مبتنی بر رسید با سند پرداختنی کارکنان و وضعیت تثبیت‌شده مطالبه از مشتری.',
    noExpenses: 'برای این قرارداد هزینه قابل بازپرداختی وجود ندارد',
    category: 'دسته‌بندی',
    amount: 'مبلغ',
    clientRecovery: 'مطالبه از مشتری',
    evidence: 'مدرک حسابداری',
    wipTitle: 'صورتحساب کار در جریان',
    wipHint: 'زمان، هزینه، مرحله، تخفیف، مالیات، ارز و مدرک نرخ تبدیل تثبیت‌شده و متصل به دریافتنی‌ها.',
    noWip: 'برای این قرارداد صورتحساب کار در جریانی وجود ندارد',
    invoice: 'صورتحساب',
    revenue: 'درآمد پیش از مالیات',
    tax: 'مالیات',
    due: 'سررسید',
    staffTitle: 'تخصیص هزینه کارکنان',
    staffHint: 'هزینه حقوق ثبت‌شده دقیقاً یک‌بار به اعضای مؤثر تیم قرارداد تخصیص می‌یابد.',
    noStaff: 'برای این قرارداد تخصیص هزینه کارکنان ثبت‌شده‌ای وجود ندارد',
    period: 'دوره',
    method: 'روش',
    payrollRun: 'لیست حقوق',
    performanceTitle: 'بهره‌وری، تحقق و سودآوری',
    performanceHint: 'شاخص‌های هم‌ارز مبتنی بر اسناد ثبت‌شده پروژه، حقوق، دریافتنی و دفتر کل.',
    utilization: 'بهره‌وری',
    realization: 'تحقق درآمد',
    unbilledWip: 'کار در جریان صورتحساب‌نشده',
    profit: 'سود پروژه',
    receivablesAging: 'سن مطالبات',
    retainerBalance: 'مانده پیش‌دریافت',
    billedRevenue: 'درآمد صورتحساب‌شده',
    postedExpenses: 'هزینه‌های ثبت‌شده',
    expenseRecovery: 'هزینه بازیافت‌شده',
    allocatedStaffCost: 'هزینه تخصیص‌یافته کارکنان',
    authoritativeFootnote: 'اسناد معتبر به ارز نمایش‌داده‌شده',
  },
  customers: {
    title: 'مشتریان',
    subtitle: 'فهرست مشتریان فروش و اطلاعات اعتباری و تماس آن‌ها.',
    newCustomer: 'مشتری جدید',
    editCustomer: 'ویرایش مشتری',
    formHint: 'اطلاعات مشتری مورد استفاده در سفارش‌های فروش و دریافتنی‌ها را ثبت کنید.',
    person: 'شخص اصلی',
    code: 'کد مشتری',
    name: 'نام مشتری',
    currency: 'ارز',
    creditLimit: 'سقف اعتبار',
    taxRegistrationNumber: 'شماره ثبت مالیاتی',
    email: 'ایمیل',
    phone: 'تلفن',
    billingAddress: 'نشانی صورتحساب',
    active: 'مشتری فعال است',
    contact: 'اطلاعات تماس',
    searchPlaceholder: 'جست‌وجوی مشتری بر پایه کد، نام، ایمیل یا تلفن',
    showInactive: 'نمایش غیرفعال‌ها',
    noCustomers: 'هنوز مشتری‌ای ثبت نشده است',
    noCustomersHint: 'نخستین مشتری را برای استفاده در سفارش فروش و فاکتور اضافه کنید.',
    onHold: 'توقف اعتباری',
  },
  suppliers: {
    title: 'تأمین‌کنندگان',
    subtitle: 'فهرست تأمین‌کنندگان خرید و اطلاعات مالیاتی و پرداخت آن‌ها.',
    newSupplier: 'تأمین‌کننده جدید',
    editSupplier: 'ویرایش تأمین‌کننده',
    formHint: 'اطلاعات تأمین‌کننده مورد استفاده در سفارش‌های خرید و پرداختنی‌ها را ثبت کنید.',
    person: 'شخص اصلی',
    code: 'کد تأمین‌کننده',
    name: 'نام تأمین‌کننده',
    currency: 'ارز',
    withholdingTaxPercent: 'نرخ مالیات تکلیفی',
    taxRegistrationNumber: 'شماره ثبت مالیاتی',
    email: 'ایمیل',
    phone: 'تلفن',
    remitToAddress: 'نشانی پرداخت',
    active: 'تأمین‌کننده فعال است',
    contact: 'اطلاعات تماس',
    searchPlaceholder: 'جست‌وجوی تأمین‌کننده بر پایه کد، نام، ایمیل یا تلفن',
    showInactive: 'نمایش غیرفعال‌ها',
    noSuppliers: 'هنوز تأمین‌کننده‌ای ثبت نشده است',
    noSuppliersHint: 'نخستین تأمین‌کننده را برای استفاده در سفارش خرید و صورتحساب اضافه کنید.',
  },
  workspace: {
    canvas: 'میز کار اسناد',
    pane: 'قاب میز کار',
    openDocuments: 'اسناد باز',
    documentMenu: 'دستورهای سند',
    paneMenu: 'دستورهای قاب',
    documentList: 'همهٔ اسناد این قاب',
    closeDocument: 'بستن',
    closeNamedDocument: 'بستن {{name}}',
    closeOthers: 'بستن بقیه',
    closeAll: 'بستن همه',
    closePane: 'بستن این قاب',
    pin: 'سنجاق کردن این زبانه',
    unpin: 'برداشتن سنجاق',
    duplicate: 'باز کردن نسخه دوم',
    splitBeside: 'تقسیم در کنار',
    splitBelow: 'تقسیم در پایین',
    floatDocument: 'باز کردن در پنجره',
    dockDocument: 'بازگرداندن به میز کار',
    dockWindow: 'اتصال این پنجره به میز کار',
    back: 'بازگشت',
    forward: 'جلو',
    maximizePane: 'تمام‌صفحه کردن این قاب',
    restorePane: 'بازگرداندن این قاب',
    maximize: 'بیشینه',
    restoreWindow: 'بازگرداندن',
    minimize: 'کمینه',
    closeWindow: 'بستن پنجره',
    floatingWindow: 'پنجره',
    minimizedWindows: 'پنجره‌های کمینه‌شده',
    resizePanes: 'تغییر اندازهٔ قاب‌ها',
    dropAsTab: 'رها کنید تا به‌صورت زبانه افزوده شود',
    dropAsSplit: 'رها کنید تا این قاب تقسیم شود',
    dropAtEdge: 'رها کنید تا در این لبه مستقر شود',
    dropAsWindow: 'رها کنید تا در پنجره‌ای مستقل باز شود',
    dropNowhere: 'اینجا جای رها کردن نیست',
    startTitle: 'اینجا چیزی باز نیست',
    startSubtitle: 'هر تعداد دفتر که کار نیاز دارد باز کنید و آن‌ها را کنار هم بچینید.',
    quickOpen: 'باز کردن',
    recent: 'به‌تازگی باز شده',
    hintDrag: 'زبانه را به لبهٔ یک قاب بکشید تا دو سند کنار هم بنشینند.',
    hintFloat: 'زبانه را بیرون از میز کار بکشید — یا کلید Ctrl را نگه دارید — تا پنجره‌ای جابه‌جاشدنی و تغییراندازه‌پذیر بگیرید.',
    hintKeys: 'Alt+W می‌بندد، Alt+M قاب را تمام‌صفحه می‌کند و Alt با کلیدهای جهت میان زبانه‌ها جابه‌جا می‌شود.',
    openCount_one: '{{count}} سند باز',
    openCount_other: '{{count}} سند باز',
    floatingCount_one: '{{count}} در پنجره',
    floatingCount_other: '{{count}} در پنجره',
    documents: {
      journal: 'سند روزنامه',
    },
  },
  context: {
    tenant: 'سازمان',
    tenantName: 'شرکت بازرگانی رها',
    branch: 'شعبه',
    branchName: 'دفتر مرکزی',
    allBranches: 'همه شعب',
    period: 'دوره مالی',
    periodName: 'مرداد ۱۴۰۵',
    functionalCurrency: 'ارز عملیاتی',
    currencyMenu: 'نمایش جزئیات ارز',
    enabledCurrencies: 'ارزهای فعال',
    disabledCurrency: 'غیرفعال',
    viewExchangeRates: 'مشاهده نرخ‌های ارز',
    language: 'زبان',
    english: 'English',
    persian: 'فارسی',
    arabic: 'العربية',
    notifications: 'اعلان‌ها',
    pendingApprovals: '{{count}} تأیید در انتظار',
    userMenu: 'بازکردن منوی کاربر',
    userName: 'سارا احمدی',
    userRole: 'مدیر مالی',
    openPeriod: 'دوره باز',
    softClosedPeriod: 'بسته‌شده موقت',
    hardClosedPeriod: 'بسته‌شده قطعی',
    scopeUnavailable: 'دامنه فضای کاری در دسترس نیست',
    switchingTenant: 'در حال تغییر سازمان…',
    scope: 'دامنه فضای کاری',
    editScope: 'تغییر دامنه فضای کاری',
  },
  auth: {
    loginTitle: 'ورود به ورزا',
    loginSubtitle: 'برای ورود به فضای کاری مالی مجاز، از حساب خود استفاده کنید.',
    username: 'نام کاربری',
    verifyTitle: 'تأیید هویت',
    verifySubtitle: 'مرحله تأیید اضافی موردنیاز این حساب را تکمیل کنید.',
    email: 'ایمیل',
    password: 'گذرواژه',
    verificationMethod: 'روش تأیید',
    verificationCode: 'کد تأیید',
    authenticatorHint: 'کد شش‌رقمی فعلی برنامه احراز هویت را وارد کنید.',
    deliveredCodeHint: 'یک کد درخواست کنید و سپس شش رقم دریافتی را وارد کنید.',
    sendCode: 'ارسال کد تأیید',
    codeSent: 'کد تأیید جدید ارسال شد.',
    developmentCode: 'کد محیط توسعه: {{code}}',
    orContinueWith: 'یا ورود با',
    // "Google" is a mark, not a word to translate: Google's branding guidelines require it in
    // Latin script in every locale, so only the phrase around it is Persian.
    continueGoogle: 'ادامه با Google',
    continueMicrosoft: 'ادامه با مایکروسافت',
    external_failed: 'ورود با سرویس خارجی تکمیل نشد.',
    external_not_linked: 'این حساب سرویس‌دهنده به کاربر ورزا متصل نیست. با گذرواژه وارد شوید و آن را در تنظیمات متصل کنید، یا با همان سرویس‌دهنده حساب بسازید.',
    external_email_taken: 'یک حساب ورزا با این نشانی ایمیل وجود دارد. با گذرواژه وارد شوید و سپس سرویس‌دهنده را در تنظیمات متصل کنید.',
    external_email_unusable: 'سرویس‌دهنده نشانی ایمیل تأییدشده‌ای برنگرداند، بنابراین حسابی ساخته نشد.',
    totpCode: 'کد احراز هویت (اختیاری)',
    totpHint: 'فقط در صورت فعال‌بودن احراز هویت چندمرحله‌ای لازم است.',
    signIn: 'ورود',
    signingIn: 'در حال ورود…',
    verify: 'تأیید',
    mfaPrompt: 'احراز هویت چندمرحله‌ای برای این حساب الزامی است.',
    credentialsError: 'ایمیل، گذرواژه یا کد تأیید پذیرفته نشد.',
    lockedError: 'این حساب موقتاً قفل است. بعداً دوباره تلاش کنید.',
    connectionError: 'ارتباط با سرویس احراز هویت برقرار نشد. دوباره تلاش کنید.',
    captchaError: 'ورزا نتوانست تأیید کند این درخواست از سوی یک انسان است. صفحه را دوباره بارگیری کنید و تلاش کنید.',
    captchaUnavailableError: 'سرویس تأیید انسانی در دسترس نیست. لحظاتی بعد دوباره تلاش کنید.',
    selectTenantTitle: 'انتخاب فضای کاری',
    selectTenantSubtitle: 'یکی از سازمان‌های اختصاص‌یافته به حساب خود را انتخاب کنید.',
    selecting: 'در حال بازکردن…',
    openTenant: 'ورود به فضای کاری',
    noTenants: 'هیچ عضویت فعال سازمانی به این حساب اختصاص نیافته است.',
    tenantError: 'سازمان انتخاب نشد. فضای کاری قبلی شما بدون تغییر باقی ماند.',
    logout: 'خروج',
    mfaSession: 'نشست محافظت‌شده با احراز چندمرحله‌ای',
    passwordSession: 'نشست گذرواژه‌ای',
    forgotLink: 'گذرواژه را فراموش کرده‌اید؟',
    createAccount: 'ثبت‌نام با دعوت‌نامه',
    forgotTitle: 'بازنشانی گذرواژه',
    forgotSubtitle: 'ایمیل حساب خود را وارد کنید تا پیوند امن و یک‌بارمصرف بازنشانی فرستاده شود.',
    recoverySentTitle: 'ایمیل خود را بررسی کنید',
    recoverySentBody: 'اگر این نشانی متعلق به حسابی باشد، پیوند بازنشانی گذرواژه در راه است.',
    recoverySentHint: 'برای امنیت شما، پیوند منقضی می‌شود و تنها یک‌بار کار می‌کند.',
    developmentRecovery: 'نسخه توسعه:',
    resetNow: 'همین حالا بازنشانی کنید',
    backToSignIn: 'بازگشت به ورود',
    sendRecovery: 'ارسال پیوند بازنشانی',
    recoveryFailed: 'درخواست بازنشانی انجام نشد. دوباره تلاش کنید.',
    recoveryRateLimited: 'تلاش‌ها بیش از حد بود. کمی صبر کنید و دوباره تلاش کنید.',
    resetTitle: 'انتخاب گذرواژه جدید',
    resetSubtitle: 'از توکن امن ایمیل استفاده کنید و یک گذرواژه قوی تازه بسازید.',
    recoveryToken: 'توکن بازنشانی',
    newPassword: 'گذرواژه جدید',
    confirmPassword: 'تکرار گذرواژه جدید',
    passwordLengthHint: 'دست‌کم {{count}} نویسه وارد کنید.',
    passwordTooShort: 'دست‌کم {{count}} نویسه وارد کنید.',
    passwordMismatch: 'دو گذرواژه یکسان نیستند.',
    resetInvalid: 'این پیوند بازنشانی نامعتبر، منقضی یا استفاده‌شده است.',
    resetFailed: 'گذرواژه بازنشانی نشد. دوباره تلاش کنید.',
    resetAction: 'ثبت گذرواژه جدید',
    requestNewLink: 'درخواست پیوند بازنشانی تازه',
    resetCompleteTitle: 'گذرواژه به‌روز شد',
    resetCompleteBody: 'گذرواژه جدید شما آماده استفاده است.',
    resetSessionsEnded: 'نشست‌های دیگر پایان یافتند. با گذرواژه جدید دوباره وارد شوید.',
    adminConsole: 'مدیریت سامانه',
    invitation_invalid: 'این دعوت‌نامه نامعتبر، منقضی، لغوشده یا استفاده‌شده است.',
  },
  administration: {
    console: 'مدیریت سامانه',
    workspace: 'فضای کاری سازمان',
    systemScope: 'سطح سامانه',
    title: 'کنترل پنل سازمان‌ها',
    subtitle: 'ایجاد و مدیریت همه سازمان‌های حسابداری از یک فهرست.',
    newTenant: 'سازمان جدید',
    createFailed: 'ایجاد سازمان انجام نشد.',
    stateFailed: 'وضعیت سازمان تغییر نکرد.',
    provisioningUnavailable: 'ایجاد پایگاه داده پیکربندی نشده است.',
    provisioningHint: ' اتصال مدیر و کاربر اجرایی پایگاه داده را تنظیم کنید.',
    summary: 'خلاصه سازمان‌ها',
    totalTenants: 'همه سازمان‌ها',
    activeTenants: 'سازمان‌های فعال',
    suspendedTenants: 'سازمان‌های تعلیق‌شده',
    totalUsers: 'کاربران سازمان‌ها',
    available: 'آماده ورود',
    restricted: 'دسترسی متوقف است',
    memberships: 'عضویت فعال',
    tenantRegister: 'فهرست سازمان‌ها',
    tenantRegisterSubtitle: 'وضعیت، پایگاه داده و اعضا',
    noTenants: 'هنوز سازمانی وجود ندارد',
    noTenantsHint: 'اولین سازمان حسابداری را ایجاد کنید.',
    organization: 'سازمان',
    database: 'پایگاه داده',
    users: 'کاربران',
    created: 'تاریخ ایجاد',
    open: 'ورود',
    suspend: 'تعلیق',
    activate: 'فعال‌سازی',
    createTitle: 'ایجاد سازمان',
    createSubtitle: 'یک پایگاه داده حسابداری جداگانه و نقش مدیر ایجاد می‌شود.',
    provisioning: 'در حال ایجاد…',
    create: 'ایجاد سازمان',
    displayName: 'نام سازمان',
    slug: 'شناسه سازمان',
    language: 'زبان پیش‌فرض',
    databasePreview: 'الگوی نام پایگاه داده',
    systemUsers: 'کاربران سامانه',
    systemUsersHint: 'حساب‌هایی را انتخاب کنید که اجازه ساخت دعوت‌نامه ثبت‌نام دارند. مدیران ارشد همیشه این اجازه را دارند.',
    noSystemUsers: 'کاربری در سامانه نیست',
    noSystemUsersHint: 'حساب‌ها پس از ایجاد در اینجا نمایش داده می‌شوند.',
    user: 'کاربر',
    canInvite: 'اجازه ساخت دعوت‌نامه',
    superAdminImplicit: 'همیشه مجاز',
    invitationAllowed: 'مجاز',
    invitationNotAllowed: 'غیرمجاز',
    invitationPermissionFailed: 'مجوز ساخت دعوت‌نامه تغییر نکرد.',
  },
  invitations: {
    title: 'دعوت‌نامه‌های ثبت‌نام',
    subtitle: 'کدهای یک‌بارمصرف ورود کاربران تازه به ورزا را بسازید یا لغو کنید.',
    back: 'بازگشت',
    governance: 'حاکمیت حساب',
    createTitle: 'ساخت دعوت‌نامه',
    createHint: 'هر کد تنها یک حساب را می‌پذیرد. آن را فقط به شخص موردنظر بدهید.',
    oneTimeTitle: 'این کد را اکنون ذخیره کنید',
    oneTimeHint: 'کد کامل فقط همین یک‌بار نمایش داده می‌شود. ورزا تنها هش یک‌طرفه آن را نگه می‌دارد.',
    rawCode: 'کد دعوت‌نامه',
    registrationLink: 'پیوند قابل اشتراک ثبت‌نام',
    copied: 'رونوشت شد',
    copyCode: 'رونوشت کد',
    copyLink: 'رونوشت پیوند',
    copyFailed: 'رونوشت خودکار انجام نشد. مقدار را انتخاب و دستی رونوشت کنید.',
    savedCode: 'کد را ذخیره کرده‌ام',
    label: 'برچسب',
    labelHint: 'توضیح اختیاری که فقط مدیران دعوت‌نامه می‌بینند.',
    expiresIn: 'انقضا پس از چند روز',
    expiryInvalid: 'یک عدد صحیح از ۱ تا {{count}} روز وارد کنید.',
    createAction: 'ساخت دعوت‌نامه',
    createFailed: 'دعوت‌نامه ساخته نشد.',
    revokeFailed: 'دعوت‌نامه لغو نشد.',
    never: 'هرگز',
    registerTitle: 'فهرست دعوت‌نامه‌ها',
    registerHint: 'پس از ساخت فقط نشانه امن کد می‌ماند و کد کامل بازیابی‌پذیر نیست.',
    emptyTitle: 'هنوز دعوت‌نامه‌ای نیست',
    emptyHint: 'نخستین دعوت‌نامه یک‌بارمصرف ثبت‌نام را بسازید.',
    codeHint: 'نشانه کد',
    created: 'ساخته‌شده',
    expires: 'انقضا',
    unlabelled: 'دعوت‌نامه بدون برچسب',
    status: {
      active: 'فعال',
      used: 'استفاده‌شده',
      revoked: 'لغوشده',
      expired: 'منقضی',
      unknown: 'ناموجود',
    },
    revoke: 'لغو',
  },
  aiControl: {
    ...en.aiControl,
    monitoring: '\u0646\u0638\u0627\u0631\u062a \u0648 \u0648\u0631\u0648\u062f \u062f\u0627\u0646\u0634',
    knowledgeHealth: '\u0633\u0644\u0627\u0645\u062a \u0647\u0648\u0634 \u062f\u0627\u0646\u0634\u06cc',
    knowledgeHealthHint: '\u0648\u0636\u0639\u06cc\u062a \u0632\u0646\u062f\u0647 \u0641\u0647\u0631\u0633\u062a\u060c \u0628\u0631\u062f\u0627\u0631\u0647\u0627\u060c \u06af\u0631\u0627\u0641\u060c \u0628\u0627\u0632\u0628\u06cc\u0646\u06cc \u0648 \u0648\u0631\u0648\u062f \u062f\u0627\u0646\u0634.',
    syncCatalog: '\u0647\u0645\u06af\u0627\u0645\u200c\u0633\u0627\u0632\u06cc \u0641\u0647\u0631\u0633\u062a',
    ingestDocument: '\u0648\u0631\u0648\u062f \u0633\u0646\u062f \u0628\u0647 \u06af\u0631\u0627\u0641 \u062f\u0627\u0646\u0634',
    ingestDocumentHint: '\u0633\u0646\u062f \u0631\u0627 \u0628\u0647 \u0628\u062e\u0634\u200c\u0647\u0627\u06cc \u0642\u0627\u0628\u0644 \u0627\u0631\u062c\u0627\u0639 \u062a\u0628\u062f\u06cc\u0644\u060c \u0628\u0631\u062f\u0627\u0631\u0633\u0627\u0632\u06cc \u0648 \u0628\u0627 \u0631\u062f\u067e\u0627\u06cc \u0645\u0646\u0628\u0639 \u0648\u0627\u0631\u062f \u06af\u0631\u0627\u0641 \u0645\u06cc\u200c\u06a9\u0646\u062f.',
    ingestIntoGraph: '\u0648\u0631\u0648\u062f \u0628\u0647 \u06af\u0631\u0627\u0641 \u062f\u0627\u0646\u0634',
    ingestionRuns: '\u0627\u062c\u0631\u0627\u0647\u0627\u06cc \u0648\u0631\u0648\u062f \u0633\u0646\u062f',
    knowledgeJurisdictionCode: '\u06a9\u062f \u062d\u0648\u0632\u0647 \u0642\u0636\u0627\u06cc\u06cc \u062f\u0627\u0646\u0634 \u0633\u0627\u0632\u0645\u0627\u0646',
    eyebrow: 'حاکمیت هوش مصنوعی',
    title: 'مرکز کنترل هوش مصنوعی',
    subtitle: 'دانش، عامل‌ها، مدل‌ها، سیاست‌ها، ارزیابی‌ها و اجرای امن سازمانی را از یک فضای کاری حسابرسی‌شده مدیریت کنید.',
    health: 'سلامت سکوی هوش مصنوعی',
    publishedPackages: 'دانش منتشرشده',
    indexedChunks: 'بخش‌های نمایه‌شده',
    operations24h: 'عملیات ۲۴ ساعت',
    failed: 'ناموفق',
    tokens24h: 'توکن ۲۴ ساعت',
    supervision: 'نظارت',
    graphCandidates: 'رابطه پیشنهادی',
    areas: 'حوزه‌های کنترل',
    tabs: {
      resources: '\u0645\u062f\u06cc\u0631\u06cc\u062a \u0645\u0646\u0627\u0628\u0639',
      knowledge: 'بسته‌های دانشی', graph: 'گراف دانش', agents: 'عامل‌ها', models: 'مدل‌ها',
      behavior: 'رفتار', operations: 'عملیات و ردپاها', conversations: 'گفت‌وگوهای دستیار', evaluations: 'ارزیابی‌ها',
    },
    conversations: 'گفت‌وگوهای دستیار',
    conversationsHint: 'نشست‌های راهنمای زنده ناشناس و کاربران ثبت‌نام‌شده. محتوای پیام فقط پس از ثبت دلیل دسترسی حسابرسی‌شده نمایش داده می‌شود.',
    searchConversations: 'جست‌وجوی کاربر، سازمان، بازدیدکننده، نشست، صفحه یا دستگاه',
    surface: 'محل گفت‌وگو',
    allSurfaces: 'همه محل‌های گفت‌وگو',
    publicLiveHelp: 'راهنمای زنده عمومی',
    dashboardChat: 'داشبورد کاربر ثبت‌نام‌شده',
    participantKind: 'نوع کاربر',
    unknownUsers: 'کاربران ناشناس',
    registeredUsers: 'کاربران ثبت‌نام‌شده',
    lastActivity: 'آخرین فعالیت',
    participant: 'کاربر',
    device: 'دستگاه',
    network: 'شبکه پوشانده‌شده',
    turns: 'نوبت‌ها',
    unknownUser: 'کاربر ناشناس',
    chatDetail: 'جزئیات نشست گفت‌وگو',
    chatAccessWarning: 'نمایش محتوای پیام، هویت شما، دلیل، نشست، سازمان، شناسه همبستگی و زمان دسترسی را در گزارش دسترسی ویژه ثبت می‌کند.',
    inspectChat: 'ثبت دلیل و نمایش',
    identity: 'هویت',
    sessionDuration: 'مدت نشست',
    clientDetails: 'جزئیات کارخواه و هش پایدار دستگاه',
    messages: 'پیام‌ها',
    userMessage: 'پیام کاربر',
    assistantMessage: 'پاسخ دستیار',
    newPackage: 'بسته دانشی جدید',
    code: 'کد',
    name: 'نام',
    packageType: 'نوع بسته',
    domain: 'حوزه',
    languages: 'زبان‌ها',
    jurisdiction: 'حوزه قضایی',
    globalJurisdiction: 'سراسری / بدون حوزه قضایی خاص',
    authority: 'مرجع منبع',
    supersedes: 'جایگزین بسته',
    none: 'هیچ‌کدام',
    newJurisdiction: 'ایجاد حوزه قضایی',
    nameEn: 'نام انگلیسی',
    nameFa: 'نام فارسی',
    effectiveFrom: 'معتبر از',
    effectiveTo: 'معتبر تا',
    packages: 'بسته‌های دانشی',
    process: 'پردازش و نمایه‌سازی',
    publish: 'انتشار',
    rollback: 'بازگردانی',
    sourceUri: 'نشانی منبع رسمی',
    upload: 'بارگذاری منبع',
    documents: 'سند',
    chunks: 'بخش',
    citationPreview: 'پیش‌نمایش بخش، بردار و ارجاع',
    embedded: 'بردارسازی‌شده',
    graphReview: 'بازبینی روابط گراف',
    fact: 'واقعیت پیشنهادی',
    confidence: 'اطمینان',
    highImpact: 'اثر بالا',
    newDefinition: 'تعریف نسخه‌دار جدید',
    definitionType: 'نوع تعریف',
    version: 'نسخه',
    jsonDefinition: 'تعریف JSON',
    definitionKinds: { prompt: 'الگوی فرمان', agent: 'عامل', model: 'پروفایل مدل', policy: 'سیاست' },
    responsibility: 'مسئولیت و قواعد ارجاع',
    modelProfile: 'کد پروفایل مدل',
    allowedTools: 'ابزارهای مجاز JSON',
    autonomyLevel: 'حداکثر سطح خودمختاری (۰ تا ۴)',
    tenantOverrides: 'سیاست‌های ویژه سازمان',
    tenantOverridesHint: 'بودجه، نظارت، خودمختاری، محدودیت اجرا و نگهداشت هر سازمان را با ثبت حسابرسی تنظیم کنید.',
    policySaved: 'سیاست سازمان ذخیره شد',
    dailyTokens: 'بودجه روزانه توکن',
    monthlyCost: 'بودجه هزینه ماهانه',
    maxModelCalls: 'حداکثر فراخوانی مدل',
    maxToolCalls: 'حداکثر فراخوانی ابزار',
    timeoutSeconds: 'مهلت اجرا (ثانیه)',
    retentionDays: 'نگهداشت ردپا (روز)',
    functionalCurrency: 'ارز عملیاتی اسناد ایجادشده توسط ربات',
    receiptCashAccount: 'شناسه حساب دفتر کل وجه دریافتی',
    receiptReceivableAccount: 'شناسه حساب کنترل دریافتنی',
    receiptUnappliedAccount: 'شناسه حساب دریافت تخصیص‌نیافته',
    receiptFxGainAccount: 'شناسه حساب سود تسعیر تحقق‌یافته',
    receiptFxLossAccount: 'شناسه حساب زیان تسعیر تحقق‌یافته',
    invoiceReceivableAccount: 'شناسه حساب دریافتنی فاکتور',
    invoiceRevenueAccount: 'شناسه حساب درآمد فاکتور',
    supplierPaymentCashAccount: 'شناسه حساب دفتر کل وجه پرداختی',
    supplierPaymentPayableAccount: 'شناسه حساب پرداختنی تأمین‌کننده',
    supplierPaymentPrepaymentAccount: 'شناسه حساب پیش‌پرداخت تأمین‌کننده',
    supplierPaymentBankAccount: 'شناسه حساب بانکی خزانه (بانک یا صندوق)',
    supplierPaymentCashBox: 'شناسه صندوق خزانه (بانک یا صندوق)',
    sensitiveDocumentAutomation: 'اجازه ایجاد اسناد حساس توسط هوش مصنوعی',
    automationEnabled: 'فعال · هوش مصنوعی فقط پیش‌نویس با نشان ربات ورزا می‌سازد و تأیید انسان الزامی است.',
    automationDisabled: 'غیرفعال · هوش مصنوعی نمی‌تواند سند حسابداری ایجاد یا ویرایش کند.',
    policyChangeReason: 'دلیل حسابرسی‌شده این تغییر',
    provider: 'ارائه‌دهنده',
    modelId: 'شناسه مدل ارائه‌دهنده',
    purpose: 'هدف مسیریابی',
    capabilities: 'برچسب قابلیت‌ها JSON',
    fallbackChain: 'زنجیره جایگزین JSON',
    contextLimit: 'حد زمینه',
    inputCost: 'هزینه ورودی در میلیون',
    outputCost: 'هزینه خروجی در میلیون',
    structuredOutput: 'پشتیبانی از خروجی ساخت‌یافته',
    scope: 'دامنه سیاست',
    versionedRegistry: 'فهرست نسخه‌دار',
    operations: 'نمایه عملیات بین‌سازمانی',
    accessReason: 'دلیل دسترسی ویژه',
    started: 'زمان شروع',
    tenant: 'سازمان',
    intent: 'قصد',
    agent: 'عامل',
    usage: 'مصرف',
    risk: 'ریسک',
    duration: 'مدت اجرا',
    modelCalls: 'فراخوانی مدل',
    toolCalls: 'فراخوانی ابزار',
    request: 'درخواست کاربر',
    policyDecision: 'تصمیم سیاست',
    evidence: 'شواهد',
    actionProposal: 'عمل پیشنهادی',
    supervisorReview: 'بازبینی ناظر',
    reviewLabel: 'برچسب بازبینی',
    supervisorNote: 'یادداشت ناظر',
    correct: 'درست',
    incorrect: 'نادرست',
    needsReview: 'نیازمند بازبینی',
    saveReview: 'ذخیره بازبینی حسابرسی‌شده',
    reviewSaved: 'بازبینی ذخیره شد',
    promoteEvaluation: 'افزودن به ارزیابی',
    promoted: 'به ارزیابی افزوده شد',
    newEvaluation: 'مورد ارزیابی جدید',
    suite: 'مجموعه',
    inputJson: 'ورودی JSON',
    expectedJson: 'نتیجه مورد انتظار JSON',
    tagsJson: 'برچسب‌ها به‌صورت JSON',
    runEvaluation: 'اجرای ارزیابی',
    runningEvaluation: 'در حال اجرا…',
    releaseTarget: 'نسخه هدف انتشار',
    traceReplayOnly: 'فقط بازپخش رد اجرا',
    minimumScore: 'حداقل امتیاز مجموعه',
    evaluationRuns: 'اجراهای ارزیابی',
    criticalFailures: 'شکست بحرانی',
    evaluationCorpus: 'مجموعه ارزیابی دوزبانه',
  },
  access: {
    security: 'امنیت سازمان',
    title: 'کاربران و دسترسی',
    subtitle: 'عضویت‌ها، نقش‌ها و مجوزهای سازمان را مدیریت کنید.',
    newUser: 'کاربر جدید',
    newRole: 'نقش جدید',
    users: 'کاربر',
    roles: 'نقش',
    saveFailed: 'تغییرات دسترسی ذخیره نشد.',
    removeConfirm: '{{name}} از این سازمان حذف شود؟',
    userRegister: 'فهرست کاربران',
    userRegisterSubtitle: 'حساب‌های دارای دسترسی به این سازمان',
    noUsers: 'کاربری وجود ندارد',
    noUsersHint: 'اولین کاربر را ایجاد و نقش او را تعیین کنید.',
    user: 'کاربر',
    assignedRoles: 'نقش‌های تخصیص‌یافته',
    unknownRole: 'نقش ناشناخته',
    noRole: 'بدون نقش',
    roleRegister: 'فهرست نقش‌ها',
    roleRegisterSubtitle: 'سطوح دسترسی قابل استفاده در این سازمان',
    systemRole: 'سیستمی',
    permissionCount: '{{count}} مجوز',
    protected: 'نقش محافظت‌شده',
    editRole: 'ویرایش نقش',
    editUserTitle: 'ویرایش دسترسی کاربر',
    editUserSubtitle: 'نقش‌های این کاربر را در سازمان تغییر دهید.',
    createUserTitle: 'ایجاد کاربر سازمان',
    createUserSubtitle: 'این حساب فقط به سازمان انتخاب‌شده دسترسی می‌گیرد.',
    displayName: 'نام نمایشی',
    passwordHint: 'حداقل ۱۲ نویسه وارد کنید.',
    permissions: 'مجوز',
    createRoleTitle: 'ایجاد سطح دسترسی',
    editRoleTitle: 'ویرایش سطح دسترسی',
    roleFormSubtitle: 'عملیات مجاز این نقش را دقیقاً انتخاب کنید.',
    roleName: 'نام نقش',
    roleKey: 'کلید نقش',
  },
  dashboard: {
    generatedAt: 'به‌روزرسانی در {{date}}',
    title: 'داشبورد مالی',
    subtitle: 'مانده‌های زنده دفتر کل، گردش کار و آخرین فعالیت اسناد این سازمان.',
    databaseBacked: 'داده زنده پایگاه داده',
    summary: 'خلاصه مالی',
    debitMovement: 'گردش بدهکار',
    creditMovement: 'گردش بستانکار',
    journalCount: 'تعداد اسناد',
    accountCount: 'تعداد حساب‌ها',
    fromLedger: 'برگرفته از دفتر کل',
    availableReports: 'گزارش‌های در دسترس',
    availableReportsHint: 'گزارش‌های استاندارد مجاز برای نقش شما، آماده اجرا.',
    monthlyActivity: 'فعالیت ماهانه دفتر کل',
    monthlyActivitySubtitle: 'گردش بدهکار و بستانکار اسناد ثبت‌شده به تفکیک ماه',
    workflow: 'گردش کار اسناد',
    workflowSubtitle: 'تعداد اسناد در هر وضعیت',
    balanceConfirmed: 'تراز آزمایشی متوازن است',
    balanceMismatch: 'تراز آزمایشی نیازمند بررسی است',
    closingTotals: 'مانده بدهکار {{debit}} · مانده بستانکار {{credit}}',
    approvals: 'تأییدهای در انتظار',
    noApprovals: 'سندی در انتظار تأیید نیست',
    noApprovalsHint: 'اسناد ارسال‌شده و تأییدشده در این بخش نمایش داده می‌شوند.',
    recentActivity: 'فعالیت‌های اخیر',
    noActivity: 'هنوز فعالیتی ثبت نشده است',
    noActivityHint: 'با ایجاد نخستین سند، جریان فعالیت سازمان آغاز می‌شود.',
  },
  journals: {
    title: 'اسناد روزنامه',
    subtitle: 'ایجاد، بازبینی، تأیید، ثبت و برگشت اسناد دوطرفه.',
    newJournal: 'سند جدید',
    editJournal: 'ویرایش پیش‌نویس',
    searchPlaceholder: 'جست‌وجوی شماره، شرح یا مدرک سند',
    allStates: 'همه وضعیت‌ها',
    allTypes: 'همه انواع',
    state: 'وضعیت',
    type: 'نوع سند',
    sort: 'مرتب‌سازی',
    from: 'از تاریخ',
    to: 'تا تاریخ',
    clearFilters: 'حذف فیلترها',
    journalNumber: 'شماره سند',
    postingDate: 'تاریخ ثبت',
    totalDebit: 'جمع بدهکار',
    totalCredit: 'جمع بستانکار',
    lineCount: 'ردیف‌ها',
    unbalanced: 'ناتراز',
    resultCount_one: '{{count}} سند',
    resultCount_other: '{{count}} سند',
    noResultsHint: 'سندی با این فیلترها یافت نشد. بازه تاریخ را گسترده‌تر کنید یا فیلترها را حذف کنید.',
    emptyRegisterHint: 'هنوز سندی ثبت نشده است. نخستین سند را بسازید تا دفتر روزنامه آغاز شود.',
    createTitle: 'ایجاد سند روزنامه',
    createSubtitle: 'سند تراز بسازید؛ همه تصمیم‌های ثبت در سرور اعتبارسنجی می‌شوند.',
    editTitle: 'ویرایش سند',
    editSubtitle: 'پیش‌نویس را پیش از ارسال برای تأیید اصلاح کنید.',
    onlyDraftEditable: 'تنها سند پیش‌نویس قابل ویرایش است. سند ثبت‌شده را باید برگشت زد.',
    numberIssuedByServer: 'شماره سند هنگام ذخیره، از سری شماره‌گذاری همین سازمان صادر می‌شود.',
    journalDetails: 'مشخصات سند',
    descriptionPlaceholder: 'هدف تجاری سند را شرح دهید',
    descriptionTooShort: 'شرح سند دست‌کم چهار نویسه لازم دارد.',
    lines: 'آرتیکل‌های سند',
    linesHint: 'هر ردیف تنها یک طرف دارد: بدهکار یا بستانکار، نه هر دو.',
    linesDetailHint: 'آرتیکل‌هایی که با ثبت این سند وارد دفتر کل می‌شوند.',
    line: 'ردیف',
    account: 'حساب',
    debit: 'بدهکار',
    credit: 'بستانکار',
    functionalDebit: 'بدهکار به ارز عملیاتی',
    functionalCredit: 'بستانکار به ارز عملیاتی',
    addLine: 'افزودن ردیف',
    removeLine: 'حذف ردیف {{number}}',
    balanceLine: 'تکمیل این ردیف با مانده اختلاف',
    balanceLineOn: 'تراز کردن سند روی ردیف {{number}}',
    totals: 'جمع‌ها',
    difference: 'اختلاف',
    balancedHint: 'بدهکار و بستانکار برابرند. سرور پیش از ثبت دوباره آن را تأیید می‌کند.',
    unbalancedHint: 'تا زمانی که بدهکار و بستانکار برابر نشوند سند ذخیره نمی‌شود.',
    serverAuthority: 'ورزا محاسبات مرورگر را مبنای قطعی حسابداری قرار نمی‌دهد.',
    saveAndSubmit: 'ذخیره و ارسال',
    memo: 'شرح ردیف',
    submit: 'ارسال برای تأیید',
    approve: 'تأیید',
    reject: 'برگشت به پیش‌نویس',
    post: 'ثبت قطعی',
    reverse: 'برگشت سند',
    discard: 'حذف پیش‌نویس',
    validate: 'بررسی',
    validationPassed: 'سرور این سند را به همین شکل می‌پذیرد.',
    rejectHint: 'تنظیم‌کننده سند این دلیل را روی پیش‌نویس بازگشتی می‌بیند.',
    reverseHint: 'سند برگشتی یک سند ثبت‌شده تازه است؛ سند اصلی دست‌نخورده باقی می‌ماند.',
    reversalDate: 'تاریخ ثبت سند برگشتی',
    reason: 'دلیل',
    reasonPlaceholder: 'تصمیم را در یک یا دو جمله توضیح دهید',
    discardHint: 'سند {{number}} از دفتر روزنامه حذف می‌شود.',
    discardIrreversible: 'پیش‌نویس هرگز وارد دفتر کل نشده است، پس حذف آن اثری باقی نمی‌گذارد. سند ثبت‌شده را باید برگشت زد.',
    returnedForCorrection: 'بازگشت برای اصلاح',
    reversedBy: 'این سند به‌طور کامل برگشت خورده است.',
    openReversal: 'مشاهده سند برگشتی',
    reversalOf: 'این سند، برگشت یک ثبت پیشین است.',
    openOriginal: 'مشاهده سند اصلی',
    sourceModule: 'ماژول مبدأ',
    sourceDocument: 'مدرک مبدأ',
    functionalCurrency: 'ارز عملیاتی',
    exchangeRate: 'نرخ ارز',
    postingRule: 'نسخه قواعد ثبت',
    auditTrail: 'ردّ حسابرسی',
    auditTrailHint: 'چه کسی و چه زمانی این سند را در چرخه پیش برده است.',
    created: 'ایجاد',
    submitted: 'ارسال',
    rejected: 'بازگشت',
    approved: 'تأیید',
    posted: 'ثبت قطعی',
    notYet: 'هنوز انجام نشده',
    stateLabel: {
      Draft: 'پیش‌نویس',
      AiPendingApproval: 'ویرایش‌شده توسط ربات ورزا · نیازمند تأیید',
      Submitted: 'ارسال‌شده',
      Approved: 'تأییدشده',
      Posted: 'ثبت‌شده',
      Reversed: 'برگشت‌خورده',
    },
    typeLabel: {
      Standard: 'عادی',
      Adjustment: 'اصلاحی',
      Opening: 'افتتاحیه',
      Closing: 'اختتامیه',
      Reversal: 'برگشتی',
    },
    sortLabel: {
      PostingDateDescending: 'جدیدترین',
      PostingDateAscending: 'قدیمی‌ترین',
      NumberDescending: 'شماره، نزولی',
      NumberAscending: 'شماره، صعودی',
      AmountDescending: 'بیشترین مبلغ',
      AmountAscending: 'کمترین مبلغ',
    },
    lineProblem: {
      accountRequired: 'یک حساب انتخاب کنید.',
      personRequired: 'این حساب تفصیلی اشخاص دارد، پس ردیف باید طرف حساب را مشخص کند.',
      amountInvalid: 'این مبلغ عدد معتبری نیست.',
      amountNegative: 'مبلغ منفی نمی‌شود؛ به‌جای آن از طرف دیگر استفاده کنید.',
      oneSideRequired: 'مبلغ بدهکار یا بستانکار را وارد کنید.',
      bothSides: 'هر ردیف تنها یک طرف دارد، نه هر دو.',
    },
  },
  ledger: {
    title: 'دفتر کل',
    subtitle: 'بررسی اثر اسناد ثبت‌شده بر هر حساب و اثبات تراز بودن دفاتر.',
    accountLedger: 'دفتر حساب',
    trialBalance: 'تراز آزمایشی',
    account: 'حساب',
    selectAccount: 'یک حساب انتخاب کنید',
    includeSubAccounts: 'به‌همراه حساب‌های زیرمجموعه',
    includingSubAccounts: 'به‌همراه حساب‌های زیرمجموعه',
    openingBalance: 'مانده ابتدای دوره',
    closingBalance: 'مانده پایان دوره',
    runningBalance: 'مانده',
    broughtForward: 'نقل از قبل',
    carriedForward: 'نقل به بعد',
    opening: 'مانده ابتدای دوره',
    movement: 'گردش دوره',
    closing: 'مانده پایان دوره',
    balanced: 'تراز است',
    outOfBalance: 'ناتراز',
    noEntries: 'آرتیکل ثبت‌شده‌ای وجود ندارد',
    noEntriesHint: 'تنها اسناد ثبت‌شده به دفتر کل می‌رسند؛ پیش‌نویس و سند ارسال‌شده در اینجا محاسبه نمی‌شوند.',
    ledgerNote: 'حساب کل، گردش کل زیرمجموعه خود را گزارش می‌کند، چون آرتیکل مستقیمی ندارد.',
    trialBalanceNote: 'مانده ابتدا، گردش و مانده پایان، هرکدام در سمت طبیعی خود گزارش می‌شوند.',
    trialBalanceHint: 'همه حساب‌هایی که در بازه انتخابی مانده یا گردش دارند.',
  },
  accounts: {
    title: 'کدینگ حساب‌ها',
    subtitle: 'ساختار درختی مستقل سازمان با کنترل ثبت و تاریخ اعتبار.',
    searchPlaceholder: 'جست‌وجوی کد یا نام حساب',
    class: {
      Asset: 'دارایی‌ها',
      Liability: 'بدهی‌ها',
      Equity: 'حقوق مالکانه',
      Revenue: 'درآمدها',
      Expense: 'هزینه‌ها',
    },
    accountCode: 'کد حساب',
    accountName: 'نام حساب',
    accountType: 'طبقه حساب',
    normalBalance: 'ماهیت حساب',
    debit: 'بدهکار',
    credit: 'بستانکار',
    parentAccount: 'حساب بالادست',
    noParent: 'سطح اول',
    balance: 'مانده خود حساب',
    rollupBalance: 'با احتساب زیرحساب‌ها',
    effectiveFrom: 'اعتبار از',
    effectiveTo: 'اعتبار تا',
    postedActivity: 'گردش ثبت‌شده',
    postingAllowed: 'ثبت مستقیم',
    enabled: 'دارد',
    disabled: 'ندارد',
    accountDetail: 'جزئیات حساب',
    selectAccount: 'برای مشاهده کنترل‌ها یک حساب را انتخاب کنید.',
    newAccount: 'حساب جدید',
    editAccount: 'ویرایش حساب',
    deleteAccount: 'حذف حساب',
    deleteConfirm: 'این حساب هیچ ثبت و زیرحسابی ندارد و می‌توان آن را از کدینگ حذف کرد. این عمل برگشت‌پذیر نیست.',
    addChild: 'افزودن زیرحساب',
    activate: 'فعال‌سازی',
    deactivate: 'غیرفعال‌سازی',
    showInactive: 'نمایش غیرفعال‌ها',
    expand: 'باز کردن',
    collapse: 'بستن',
    dialogHint: 'همه قواعد حسابداری در سرور اعمال می‌شود؛ این فرم فقط مشخصات حساب را اعلام می‌کند.',
    codeLocked: 'کد حساب تغییر نمی‌کند؛ اسناد و گزارش‌های ثبت‌شده با همین کد باید با کدینگ هم‌خوان بمانند.',
    postedActivityLocked: 'پس از ثبت گردش، طبقه و ماهیت حساب قابل تغییر نیست.',
    parentNotPostable: 'حساب بالادست جمع زیرحساب‌ها را نگه می‌دارد و خودش ردیف سند نمی‌پذیرد.',
    leafHint: 'حساب قابل ثبت مستقیماً ردیف سند می‌پذیرد و فقط مانده خودش را گزارش می‌کند.',
    requiresPerson: 'تفصیلی‌پذیر (اشخاص)',
    requiresPersonHint: 'هر ردیف ثبت‌شده در این حساب باید شخص مربوط را مشخص کند تا مانده حساب به تفکیک اشخاص قابل تحلیل بماند.',
    requiresPersonLocked: 'پس از ثبت گردش، تفصیلی‌پذیری حساب قابل تغییر نیست.',
  },
  persons: {
    title: 'اشخاص',
    subtitle: 'طرف‌حساب‌هایی که دفتر بر مبنای آن‌ها تفکیک می‌شود؛ هر شخص دفتر تفصیلی خودش را دارد.',
    searchPlaceholder: 'جست‌وجوی کد، نام یا شناسه',
    kind: 'نوع شخص',
    kindLabel: {
      Real: 'حقیقی',
      Legal: 'حقوقی',
    },
    allKinds: 'همه انواع',
    personCode: 'کد شخص',
    name: 'نام',
    tradeName: 'نام تجاری',
    nationalId: 'کد ملی',
    nationalNumber: 'شناسه ملی',
    nationalIdLength: 'کد ملی ۱۰ رقم است.',
    nationalNumberLength: 'شناسه ملی ۱۱ رقم است.',
    identifierChecksum: 'رقم کنترلی با بقیه شماره هم‌خوان نیست.',
    economicCode: 'کد اقتصادی',
    economicCodeLength: 'کد اقتصادی ۱۲ یا ۱۴ رقم است.',
    registrationNumber: 'شماره ثبت',
    phone: 'تلفن',
    email: 'رایانامه',
    address: 'نشانی',
    notes: 'توضیحات',
    postedActivity: 'گردش ثبت‌شده',
    owesUs: 'بدهکار به ما',
    weOwe: 'بستانکار از ما',
    newPerson: 'شخص جدید',
    editPerson: 'ویرایش شخص',
    deletePerson: 'حذف شخص',
    deleteConfirm: 'این شخص هیچ ثبتی ندارد و می‌توان رکورد آن را حذف کرد. این عمل برگشت‌پذیر نیست.',
    activate: 'فعال‌سازی',
    deactivate: 'غیرفعال‌سازی',
    showInactive: 'نمایش غیرفعال‌ها',
    selectPerson: 'برای مشاهده مشخصات و دفتر، یک شخص را انتخاب کنید.',
    dialogHint: 'همه قواعد حسابداری در سرور اعمال می‌شود؛ این فرم فقط مشخصات طرف‌حساب را اعلام می‌کند.',
    codeLocked: 'کد شخص تغییر نمی‌کند؛ دفتر و اسناد ثبت‌شده با همین کد باید با فهرست اشخاص هم‌خوان بمانند.',
    postedActivityLocked: 'پس از ثبت گردش، نوع شخص قابل تغییر نیست.',
    postedHint: 'این شخص گردش ثبت‌شده دارد؛ می‌توان آن را غیرفعال کرد اما حذف نه.',
    noPostingsHint: 'هنوز هیچ ثبتی به نام این شخص نیست. برای بازشدن دفتر او، در ردیف سندِ یک حساب تفصیلی‌پذیر نامش را انتخاب کنید.',
    ledgerTitle: 'دفتر تفصیلی شخص',
    ledgerSubtitle: 'همه ثبت‌های قطعی‌شده به نام این شخص، از قدیم به جدید.',
    from: 'از تاریخ',
    to: 'تا تاریخ',
    openingBalance: 'مانده ابتدای دوره',
    totalDebit: 'جمع بدهکار',
    totalCredit: 'جمع بستانکار',
    closingBalance: 'مانده پایان دوره',
    runningBalance: 'مانده',
    document: 'سند',
    account: 'حساب',
    noEntries: 'در این بازه ثبتی وجود ندارد',
    noEntriesHint: 'بازه تاریخ را بازتر کنید یا ردیف سندی به نام این شخص ثبت کنید.',
    source: {
      GeneralLedger: 'سند دستی',
      Receivables: 'دریافتنی‌ها',
      Payables: 'پرداختنی‌ها',
      Treasury: 'نقد و بانک',
      Sales: 'فروش',
      Purchasing: 'خرید',
    },
    none: 'بدون شخص',
    required: 'انتخاب شخص الزامی است',
  },
  exchange: {
    title: 'نرخ‌های ارز',
    subtitle: 'نرخ‌های جهت‌دار و تاریخ‌دار همراه با تأیید و سابقه مصرف.',
    newRate: 'نرخ جدید',
    fromCurrency: 'ارز مبدأ',
    toCurrency: 'ارز مقصد',
    rate: 'نرخ',
    rateType: 'نوع نرخ',
    source: 'منبع',
    validFrom: 'معتبر از',
    approvedBy: 'تأییدکننده',
    direction: 'جهت',
    directPreview: 'نمایش مستقیم',
    inversePreview: 'نمایش معکوس',
    usage: 'کاربرد در اسناد',
    history: 'تاریخچه نرخ',
    rateEditor: 'ویرایشگر نرخ جهت‌دار',
    editorHint: 'جهت نرخ صریح است و از جای فیلدها برداشت نمی‌شود.',
    submitApproval: 'ارسال برای تأیید',
    resultCount: '{{count}} نرخ ذخیره‌شده',
    noRates: 'نرخ ارزی ثبت نشده است',
    noRatesHint: 'نخستین نرخ جهت‌دار این سازمان را ایجاد کنید.',
    createTitle: 'ایجاد نرخ ارز',
    rateDetails: 'جزئیات نرخ',
    spot: 'لحظه‌ای',
    closing: 'پایانی',
    actionFailed: 'عملیات نرخ ارز انجام نشد.',
    invalidRate: 'دو ارز متفاوت انتخاب کنید و نرخی بزرگ‌تر از صفر وارد کنید.',
    fixedRialTomanRate: 'نسبت ریال و تومان همیشه ۱۰ به ۱ است؛ فقط نرخ دلار را ثبت کنید.',
    sourceType: {
      Manual: 'دستی',
      Provider: 'ارائه‌دهنده',
      Contract: 'قرارداد',
      Bank: 'بانک',
      Import: 'ورودی فایل',
    },
    noRecalculation: 'اسناد ثبت‌شده شواهد نرخ اصلی خود را حفظ می‌کنند.',
  },
  inventory: {
    title: 'انبار',
    subtitle: 'موجودی قابل‌فروش، ارزش‌گذاری، گردش، انتقال و شمارش لحظه‌ای.',
    stock: 'موجودی',
    products: 'کالاها',
    warehouses: 'انبارها',
    lots: 'بهرها',
    reservations: 'رزروها',
    transfers: 'انتقال‌ها',
    counts: 'شمارش‌ها',
    movements: 'گردش‌ها',
    productSearch: 'جست‌وجوی کالا، SKU یا بارکد',
    allWarehouses: 'همه انبارها',
    lowStockOnly: 'فقط موجودی کم',
    onHand: 'موجودی فیزیکی',
    available: 'قابل‌فروش',
    reserved: 'رزروشده',
    incoming: 'در راه',
    valuation: 'ارزش موجودی',
    activeSkus: 'کالاهای فعال',
    negativeStock: 'موجودی منفی',
    provisionalCost: 'بهای موقت',
    product: 'کالا',
    sku: 'SKU',
    warehouse: 'انبار',
    stockStatus: 'وضعیت موجودی',
    healthy: 'مناسب',
    low: 'کم',
    unitCost: 'بهای واحد',
    unitCostHint: 'به {{currency}} وارد می‌شود؛ همان ارز عملیاتی که این موجودی با آن ارزش‌گذاری شده است.',
    quantity: 'مقدار',
    direction: 'جهت',
    reason: 'دلیل',
    manualEntry: 'ثبت دستی',
    adjustmentDefaultReason: 'تعدیل دستی موجودی',
    noStock: 'هنوز موجودی‌ای در این فضای کاری رسید نشده است.',
    noLots: 'برای کالاهای بهره‌ای هنوز بهری ثبت نشده است.',
    noMovements: 'هنوز گردش انباری ثبت نشده است.',
    noReservations: 'در حال حاضر موجودی رزروشده‌ای وجود ندارد.',
    noTransfers: 'هنوز انتقال بین انباری ثبت نشده است.',
    noCounts: 'هنوز شمارش انبار آغاز نشده است.',
    receive: 'رسید',
    issue: 'حواله',
    adjust: 'تعدیل',
    release: 'آزادسازی',
    quarantineAction: 'قرنطینه',
    recall: 'فراخوان',
    recallReference: 'فراخوان ثبت‌شده از فضای کاری انبار',
    recordReceipt: 'ثبت رسید',
    recordIssue: 'ثبت حواله',
    recordAdjustment: 'ثبت تعدیل',
    movementHint: 'مقدارها با واحد پایه کالا وارد می‌شوند و بهای حواله از لایه‌های ارزش‌گذاری برداشت می‌شود.',
    adjustmentGain: 'اضافه — کالای یافت‌شده',
    adjustmentLoss: 'کسری — کالای حذف‌شده',
    newProduct: 'کالای جدید',
    editProduct: 'ویرایش کالا',
    productHint: 'SKU در هر مستأجر یکتاست و پس از ایجاد کالا تغییر نمی‌کند.',
    productLockedHint: 'این کالا سابقه گردش دارد؛ واحد پایه و روش ارزش‌گذاری آن قفل است.',
    productName: 'نام',
    baseUnit: 'واحد پایه',
    productType: 'نوع کالا',
    trackingPolicy: 'سیاست ردیابی',
    valuationMethod: 'روش ارزش‌گذاری',
    valuationLocked: 'به دلیل سابقه گردش قفل شده است.',
    barcode: 'بارکد',
    category: 'گروه کالا',
    reorderPoint: 'نقطه سفارش',
    safetyStock: 'موجودی اطمینان',
    effectiveFrom: 'اعتبار از',
    newWarehouse: 'انبار جدید',
    editWarehouse: 'ویرایش انبار',
    warehouseHint: 'کد انبار روی همه گردش‌ها ثبت می‌شود و بعداً قابل تغییر نیست.',
    warehouseName: 'نام',
    code: 'کد',
    branch: 'شعبه',
    address: 'نشانی',
    negativeStockAllowed: 'اجازه موجودی منفی',
    negativeStockHint: 'در صورت اجازه، حواله بیش از موجودی با بهای موقت نگهداری می‌شود تا رسید بعدی آن را اصلاح کند.',
    locations: 'مکان‌ها',
    newLocation: 'مکان جدید',
    locationHint: 'یک مکان قرنطینه نمی‌تواند هم‌زمان قابل برداشت باشد.',
    locationName: 'نام',
    location: 'مکان',
    zone: 'ناحیه',
    pickable: 'قابل برداشت',
    receivable: 'قابل رسید',
    quarantine: 'قرنطینه',
    inTransit: 'در راه',
    locationFlags: 'ویژگی‌ها',
    newLot: 'بهر جدید',
    lotHint: 'شماره بهر برای هر کالا یکتاست و تخصیص بر پایه نزدیک‌ترین انقضا را هدایت می‌کند.',
    lot: 'بهر',
    lotNumber: 'شماره بهر',
    supplierLot: 'بهر تأمین‌کننده',
    manufacturedOn: 'تاریخ تولید',
    expiresOn: 'تاریخ انقضا',
    expiryRequired: 'این کالا انقضا را ردیابی می‌کند، پس تاریخ الزامی است.',
    lotRequiredHint: 'این کالا بهره‌ای است، پس هر گردش باید بهر را مشخص کند.',
    noTrackedProducts: 'هنوز کالای بهره‌ای وجود ندارد. ابتدا سیاست ردیابی یک کالا را روی بهر بگذارید.',
    movementType: 'نوع گردش',
    sourceDocument: 'سند مبدأ',
    requiredDate: 'تاریخ نیاز',
    requested: 'درخواست‌شده',
    allocated: 'تخصیص‌یافته',
    consumed: 'مصرف‌شده',
    newTransfer: 'انتقال جدید',
    transferHint: 'با حواله مبدأ، کالا تا زمان رسید مقصد در راه می‌ماند.',
    documentNumber: 'شماره سند',
    sourceWarehouse: 'از انبار',
    destinationWarehouse: 'به انبار',
    addLine: 'افزودن ردیف',
    lines: 'ردیف‌ها',
    route: 'مسیر',
    inTransitQuantity: 'در راه',
    issueTransfer: 'حواله',
    receiveTransfer: 'رسید',
    receiveTransferHint: 'حداکثر تا مقدار در راه هر ردیف قابل رسید است.',
    newCount: 'شمارش جدید',
    countHint: 'مقدار سیستمی هنگام ساخت برگه شمارش تثبیت می‌شود.',
    countScope: 'کالاهای مشمول شمارش',
    blindCount: 'شمارش کور',
    blindCountHint: 'این شمارش کور است، پس مقدار سیستمی از شمارنده پنهان می‌ماند.',
    blindCountShort: 'کور',
    countSheet: 'برگه شمارش',
    countSheetHint: 'برای هر ردیف مقدار مشاهده‌شده روی قفسه را وارد کنید.',
    systemQuantity: 'سیستمی',
    variance: 'مغایرت',
    enterCount: 'ثبت شمارش',
    submit: 'ارسال',
    approve: 'تأیید',
    recount: 'شمارش مجدد',
    post: 'ثبت قطعی',
    productType_StockItem: 'کالای انبارشدنی',
    productType_NonStockItem: 'کالای غیرانبارشدنی',
    productType_Service: 'خدمت',
    productType_Bundle: 'بسته',
    tracking_None: 'بدون ردیابی',
    tracking_Lot: 'بهر',
    tracking_Serial: 'سریال',
    tracking_LotAndExpiry: 'بهر و انقضا',
    valuation_MovingAverage: 'میانگین متحرک',
    valuation_Fifo: 'اولین صادره از اولین وارده',
    lotStatus_Quarantined: 'قرنطینه',
    lotStatus_Released: 'آزادشده',
    lotStatus_Blocked: 'مسدود',
    lotStatus_Recalled: 'فراخوان‌شده',
    movement_Opening: 'افتتاحیه',
    movement_PurchaseReceipt: 'رسید خرید',
    movement_SalesIssue: 'حواله فروش',
    movement_CustomerReturn: 'برگشت از مشتری',
    movement_SupplierReturn: 'برگشت به تأمین‌کننده',
    movement_TransferIssue: 'حواله انتقال',
    movement_TransferReceipt: 'رسید انتقال',
    movement_AdjustmentGain: 'تعدیل اضافه',
    movement_AdjustmentLoss: 'تعدیل کسری',
    movement_CountVariance: 'مغایرت شمارش',
    movement_StatusChange: 'تغییر وضعیت',
    movement_CostAdjustment: 'تعدیل بها',
    serials: 'سریال‌ها',
    units: 'واحدها',
    reconciliation: 'مغایرت‌گیری',
    newSerial: 'سریال جدید',
    serialNumber: 'شماره سریال',
    serialHint: 'هر سریال یک واحد است، پس دفتر سریال تعیین می‌کند که رسید یا حواله ممکن است یا نه.',
    noSerials: 'هنوز سریالی ثبت نشده است.',
    noSerialisedProducts: 'هنوز کالای سریالی وجود ندارد. ابتدا سیاست ردیابی یک کالا را روی سریال بگذارید.',
    scrap: 'اسقاط',
    newUnit: 'واحد جدید',
    unitName: 'نام',
    unitHint: 'بُعد واحد مانع تبدیل جرم به تعداد می‌شود.',
    dimension: 'بُعد',
    dimensionHint: 'تبدیل فقط میان واحدهای هم‌بُعد تعریف می‌شود.',
    newConversion: 'تبدیل جدید',
    conversionHint: 'ضریب‌ها اعشاری دقیق‌اند و در هر دو جهت اعمال می‌شوند.',
    fromUnit: 'از واحد',
    toUnit: 'به واحد',
    factor: 'ضریب',
    factorHint: 'هر {{from}} برابر این مقدار {{to}} است.',
    scope: 'دامنه',
    scopeHint: 'تبدیل ویژه کالا بر تبدیل سراسری اولویت دارد.',
    allProducts: 'همه کالاها',
    entered: 'ورودی',
    newReservation: 'رزرو جدید',
    reservationHint: 'هنگام سنجش درخواست، موجودی قفل می‌شود تا دو درخواست یک موجودی را برندارند.',
    allocationStrategy: 'راهبرد تخصیص',
    allowPartial: 'پذیرش تخصیص جزئی',
    untracked: 'بدون ردیابی',
    strategy_Fifo: 'اولین وارده — قدیمی‌ترین رسید',
    strategy_Fefo: 'نزدیک‌ترین انقضا',
    strategy_SpecificLot: 'بهر مشخص',
    strategyHint_Fifo: 'از بهری که زودتر رسیده برداشت می‌کند.',
    strategyHint_Fefo: 'از بهری که زودتر منقضی می‌شود برداشت می‌کند؛ مناسب کالای فاسدشدنی.',
    strategyHint_SpecificLot: 'فقط از بهر انتخابی برداشت می‌کند و به بهر دیگر سرریز نمی‌شود.',
    controlAccount: 'حساب کنترل انبار',
    subledgerValue: 'ارزش دفتر انبار',
    noReconciliation: 'هنوز موجودی‌ای به حساب انبار نگاشت نشده است. ابتدا نگاشت حساب پیش‌فرض را تعریف کنید.',
    unmappedProducts: '{{count}} کالا موجودی دارد اما به هیچ حساب انباری نگاشت نشده است، پس مغایرت‌گیری کامل نیست.',
    dimension_Count: 'تعداد',
    dimension_Mass: 'جرم',
    dimension_Volume: 'حجم',
    dimension_Length: 'طول',
    dimension_Area: 'مساحت',
    dimension_Time: 'زمان',
  },
  reports: {
    title: 'گزارش‌ها',
    centerSubtitle: 'گزارش‌های استاندارد مجاز، جمع‌های قابل بازتولید و نماهای تحلیلی را در یک مرکز اجرا و بررسی کنید.',
    standardReports: 'گزارش‌های استاندارد',
    standardReportsHint: 'گزارش‌های حسابداری متصل به پایگاه داده که برای نقش فعلی شما مجاز هستند.',
    standardReportsUnavailable: 'فهرست گزارش‌ها بارگذاری نشد. مهاجرت گزارش‌گیری و دسترسی‌های نقش را بررسی کنید.',
    noAuthorizedReports: 'گزارش استانداردی در دسترس نیست',
    noAuthorizedReportsHint: 'از مدیر بخواهید دسترسی ماژول گزارش و گزارش‌های موردنیاز را به نقش شما بدهد.',
    savedAnalyticalReports: 'گزارش‌های تحلیلی ذخیره‌شده',
    readOnly: 'فقط خواندنی',
    runReport: 'اجرای گزارش',
    running: 'در حال اجرا…',
    readyToRun: 'آماده اجرا',
    readyToRunHint: 'فیلترها را انتخاب کنید و گزارش را روی داده‌های حسابداری ثبت‌شده اجرا کنید.',
    requiredFilter: '{{filter}} الزامی است.',
    from: 'از',
    to: 'تا',
    functionalCurrency: 'ارز عملیاتی',
    fiscalYearNumber: 'سال مالی {{number}}',
    multiSelectHint: 'برای انتخاب چند مقدار، Ctrl یا Command را نگه دارید.',
    defaultPosted: 'برای استفاده از پیش‌فرض گزارش خالی بگذارید؛ در صورت کاربرد فقط ثبت‌شده‌ها.',
    commaSeparated: 'مقادیر جداشده با ویرگول',
    reproducibleResult: 'نتیجه حسابرسی‌شده و قابل بازتولید',
    rowsCount: '{{count}} ردیف',
    categories: {
      Accounting: 'حسابداری',
      Audit: 'حسابرسی و کنترل',
    },
    standard: {
      'chart-of-accounts': {
        name: 'کدینگ حساب‌ها',
        description: 'ساختار حساب‌ها، طبقه‌بندی، قواعد ثبت و تاریخ‌های اعتبار.',
      },
      'general-ledger': {
        name: 'دفتر کل',
        description: 'آرتیکل‌های ثبت‌شده همراه با مانده جاری و ارجاع سند مبنا.',
      },
      'trial-balance': {
        name: 'تراز آزمایشی',
        description: 'بدهکار و بستانکار اول دوره، گردش دوره و پایان دوره به تفکیک حساب.',
      },
      'journal-register': {
        name: 'دفتر ثبت اسناد',
        description: 'وضعیت سند، جمع آرتیکل‌ها، ماژول مبنا و ارجاع مدارک.',
      },
    },
    parameters: {
      'date-range': 'بازه تاریخ',
      'fiscal-year': 'سال مالی',
      'accounting-period': 'دوره مالی',
      accounts: 'حساب‌ها',
      'account-range': 'بازه کد حساب',
      persons: 'اشخاص',
      currency: 'ارز',
      'posting-status': 'وضعیت ثبت',
      'journal-types': 'انواع سند',
      'source-modules': 'ماژول‌های مبنا',
      search: 'جست‌وجو',
    },
    columns: {
      'account-code': 'کد حساب',
      'account-name': 'نام حساب',
      'parent-account-code': 'حساب والد',
      'account-class': 'طبقه حساب',
      'normal-balance': 'ماهیت حساب',
      'is-active': 'فعال',
      'posting-allowed': 'مجاز برای ثبت',
      'requires-person': 'شخص الزامی',
      'effective-from': 'معتبر از',
      'effective-to': 'معتبر تا',
      'posting-date': 'تاریخ ثبت',
      'journal-number': 'شماره سند',
      'source-document-number': 'مدرک مبنا',
      'person-name': 'شخص',
      description: 'شرح',
      debit: 'بدهکار',
      credit: 'بستانکار',
      'running-balance': 'مانده جاری',
      'opening-debit': 'بدهکار اول دوره',
      'opening-credit': 'بستانکار اول دوره',
      'period-debit': 'گردش بدهکار',
      'period-credit': 'گردش بستانکار',
      'closing-debit': 'بدهکار پایان دوره',
      'closing-credit': 'بستانکار پایان دوره',
      'journal-type': 'نوع سند',
      'journal-state': 'وضعیت سند',
      'line-count': 'تعداد آرتیکل',
      'source-module': 'ماژول مبنا',
    },
    builderSubtitle: 'نمودارهای قابل‌استفاده مجدد را از اسناد ثبت‌شده بسازید و نتیجه جاری را خروجی بگیرید.',
    exportCsv: 'خروجی CSV',
    savedReports: 'گزارش‌های ذخیره‌شده',
    noSavedReports: 'گزارشی ذخیره نشده است',
    noSavedReportsHint: 'نخستین نمای تحلیلی را در بخش زیر بسازید.',
    createReport: 'ساخت گزارش',
    reportName: 'نام گزارش',
    dimensionLabel: 'گروه‌بندی بر اساس',
    measureLabel: 'شاخص',
    chartTypeLabel: 'نوع نمودار',
    dimension: {
      Month: 'ماه',
      State: 'وضعیت سند',
      JournalType: 'نوع سند',
    },
    measure: {
      Debit: 'بدهکار',
      Credit: 'بستانکار',
      JournalCount: 'تعداد اسناد',
    },
    chartType: {
      Bar: 'نمودار میله‌ای',
      Line: 'نمودار خطی',
      Pie: 'نمودار دایره‌ای',
    },
    createFailed: 'گزارش ذخیره نشد.',
    nameRequired: 'نام گزارش را وارد کنید.',
    creating: 'در حال ساخت…',
    saveAndRun: 'ذخیره و اجرا',
    selectOrCreate: 'یک گزارش را انتخاب کنید یا بسازید',
    selectOrCreateHint: 'گزارش‌های ذخیره‌شده مستقیماً روی داده اسناد ثبت‌شده اجرا می‌شوند.',
    generatedAt: 'تولیدشده در {{date}}',
    noReportData: 'داده ثبت‌شده‌ای برای این گزارش نیست',
    noReportDataHint: 'اسناد را ثبت کنید یا گروه‌بندی و شاخص دیگری انتخاب کنید.',
    group: 'گروه',
  },
  modules: {
    receivablesTitle: 'حساب‌های دریافتنی',
    receivablesDescription: 'مانده مشتریان، اقلام باز، وصول و سن مطالبات.',
    payablesTitle: 'حساب‌های پرداختنی',
    payablesDescription: 'صورتحساب تأمین‌کننده، تطبیق سه‌طرفه، پرداخت و سن بدهی.',
    purchasingTitle: 'خرید',
    purchasingDescription: 'سفارش خرید، دریافت، تطبیق و برگشت به تأمین‌کننده.',
    salesTitle: 'فروش',
    salesDescription: 'پیش‌فاکتور، سفارش، تحویل، صورتحساب و برگشت مشتری.',
    availableSoon: 'زیرساخت فضای کاری آماده است',
    foundationMessage: 'راهبری، دسترسی‌ها، بومی‌سازی و مرزبندی مسیرهای این ماژول آماده شده است.',
    openItems: 'اقلام باز',
    dueSoon: 'نزدیک سررسید',
    activeDocuments: 'اسناد فعال',
    exceptionQueue: 'صف مغایرت‌ها',
  },
  docStatus: {
    Draft: 'پیش‌نویس',
    AiPendingVerification: 'ویرایش‌شده توسط ربات ورزا · نیازمند تأیید',
    Approved: 'تأییدشده',
    Posted: 'ثبت‌شده',
    Calculated: 'محاسبه‌شده',
    PartiallyPaid: 'تسویه جزئی',
    Paid: 'تسویه‌شده',
    Voided: 'ابطال‌شده',
    Reversed: 'برگشت‌خورده',
    Applied: 'تخصیص‌یافته',
    Released: 'آزادشده',
    Settled: 'تسویه‌شده',
    Submitted: 'ارسال‌شده',
    Confirmed: 'تأیید نهایی',
    Rejected: 'ردشده',
    Superseded: 'جایگزین‌شده',
    Cancelled: 'لغوشده',
    Open: 'باز',
    InReview: 'در حال بررسی',
    Closed: 'بسته',
    Active: 'فعال',
    Inactive: 'غیرفعال',
    Frozen: 'مسدود',
    Suspended: 'معلق',
    PendingVerification: 'در انتظار احراز',
    Verified: 'احرازشده',
    Sealed: 'پلمب‌شده',
    InTransit: 'در راه',
    Deposited: 'واریزشده',
    Counted: 'شمارش‌شده',
    Staged: 'بارگذاری‌شده',
    Validated: 'اعتبارسنجی‌شده',
    Accepted: 'پذیرفته‌شده',
    Duplicate: 'تکراری',
    Unmatched: 'تطبیق‌نشده',
    Matched: 'تطبیق‌شده',
    Excluded: 'کنارگذاشته‌شده',
    Receipt: 'دریافت',
    Disbursement: 'پرداخت',
    Transfer: 'انتقال',
    Adjustment: 'تعدیل',
    OutstandingDeposit: 'واریز در راه',
    OutstandingPayment: 'پرداخت وصول‌نشده',
    BankOnlyCharge: 'هزینه فقط بانکی',
    BankOnlyCredit: 'بستانکاری فقط بانکی',
    LedgerError: 'اشتباه دفتر',
    BankError: 'اشتباه بانک',
    Issued: 'حواله‌شده',
    PartiallyReceived: 'رسید جزئی',
    Received: 'رسیدشده',
    Counting: 'در حال شمارش',
    PendingApproval: 'در انتظار تأیید',
    Quarantined: 'قرنطینه',
    Blocked: 'مسدود',
    Recalled: 'فراخوان‌شده',
    FullyAllocated: 'تخصیص کامل',
    PartiallyAllocated: 'تخصیص جزئی',
    Consumed: 'مصرف‌شده',
    Expired: 'منقضی',
    Registered: 'ثبت‌شده',
    InStock: 'موجود',
    Scrapped: 'اسقاط‌شده',
  },
  subledger: {
    customers: 'مشتریان',
    suppliers: 'تأمین‌کنندگان',
    openItems: 'اقلام باز',
    receipts: 'دریافت‌ها',
    payments: 'پرداخت‌ها',
    creditNotes: 'برگه بستانکار',
    debitNotes: 'برگه بدهکار',
    bills: 'صورتحساب‌ها',
    paymentRuns: 'دسته پرداخت',
    aging: 'سن مانده',
    proposal: 'پیشنهاد پرداخت',
    code: 'کد',
    name: 'نام',
    creditLimit: 'سقف اعتبار',
    contact: 'تماس',
    documentNumber: 'سند',
    invoiceDate: 'تاریخ فاکتور',
    billDate: 'تاریخ صورتحساب',
    dueDate: 'سررسید',
    total: 'جمع',
    open: 'باز',
    applied: 'تخصیص‌یافته',
    unapplied: 'تخصیص‌نیافته',
    daysOverdue: 'روز تأخیر',
    onHold: 'متوقف',
    disputed: 'مورد اختلاف',
    matchException: 'مغایرت تطبیق',
    withholdingTax: 'مالیات تکلیفی',
    discount: 'تخفیف',
    net: 'خالص',
    exchangeRate: 'نرخ',
    realizedFx: 'تفاوت تسعیر تحقق‌یافته',
    reason: 'دلیل',
    asOf: 'در تاریخ',
    bucketCurrent: 'جاری',
    bucketOverdue: '{{range}} روز',
    controlAccount: 'حساب کنترل',
    difference: 'اختلاف',
    reconciled: 'با حساب کنترل مطابقت دارد',
    outOfBalance: 'با حساب کنترل مطابقت ندارد',
    eligible: 'واجد شرایط',
    ineligible: 'فاقد شرایط',
    noRecords: 'هنوز سندی با این نما مطابقت ندارد.',
    functionalTotal: 'جمع به ارز عملیاتی',
    prepayment: 'پیش‌پرداخت',
  },
  commerce: {
    orderNumber: 'شماره سفارش',
    customer: 'مشتری',
    supplier: 'تأمین‌کننده',
    paymentTerms: 'شرایط پرداخت',
    deliveryAddress: 'نشانی تحویل',
    unitPrice: 'بهای واحد',
    tax: 'مالیات',
    lines: 'ردیف‌ها',
    shipmentNumber: 'شماره ارسال',
    inventoryAccount: 'حساب موجودی',
    cogsAccount: 'حساب بهای تمام‌شده',
    expectedDate: 'تاریخ مورد انتظار',
    receiptNumber: 'شماره رسید',
    grniAccount: 'حساب کالای دریافت‌شده فاکتورنشده',
  },
  sales: {
    title: 'سیستم فروش',
    subtitle: 'سفارش مشتری، تأیید، رزرو موجودی و ثبت ارسال در یک دفتر عملیاتی.',
    newOrder: 'سفارش فروش جدید',
    orderHint: 'سفارش مشتری را همراه با شرایط تجاری و نخستین ردیف کالا ایجاد کنید.',
    postShipment: 'ثبت ارسال',
    shipmentHint: 'مقادیر رزروشده باقی‌مانده را حواله و بهای موجودی آن را شناسایی کنید.',
    orders: 'سفارش‌ها',
    fulfillment: 'ارسال و تحویل',
    noOrders: 'هنوز سفارش فروشی ایجاد نشده است.',
    orderValue: 'ارزش سفارش‌ها',
    openOrders: 'سفارش‌های باز',
    awaitingApproval: 'در انتظار تأیید',
    readyToShip: 'آماده ارسال',
    confirm: 'تأیید نهایی',
    reserve: 'رزرو موجودی',
    ship: 'ارسال',
    issueInvoice: 'صدور فاکتور',
  },
  purchasing: {
    title: 'سیستم خرید',
    subtitle: 'سفارش تأمین‌کننده، تأیید، رسید کالا و تعهد موجودی در یک دفتر عملیاتی.',
    newOrder: 'سفارش خرید جدید',
    orderHint: 'سفارش تأمین‌کننده را همراه با تاریخ مورد انتظار و نخستین ردیف کالا ایجاد کنید.',
    postReceipt: 'ثبت رسید کالا',
    receiptHint: 'مقادیر باقی‌مانده سفارش را به انبار رسید و بدهی تأمین‌کننده را تعهد کنید.',
    orders: 'سفارش‌ها',
    receiving: 'دریافت کالا',
    noOrders: 'هنوز سفارش خریدی ایجاد نشده است.',
    orderValue: 'ارزش سفارش‌ها',
    openOrders: 'سفارش‌های باز',
    awaitingApproval: 'در انتظار تأیید',
    readyToReceive: 'آماده دریافت',
    receive: 'دریافت',
    issueBill: 'صدور صورت‌حساب تأمین‌کننده',
  },
  automation: {
    title: 'اتوماسیون اسناد',
    subtitle: 'کنترل صدور بین‌سیستمی، تأیید، ثبت، تلاش مجدد، برگشت و ردیابی اسناد.',
    salesInvoiceHint: 'فاکتور مشتری از مقادیر ارسال‌شده صادر می‌شود و طبق سیاست برای تأیید منتظر می‌ماند.',
    supplierBillHint: 'صورت‌حساب از مقادیر پذیرفته‌شده رسید، با تطبیق سه‌طرفه و تسویه GRNI ساخته می‌شود.',
    documentNumber: 'شماره سند',
    dueDate: 'تاریخ سررسید',
    receivableAccount: 'حساب دریافتنی',
    revenueAccount: 'حساب درآمد',
    outputTaxAccount: 'حساب مالیات فروش',
    payableAccount: 'حساب پرداختنی',
    varianceAccount: 'حساب انحراف بهای خرید',
    inputTaxAccount: 'حساب مالیات خرید',
    policy: 'سیاست اتوماسیون',
    processQueue: 'صف فرایندها',
    noProcesses: 'هنوز فرایند خودکاری در صف نیست.',
    source: 'سند مبدأ',
    target: 'سند تولیدشده',
    attempts: 'تعداد تلاش',
    error: 'آخرین خطا',
    autoApproveSales: 'تأیید خودکار فاکتور فروش',
    autoPostSales: 'ثبت خودکار فاکتور فروش',
    autoApprovePurchasing: 'تأیید خودکار صورت‌حساب خرید',
    autoPostPurchasing: 'ثبت خودکار صورت‌حساب خرید',
    publishToVii: 'انتشار اسناد ثبت‌شده در VII با رعایت رضایت',
    functionalCurrency: 'ارز عملیاتی',
    maximumFailures: 'حداکثر خطاهای تلاش مجدد',
    savePolicy: 'ذخیره سیاست',
    retry: 'تلاش مجدد',
    cancel: 'لغو فرایند',
    reverse: 'برگشت',
    reverseReason: 'دلیل برگشت',
  },
  payroll: {
    title: 'حقوق و دستمزد',
    subtitle: 'قرارداد مالی کارکنان، کسورات قابل تنظیم، محاسبه حقوق، فیش‌ها، تأیید و ثبت در دفتر کل.',
    employees: 'کارکنان',
    runs: 'دوره‌های حقوق',
    payslips: 'فیش‌های حقوقی',
    newEmployee: 'کارمند جدید',
    employeeHint: 'یک شخص حقیقی را به شماره کارمندی متصل و مبالغ و نرخ‌های قرارداد او را تعریف کنید.',
    newRun: 'محاسبه حقوق جدید',
    runHint: 'دوره، حساب‌ها و تعدیلات کارکنان را انتخاب کنید؛ ورزا پیش از تأیید هر فیش را محاسبه می‌کند.',
    noEmployees: 'هنوز کارمندی در حقوق و دستمزد تعریف نشده است.',
    noRuns: 'هنوز دوره حقوقی محاسبه نشده است.',
    noPayslips: 'برای مشاهده فیش‌ها یک دوره حقوق محاسبه کنید.',
    noAvailablePersons: 'همه اشخاص حقیقی فعال به حقوق و دستمزد متصل هستند. پیش از ایجاد کارمند، شخص دیگری اضافه کنید.',
    noCurrencyEmployees: 'کارمند فعالی با این ارز وجود ندارد.',
    activeEmployees: 'کارکنان فعال',
    monthlyBase: 'حقوق و مزایای پایه ماهانه',
    pendingRuns: 'دوره‌های در انتظار ثبت',
    latestNet: 'آخرین خالص حقوق',
    employeeNumber: 'شماره کارمندی',
    employee: 'کارمند',
    hireDate: 'تاریخ استخدام',
    baseSalary: 'حقوق پایه',
    housingAllowance: 'حق مسکن',
    foodAllowance: 'بن و خواربار',
    employeeInsuranceRate: 'بیمه سهم کارمند (٪)',
    employerInsuranceRate: 'بیمه سهم کارفرما (٪)',
    incomeTaxRate: 'نرخ مالیات درآمد (٪)',
    bankAccountNumber: 'شماره حساب / شبا',
    insuranceRates: 'بیمه (کارمند / کارفرما)',
    terminate: 'پایان همکاری',
    terminateConfirm: 'همکاری این کارمند از امروز پایان یابد؟',
    calculate: 'محاسبه حقوق',
    runNumber: 'شماره دوره',
    period: 'دوره پرداخت',
    periodStart: 'آغاز دوره',
    periodEnd: 'پایان دوره',
    paymentDate: 'تاریخ پرداخت',
    taxExemption: 'معافیت مالیاتی هر کارمند',
    payrollAccounts: 'حساب‌های دفتر کل',
    salaryExpense: 'هزینه حقوق',
    employerContributionExpense: 'هزینه سهم کارفرما',
    payrollPayable: 'حقوق خالص پرداختنی',
    insurancePayable: 'بیمه پرداختنی',
    taxPayable: 'مالیات پرداختنی',
    deductionsPayable: 'سایر کسورات پرداختنی',
    currencyEmployeesHint: 'فقط کارکنان فعالی که با {{currency}} پرداخت می‌شوند منظور خواهند شد.',
    overtimeHours: 'ساعت اضافه‌کاری',
    overtimeHoursFor: 'ساعت اضافه‌کاری {{name}}',
    overtimeRate: 'نرخ ساعتی اضافه‌کاری',
    bonus: 'پاداش',
    otherAllowances: 'سایر مزایا',
    otherDeductions: 'سایر کسورات',
    grossEarnings: 'حقوق ناخالص',
    employeeInsurance: 'بیمه سهم کارمند',
    employerInsurance: 'بیمه سهم کارفرما',
    incomeTax: 'مالیات درآمد',
    netPay: 'خالص پرداختی',
    selectRun: 'دوره حقوق',
    post: 'ثبت در دفتر کل',
  },
  receivables: {
    title: 'حساب‌های دریافتنی',
    subtitle: 'اعتبار مشتری، اقلام باز، دریافت‌ها، برگه‌های بستانکار و سن مطالبات منطبق با حساب کنترل.',
    newReceipt: 'دریافت جدید',
    totalOpen: 'مطالبات باز',
    overdue: 'معوق',
    unappliedReceipts: 'دریافت‌های تخصیص‌نیافته',
    creditHolds: 'توقف اعتباری',
    holdReason: 'دلیل توقف',
    creditStatus: 'وضعیت اعتبار',
    available: 'اعتبار در دسترس',
    writeOffs: 'حذف مطالبات',
    collections: 'وصول مطالبات',
    newInvoice: 'فاکتور جدید',
    newCreditNote: 'برگه بستانکار جدید',
    newWriteOff: 'حذف مطالبه جدید',
    newActivity: 'فعالیت جدید',
    approve: 'تأیید',
    post: 'ثبت',
    apply: 'تخصیص',
    unapply: 'لغو تخصیص',
    reverse: 'برگشت',
    voidDocument: 'ابطال',
    hold: 'اعمال توقف',
    releaseHold: 'رفع توقف',
    statement: 'صورت‌حساب',
    clearSmallBalance: 'تسویه مانده جزئی',
    complete: 'تکمیل',
    cancelActivity: 'لغو',
    invoiceNumber: 'شماره فاکتور',
    receiptNumber: 'شماره دریافت',
    creditNoteNumber: 'شماره برگه بستانکار',
    writeOffNumber: 'شماره حذف مطالبه',
    customer: 'مشتری',
    lineDescription: 'شرح',
    netAmount: 'مبلغ خالص',
    taxAmount: 'مبلغ مالیات',
    taxRate: 'نرخ مالیات',
    taxInclusive: 'قیمت شامل مالیات است',
    addLine: 'افزودن سطر',
    removeLine: 'حذف سطر {{number}}',
    revenueAccount: 'حساب درآمد',
    outputTaxAccount: 'حساب مالیات فروش',
    receivableAccount: 'حساب کنترل دریافتنی',
    cashAccount: 'حساب بانک یا صندوق',
    advanceAccount: 'حساب پیش‌دریافت مشتری',
    expenseAccount: 'حساب هزینه مطالبات سوخت‌شده',
    fxGainAccount: 'حساب سود تسعیر تحقق‌یافته',
    fxLossAccount: 'حساب زیان تسعیر تحقق‌یافته',
    amount: 'مبلغ',
    paymentMethod: 'روش پرداخت',
    paymentMethodBank: 'انتقال بانکی',
    paymentMethodCash: 'نقدی',
    paymentMethodCheque: 'چک',
    approvalThreshold: 'آستانه تأیید',
    smallBalanceThreshold: 'آستانه مانده جزئی',
    elevatedApproval: 'اعمال مجوز سطح بالاتر',
    creditOverride: 'درخواست نادیده‌گرفتن محدودیت اعتبار',
    overrideReason: 'دلیل نادیده‌گرفتن',
    applyTo: 'تخصیص به قلم باز',
    activityKind: 'نوع فعالیت',
    activityDate: 'تاریخ فعالیت',
    followUpDate: 'تاریخ پیگیری',
    promisedAmount: 'مبلغ وعده‌داده‌شده',
    promisedDate: 'تاریخ وعده',
    templateCode: 'قالب',
    notes: 'یادداشت',
    outcome: 'نتیجه',
    openOnly: 'فقط فعالیت‌های باز',
    from: 'از',
    to: 'تا',
    openingBalance: 'مانده ابتدای دوره',
    closingBalance: 'مانده پایان دوره',
    overdueBalance: 'مانده معوق',
    exposure: 'میزان تعهد',
    openInvoices: 'فاکتورهای باز',
    openOrders: 'سفارش‌های باز',
    unbilledShipments: 'ارسال‌های فاکتورنشده',
    earlyDiscount: 'تخفیف تسویه زودهنگام',
    blocked: 'مسدود',
    allowed: 'مجاز',
    saved: 'ذخیره شد.',
    noAccountsConfigured: 'شناسه حساب‌های دفتر کل مقصد این سند را وارد کنید.',
  },
  payables: {
    title: 'حساب‌های پرداختنی',
    subtitle: 'اطلاعات پایه تأمین‌کننده، صورتحساب با تطبیق سه‌طرفه، پرداخت، دسته پرداخت و سن بدهی.',
    newPayment: 'پرداخت جدید',
    totalOpen: 'بدهی‌های باز',
    dueThisWeek: 'سررسید تا هفت روز آینده',
    heldBills: 'متوقف یا مورد اختلاف',
    runsAwaitingApproval: 'دسته‌های در انتظار تأیید',
    withholdingRate: 'درصد مالیات تکلیفی',
    bankAccount: 'حساب بانکی',
    selected: 'انتخاب‌شده',
    fileReference: 'شناسه فایل',
    breakdown: 'تفکیک مانده',
    breakdownHint: 'اقلامی که در کنار صورتحساب‌های باز طبقه‌بندی‌شده قرار می‌گیرند و جداگانه گزارش می‌شوند تا مانده حساب کنترل پرداختنی سطر به سطر قابل توضیح باشد.',
    advances: 'پیش‌پرداخت تأمین‌کننده',
    advancesHint: 'پیش‌پرداخت‌ها و پرداخت‌های تخصیص‌نیافته که هنوز نزد تأمین‌کننده است.',
    debitBalances: 'مانده‌های بدهکار',
    debitBalancesHint: 'تأمین‌کنندگانی که مانده خالص آن‌ها دریافتنی است و برای ارائه در صورت‌های مالی از پرداختنی جدا می‌شوند.',
    disputedBills: 'صورتحساب‌های مورد اختلاف',
    disputedBillsHint: 'صورتحساب‌های بازی که مورد اختلاف علامت خورده‌اند و تا رفع اختلاف از پیشنهاد پرداخت کنار گذاشته می‌شوند.',
    receiptAccruals: 'کالای دریافت‌شده بدون صورتحساب',
    receiptAccrualsHint: 'رسیدهای انبار که در برابر صورتحساب تطبیق یا تأیید نشده‌اند.',
    unappliedAmount: 'تخصیص‌نیافته',
    accruedAmount: 'تعهدشده',
    functionalAmount: 'به ارز عملیاتی',
    exchangeRate: 'نرخ',
    paymentFile: 'فایل پرداخت',
    paymentFileHint: 'تولید فایل دستور پرداخت بانکی برای دسته تأییدشده. هش ذخیره‌شده، فایل ارسالی به بانک را اثبات می‌کند.',
    fileFormat: 'قالب فایل',
    generateFile: 'تولید فایل',
    download: 'دریافت فایل',
    fileHash: 'هش فایل',
    instructionCount: 'تعداد دستور',
    noBreakdownItems: 'موردی برای نمایش در این بخش وجود ندارد.',
  },
  treasury: {
    title: 'نقد و بانک',
    subtitle: 'حساب‌های بانکی، صندوق‌ها، واریزها، بارگذاری صورت‌حساب بانکی و مغایرت‌گیری.',
    bankAccounts: 'حساب‌های بانکی',
    cashBoxes: 'صندوق‌ها',
    cashTransactions: 'تراکنش‌های نقدی',
    deposits: 'واریزها',
    statements: 'صورت‌حساب‌های بانکی',
    reconciliation: 'مغایرت‌گیری',
    accountNumber: 'شماره حساب',
    bankName: 'بانک',
    iban: 'شبا',
    lastReconciled: 'مغایرت‌گیری تا',
    neverReconciled: 'بدون مغایرت‌گیری',
    float: 'تنخواه',
    maximumBalance: 'سقف موجودی',
    kind: 'نوع',
    signedAmount: 'گردش',
    fileName: 'فایل',
    format: 'قالب',
    period: 'دوره',
    lines: 'سطرها',
    duplicates: 'تکراری',
    balanceDrift: 'انحراف مانده',
    openingBalance: 'مانده ابتدا',
    closingBalance: 'مانده پایان',
    statementBalance: 'مانده پایان صورت‌حساب',
    ledgerBalance: 'مانده پایان دفتر',
    outstandingDeposits: 'واریز در راه',
    outstandingPayments: 'پرداخت‌های وصول‌نشده',
    bankOnlyItems: 'اقلام فقط بانکی',
    errorItems: 'اصلاح اشتباهات',
    adjustedDifference: 'اختلاف تعدیل‌شده',
    matchedTotal: 'تطبیق‌شده',
    balanced: 'به‌طور کامل توضیح داده شده',
    unbalanced: 'اختلاف توضیح‌داده‌نشده',
    reconcilingItems: 'اقلام مغایرت',
    cleared: 'تسویه‌شده',
    outstanding: 'باز',
    cashOnHand: 'موجودی نقد',
    depositsInTransit: 'واریز در راه',
    openReconciliations: 'مغایرت‌گیری‌های باز',
    frozenAccounts: 'حساب‌های مسدود',
    automation: 'خودکارسازی',
    mappingProfiles: 'الگوهای نگاشت',
    mappingProfilesHint: 'نگاشت ستون‌ها که خروجی بانک را به سطرهای صورت‌حساب تبدیل می‌کند. بارگذاری CSV و XLSX به الگو نیاز دارد؛ MT940، CAMT.053 و OFX خودتوصیف هستند.',
    dateFormat: 'قالب تاریخ',
    culture: 'زبان و منطقه',
    amountConvention: 'قرارداد مبلغ',
    signedAmountConvention: 'یک ستون علامت‌دار',
    debitCreditConvention: 'ستون‌های جداگانه بدهکار و بستانکار',
    scopedToAccount: 'حساب مرتبط',
    allAccounts: 'همه حساب‌ها',
    journalProposals: 'پیشنهادهای سند',
    journalProposalsHint: 'اسناد پیش‌نویسی که برای سطرهای تطبیق‌نشده پیشنهاد شده‌اند. طبقه‌بندی را بررسی کنید، سپس برای ثبت تأیید یا با ذکر دلیل رد کنید.',
    proposalDate: 'تاریخ پیشنهاد',
    category: 'طبقه‌بندی',
    counterAccount: 'حساب مقابل',
    matchedRule: 'قاعده',
    suspense: 'حساب انتظامی',
    moneyIn: 'ورودی',
    moneyOut: 'خروجی',
    accept: 'تأیید',
    reject: 'رد',
    reclassify: 'تغییر طبقه‌بندی',
    postedJournal: 'سند',
    rejectionReason: 'دلیل رد',
    noProposals: 'پیشنهاد پیش‌نویسی برای بررسی وجود ندارد.',
    cashLimitAlerts: 'هشدارهای سقف صندوق',
    cashLimitAlertsHint: 'صندوق‌هایی که موجودی آن‌ها از سقف مصوب بیشتر است. مازاد را واریز کنید تا صندوق به محدوده مجاز بازگردد.',
    balance: 'موجودی',
    excessAmount: 'مازاد بر سقف',
    custodian: 'مسئول صندوق',
    noCashLimitAlerts: 'موجودی همه صندوق‌ها در محدوده مجاز است.',
    supportedFormats: 'قالب‌های پشتیبانی‌شده',
    requiresProfile: 'نیازمند الگوی نگاشت',
    selfDescribing: 'خودتوصیف',
  },
  settings: {
    title: 'تنظیمات',
    subtitle: 'بومی‌سازی، ترجیحات ارزی و امنیت حساب سازمان.',
    localization: 'بومی‌سازی',
    localizationHint: 'زبان رابط، تقویم و ظاهر سامانه را انتخاب کنید.',
    financial: 'تنظیمات مالی',
    financialHint: 'ارز پیش‌فرض سازمان و نرخ جاری دلار را تعیین کنید.',
    security: 'امنیت و نشست‌ها',
    securityHint: 'ورود خارجی و احراز هویت دومرحله‌ای را مدیریت کنید.',
    voice: 'هوش مصنوعی و صدا',
    voiceHint: 'فرمان‌های صوتی خصوصی فارسی و انگلیسی را روی همین دستگاه نصب و مدیریت کنید.',
    device: 'این دستگاه',
    deviceHint: 'ورزا را روی این دستگاه نصب کنید و تعیین کنید نسخهٔ برون‌خط نگه داشته شود یا نه.',
    backup: 'پشتیبان‌گیری',
    backupHint: 'دریافت نسخه پشتیبان کامل سامانه و پایگاه داده همین سازمان.',
    defaultLanguage: 'زبان پیش‌فرض',
    displayCalendar: 'تقویم نمایش',
    gregorian: 'میلادی',
    solarHijri: 'هجری شمسی',
    islamicHijri: 'هجری قمری',
    theme: 'پوسته نمایش',
    calendarFollowsLanguage: 'تقویم نمایشی مستقل از زبان رابط است و روی این دستگاه ذخیره می‌شود.',
    digitSystem: 'رقم‌های نمایشی',
    latinDigits: 'لاتین (0123456789)',
    persianDigits: 'فارسی (۰۱۲۳۴۵۶۷۸۹)',
    arabicIndicDigits: 'عربی (٠١٢٣٤٥٦٧٨٩)',
    iranCurrencyDisplay: '\u0646\u0645\u0627\u06cc\u0634 \u067e\u0648\u0644 \u0627\u06cc\u0631\u0627\u0646',
    iranCurrencyDisplayRial: '\u0631\u06cc\u0627\u0644',
    iranCurrencyDisplayToman: '\u062a\u0648\u0645\u0627\u0646',
    iranCurrencyDisplayBoth: '\u0631\u06cc\u0627\u0644 \u0648 \u062a\u0648\u0645\u0627\u0646',
    iranCurrencyDisplayHint: '\u0641\u0642\u0637 \u0646\u0645\u0627\u06cc\u0634 \u062a\u063a\u06cc\u06cc\u0631 \u0645\u06cc\u200c\u06a9\u0646\u062f\u061b \u0648\u0627\u062d\u062f \u062d\u0633\u0627\u0628\u062f\u0627\u0631\u06cc \u0630\u062e\u06cc\u0631\u0647\u200c\u0634\u062f\u0647 \u0648 \u0645\u0628\u0644\u063a \u0627\u0635\u0644\u06cc \u062a\u063a\u06cc\u06cc\u0631 \u0646\u0645\u06cc\u200c\u06a9\u0646\u062f.',
    dualCalendarHint: 'تاریخ‌ها به‌صورت استاندارد ISO ذخیره می‌شوند و فرم‌ها معادل آن‌ها را در تقویم‌های دیگر نیز نشان می‌دهند.',
    supportedCurrencies: 'ارزهای سامانه',
    supportedCurrenciesHint: 'این سازمان فقط می‌تواند از ارزهای فعال زیر استفاده کند.',
    enabled: 'فعال',
    defaultCurrency: 'ارز پیش‌فرض سازمان',
    defaultCurrencyHint: 'به‌عنوان ارز ترجیحی این سازمان ذخیره می‌شود.',
    usdToIrrRate: 'نرخ دلار به ریال',
    usdRateHint: 'تعداد ریال برابر با یک دلار را وارد کنید.',
    currentUsdRate: 'نرخ جاری: ۱ $ (Dollar) = {{rate}} ریال',
    rialsPerToman: 'ضریب تبدیل ریال و تومان',
    rialTomanFactorHint: 'تعداد ریال معادل یک تومان را بر اساس سیاست این سازمان وارد کنید.',
    currentRialTomanFactor: 'ضریب جاری: ۱ تومان = {{factor}} ریال',
    currencySaved: 'تنظیمات ارز ذخیره شد.',
    currencySaveFailed: 'تنظیمات ارز ذخیره نشد.',
    usdRateInvalid: 'نرخ دلار باید بزرگ‌تر از صفر باشد.',
    rialTomanFactorInvalid: 'ضریب ریال و تومان باید بزرگ‌تر از صفر باشد.',
    externalSignIn: 'ورود با سرویس خارجی',
    externalSignInHint: 'سرویس‌دهنده را فقط در حالت ورودشده متصل کنید. ورودهای بعدی از شناسه پایدار حساب سرویس‌دهنده استفاده می‌کنند.',
    providerLinked: 'به حساب ورزا متصل است',
    providerNotLinked: 'متصل نیست',
    link: 'اتصال حساب',
    unlink: 'قطع اتصال',
    notConfigured: 'پیکربندی نشده',
    twoFactor: 'احراز هویت دومرحله‌ای',
    twoFactorHint: 'یک یا چند روش را برای حساب خود فعال کنید. برنامه احراز هویت از پیامک یا ایمیل امن‌تر است.',
    authenticatorApp: 'برنامه احراز هویت',
    googleAuthenticator: 'گوگل آتنتیکیتور',
    microsoftAuthenticator: 'مایکروسافت آتنتیکیتور',
    authenticatorHint: 'با کدهای زمان‌محور استاندارد کار می‌کند.',
    emailCode: 'کد ایمیل',
    emailCodeHint: 'ارسال کد یک‌بارمصرف به ایمیل حساب شما.',
    smsCode: 'کد پیامک',
    smsCodeHint: 'ارسال کد یک‌بارمصرف به شماره همراه تأییدشده.',
    phoneNumber: 'شماره همراه با قالب بین‌المللی',
    activate: 'فعال‌سازی',
    disable: 'غیرفعال‌سازی',
    finishEnrollment: 'تکمیل فعال‌سازی',
    manualKeyHint: 'گوگل آتنتیکیتور یا مایکروسافت آتنتیکیتور را باز کنید و این کلید راه‌اندازی را دستی وارد کنید.',
    openAuthenticator: 'بازکردن در برنامه احراز هویت',
    confirmActivate: 'تأیید و فعال‌سازی',
    mfaEnabled: 'احراز هویت دومرحله‌ای فعال شد.',
    invalidVerificationCode: 'کد تأیید پذیرفته نشد.',
    securityLoadFailed: 'تنظیمات امنیتی بارگیری نشد.',
    securitySaveFailed: 'تغییر امنیتی ذخیره نشد.',
    fullTenantBackup: 'نسخه پشتیبان کامل سازمان',
    fullTenantBackupHint: 'یک بسته پشتیبان برخط فقط از سازمان انتخاب‌شده می‌سازد. اطلاعات سازمان‌های دیگر و پایگاه داده کنترل سامانه هرگز در آن قرار نمی‌گیرد.',
    backupDatabase: 'پایگاه داده کامل PostgreSQL سازمان',
    backupConfiguration: 'تنظیمات و مجوزهای سازمان',
    backupAudit: 'داده‌های ممیزی، یکپارچه‌سازی و کارهای پس‌زمینه',
    backupChecksum: 'فایل راهنما و کنترل صحت SHA-256',
    backupSensitive: 'فایل دریافتی شامل اطلاعات حساس کسب‌وکار است. آن را رمزگذاری‌شده نگه دارید و فقط از مسیر امن و تأییدشده به اشتراک بگذارید.',
    backupPermissionRequired: 'نقش شما مجوز دریافت نسخه پشتیبان سازمان را ندارد.',
    backupPermissionFailed: 'بررسی مجوز پشتیبان‌گیری انجام نشد.',
    getBackup: 'دریافت پشتیبان کامل',
    confirmBackup: 'نسخه پشتیبان این سازمان ساخته و دریافت شود؟',
    confirmBackupHint: 'ساخت پشتیبان پایگاه‌های داده بزرگ ممکن است چند دقیقه طول بکشد. تا آغاز دریافت، این صفحه را باز نگه دارید.',
    downloadBackup: 'ساخت و دریافت',
    preparingBackup: 'در حال آماده‌سازی پشتیبان…',
    backupReady: 'نسخه پشتیبان سازمان آماده و به بخش دریافت‌های شما ارسال شد.',
    backupFailed: 'نسخه پشتیبان سازمان آماده نشد. مجوز خود را بررسی کنید و دوباره تلاش کنید.',
  },
  documents: {
    printExport: 'چاپ و خروجی',
    print: 'چاپ',
    printDocument: 'چاپ این سند',
    preview: 'پیش‌نمایش چاپ',
    printPdf: 'چاپ / ذخیره PDF',
    exportAs: 'خروجی سند با قالب',
    word: 'ورد',
    excel: 'اکسل',
    noData: 'در این صفحه ردیف فهرست یا فیلد فرمی برای خروجی وجود ندارد.',
  },
  theme: {
    light: 'روشن',
    dark: 'تیره',
    system: 'هماهنگ با سیستم',
    switchToDark: 'تغییر به پوسته تیره',
    switchToLight: 'تغییر به پوسته روشن',
  },
  security: {
    recaptchaNotice: 'این سایت با reCAPTCHA محافظت می‌شود و <privacy>سیاست حریم خصوصی</privacy> و <terms>شرایط استفاده</terms> گوگل بر آن حاکم است.',
  },
  landing: {
    nav: {
      about: 'درباره ورزا',
      identity: 'شناسه بین‌سازمانی',
      plans: 'طرح‌ها و قیمت‌ها',
      support: 'پشتیبانی',
      policy: 'سیاست‌ها',
      sections: 'بخش‌های صفحه اصلی',
    },
    signIn: 'ورود',
    openWorkspace: 'ورود به کنسول',
    explore: 'بیشتر بخوانید',
    about: {
      title: 'درباره ورزا',
      body: 'ورزا دفتر کل، انبار، دریافتنی‌ها، پرداختنی‌ها، خزانه، خرید و فروش را در یک سامانه ماژولار نگه می‌دارد. هر کسب‌وکار پایگاه داده مستقل خود را دارد، هر ثبت رد حسابرسی خود را حفظ می‌کند و کل رابط کاربری به فارسی و انگلیسی کار می‌کند.',
      ledgerTitle: 'هسته دوطرفه',
      ledgerBody: 'دفتر کل، چندارزی و بستن دوره مانند یک زنجیره تراز عمل می‌کنند.',
      inventoryTitle: 'انبار قابل تطبیق',
      inventoryBody: 'ارزش موجودی تا سطح سری ساخت و شماره سریال با دفتر کل تطبیق می‌شود.',
      bilingualTitle: 'دوزبانه از پایه',
      bilingualBody: 'فارسی راست‌به‌چپ و انگلیسی چپ‌به‌راست، هر دو در جایگاه اصلی.',
      tenantTitle: 'تفکیک مستأجرها',
      tenantBody: 'یک پایگاه داده فیزیکی برای هر کسب‌وکار، با نقش‌های کم‌دسترسی.',
    },
    identity: {
      title: 'شناسه بین‌سازمانی ورزا',
      body: 'شناسه بین‌سازمانی ورزا (VII) یک شناسه یکتا برای هر شخص حقیقی یا حقوقی در سراسر ورزا است، مانند شبا در نظام بانکی. با یک نشانی ایمیل ثبت‌نام کنید، آن را تأیید کنید و کد از آنِ شماست؛ رایگان و مستقل از هر کسب‌وکاری که از آن استفاده می‌کند.',
      codeTitle: 'یک کد، همراه با رقم کنترلی',
      codeBody: 'بیست نویسه با ارقام کنترلی خودش؛ کدی که اشتباه گفته یا تایپ شود رد می‌شود و به شخص دیگری نسبت داده نمی‌شود.',
      inboxTitle: 'اسناد شما، در یک جا',
      inboxBody: 'فاکتورها، رسیدها و صورت‌حساب‌هایی که هر کسب‌وکار ورزا برای شما صادر می‌کند، در یک پورتال سبک می‌آید؛ خواندنی، نه ویرایش‌شدنی.',
      shareTitle: 'واگذاری با شرط شما',
      shareBody: 'کارت را با ایمیل یا کیو‌آر به اشتراک بگذارید، تصمیم بگیرید در فهرست عمومی دیده شوید یا نه، و هر زمان خواستید دسترسی یک کسب‌وکار را ببندید.',
      action: 'ساخت رایگان شناسه بین‌سازمانی',
      actionHint: 'تنها یک نشانی ایمیل لازم است.',
    },
    plans: {
      title: 'طرح‌ها و قیمت‌ها',
      body: 'با شناسه بین‌سازمانی رایگان شروع کنید و تنها زمانی که کسب‌وکارتان به دفتر کل کامل نیاز دارد، ارتقا دهید.',
      note: 'قیمت‌ها بدون مالیات‌های محلی است. پرداخت سالانه در طرح‌های پولی ممکن است.',
      popular: 'پرانتخاب‌ترین',
      identityAction: 'ساخت شناسه رایگان',
      contactAction: 'تماس با ما',
      identityName: 'شناسه بین‌سازمانی',
      identityPrice: 'رایگان',
      identityCadence: 'بدون نیاز به کارت بانکی',
      identitySummary: 'یک شناسه، برای شخص حقیقی یا حقوقی.',
      identityFirst: 'کد شناسه بین‌سازمانی و کارت کیو‌آر شما',
      identitySecond: 'پورتال سبک، بدون ورود اطلاعات',
      identityThird: 'همه اسنادی که کسب‌وکارها برای شما صادر می‌کنند',
      identityFourth: 'اشتراک کد با ایمیل یا اسکن',
      personalName: 'شخصی',
      personalPrice: '۵۰۰٬۰۰۰ تومان',
      personalCadence: 'ماهانه',
      personalSummary: 'استفاده شخصی و کسب‌وکار کوچک.',
      personalFirst: 'دفتر کل دوطرفه و انبار به‌صورت کامل',
      personalSecond: 'اتصال تا ۵ شناسه بین‌سازمانی ورزا',
      personalThird: 'صدور سند برای افرادی که با آن‌ها کار می‌کنید',
      personalFourth: 'بدون قابلیت‌های هوش مصنوعی',
      professionalName: 'حرفه‌ای',
      professionalPrice: '۵٬۰۰۰٬۰۰۰ تومان',
      professionalCadence: 'ماهانه',
      professionalSummary: 'کسب‌وکاری در حال رشد با عملیات واقعی.',
      professionalFirst: 'همه امکانات طرح شخصی، به‌همراه همه ماژول‌ها',
      professionalSecond: 'اتصال به شناسه‌های بین‌سازمانی بدون محدودیت',
      professionalThird: 'قابلیت‌های هوش مصنوعی در سقف ماهانه',
      professionalFourth: 'ورود دومرحله‌ای و مدیریت دسترسی',
      enterpriseName: 'سازمانی',
      enterprisePrice: 'اختصاصی',
      enterpriseCadence: 'بر پایه قرارداد',
      enterpriseSummary: 'چند شخصیت حقوقی، یک رویه حسابداری.',
      enterpriseFirst: 'شرکت‌ها و پایگاه‌های داده بدون محدودیت',
      enterpriseSecond: 'ورود یکپارچه و محیط اختصاصی',
      enterpriseThird: 'کارشناس پشتیبانی اختصاصی',
    },
    support: {
      title: 'پشتیبانی',
      body: 'با کسی در تماس باشید که محصول و پیکربندی شما را می‌شناسد.',
      email: 'support@varza.app',
      emailTitle: 'میز پشتیبانی',
      emailBody: 'پاسخ در روزهای کاری، به فارسی یا انگلیسی.',
      docsTitle: 'مستندات',
      docsBody: 'راهنمای راه‌اندازی، انتقال داده و بستن دوره همراه محصول ارائه می‌شود.',
      statusTitle: 'وضعیت سرویس',
      statusBody: 'توقف‌های برنامه‌ریزی‌شده و رخدادها پیش از اثرگذاری اعلام می‌شوند.',
    },
    policy: {
      title: 'سیاست‌ها',
      body: 'آنچه ورزا با داده شما می‌کند، بی‌ابهام.',
      dataTitle: 'تفکیک داده',
      dataBody: 'هر مستأجر پایگاه داده فیزیکی جداگانه دارد و هیچ پرس‌وجویی از این مرز عبور نمی‌کند.',
      securityTitle: 'امنیت',
      securityBody: 'نشست‌ها را سرور صادر می‌کند، ورود دومرحله‌ای در دسترس است و دسترسی از مجوزهایی که می‌دهید پیروی می‌کند.',
      privacyTitle: 'حریم خصوصی',
      privacyBody: 'اسناد حسابداری شما متعلق به شماست؛ هرگز فروخته، به اشتراک گذاشته یا داده‌کاوی نمی‌شود.',
      termsTitle: 'شرایط',
      termsBody: 'متن کامل سیاست حریم خصوصی و شرایط خدمات همراه قرارداد سرویس ارائه می‌شود.',
    },
    footer: {
      rights: '© {{year}} ورزا. همه حقوق محفوظ است.',
    },
  },
  vii: {
    eyebrow: 'شناسه بین‌سازمانی ورزا',
    pageTitle: 'شناسه بین‌سازمانی',
    pageSubtitle: 'کارت شناسه بین‌سازمانی شما، اسنادی که کسب‌وکارها برای شما صادر کرده‌اند، و دارندگانی که این شرکت به آن‌ها متصل است.',
    tabIdentity: 'شناسه من',
    tabInbox: 'صادرشده برای من',
    tabNetwork: 'دارندگان متصل',
    cardEyebrow: 'شناسه بین‌سازمانی ورزا',
    code: 'کد شناسه بین‌سازمانی',
    copyCode: 'رونوشت کد',
    codeCopied: 'کد رونوشت شد.',
    holder: 'دارنده',
    holderKind: 'نوع دارنده',
    holderKindHint: 'شخص حقیقی کد ملی دارد؛ شخص حقوقی یک شرکت ثبت‌شده است.',
    email: 'نشانی ایمیل',
    issuedOn: 'تاریخ صدور',
    dueOn: 'سررسید',
    amount: 'مبلغ',
    currency: 'ارز',
    business: 'کسب‌وکار',
    connectedOn: 'تاریخ اتصال',
    documents: 'اسناد',
    issuer: 'صادرکننده',
    qrLabel: 'کد کیو‌آر شناسه بین‌سازمانی ورزا {{code}}',
    qrHint: 'برای خواندن کد اسکن کنید. این کد فقط شناسایی می‌کند و ورود به سامانه نیست.',
    kind: {
      real: 'شخص حقیقی',
      legal: 'شخص حقوقی',
    },
    state: {
      pending: 'در انتظار تأیید',
      active: 'فعال',
      suspended: 'معلق',
    },
    claimTitle: 'هنوز شناسه بین‌سازمانی ندارید',
    claimBody: 'حساب شما پیش‌تر تأیید شده است، پس کد همین حالا صادر می‌شود. این کد حتی با ترک این شرکت نزد شما می‌ماند.',
    claimAction: 'صدور شناسه من',
    claimFailed: 'صدور شناسه ممکن نشد.',
    shareTitle: 'اشتراک کارت',
    shareBody: 'کد را برای کسب‌وکاری بفرستید که باید برای شما سند صادر کند.',
    shareEmail: 'ارسال به',
    shareNote: 'یادداشت (اختیاری)',
    shareAction: 'ارسال کارت',
    shareSent: 'کارت به {{email}} فرستاده شد.',
    shareFailed: 'ارسال کارت ممکن نشد.',
    shareCooldown: 'کمی صبر کنید و دوباره کارت بفرستید.',
    discoverabilityTitle: 'نمایش در فهرست',
    discoverabilityBody: 'کد دقیق شما همیشه کار می‌کند. نمایش در فهرست اجازه می‌دهد کسب‌وکار شما را با نام یا ایمیل هم پیدا کند.',
    discoverableOn: 'در فهرست نمایش داده می‌شود',
    discoverableOff: 'فقط با کد دقیق قابل دسترسی است',
    discoverabilityFailed: 'ذخیره تنظیم فهرست ممکن نشد.',
    myConnectionsTitle: 'کسب‌وکارهای متصل به من',
    myConnectionsBody: 'هر کدام تا زمانی که اتصال را نبندید می‌توانند برای شناسه شما سند صادر کنند.',
    noConnectionsTitle: 'هنوز کسب‌وکاری متصل نیست',
    noConnectionsBody: 'کارت خود را به اشتراک بگذارید تا اسناد صادرشده برای شما اینجا بیاید.',
    revokeAccess: 'بستن',
    summaryTotal: 'اسناد',
    summaryUnread: 'خوانده‌نشده',
    summaryOpen: 'باز',
    summaryOverdue: 'سررسید گذشته',
    openBalance: 'مبلغ باز',
    settledTotal: 'تسویه‌شده',
    filterLabel: 'پالایش بر پایه وضعیت',
    filterAll: 'همه',
    markRead: 'علامت خوانده‌شده',
    noDocumentsTitle: 'هنوز سندی برای شما صادر نشده است',
    noDocumentsBody: 'وقتی کسب‌وکاری متصل، فاکتور یا رسیدی منتشر کند، اینجا دیده می‌شود.',
    documentKind: {
      invoice: 'فاکتور',
      creditnote: 'برگه بستانکار',
      receipt: 'رسید',
      statement: 'صورت‌حساب',
      contract: 'قرارداد',
      notice: 'اعلامیه',
      other: 'سند',
    },
    documentState: {
      issued: 'باز',
      settled: 'تسویه‌شده',
      cancelled: 'ابطال‌شده',
    },
    directoryTitle: 'یافتن دارنده',
    directoryBody: 'با کد دقیق شناسه بین‌سازمانی جست‌وجو کنید، یا با نام و ایمیل برای دارندگانی که نمایش در فهرست را پذیرفته‌اند.',
    directoryQuery: 'کد، نام یا ایمیل',
    directoryPlaceholder: 'VI49 1234 5678 9012 3456',
    directoryHint: 'دست‌کم سه نویسه. کد با فاصله یا بدون فاصله پذیرفته می‌شود.',
    tenantPerson: 'شخص محلی',
    tenantPersonHint: 'شخص اصلی که این شناسه در شرکت شما نمایندهٔ آن است انتخاب کنید.',
    personRequired: 'پیش از اتصال یک شخص محلی انتخاب کنید.',
    personKindMismatch: 'شخص محلی و دارندهٔ شناسه باید هر دو حقیقی یا هر دو حقوقی باشند.',
    noMatchTitle: 'دارنده‌ای یافت نشد',
    noMatchBody: 'کد را بررسی کنید یا از دارنده بخواهید کارتش را با شما به اشتراک بگذارد.',
    connectAction: 'اتصال',
    alreadyConnected: 'متصل',
    connectFailed: 'برقراری اتصال ممکن نشد.',
    codeNotFound: 'شناسه فعالی با این کد وجود ندارد.',
    tenantConnectionsTitle: 'دارندگان متصل',
    tenantConnectionsBody: 'شناسه‌هایی که این شرکت می‌تواند برایشان سند منتشر کند.',
    noTenantConnectionsTitle: 'هنوز دارنده‌ای متصل نیست',
    noTenantConnectionsBody: 'در کادر بالا کدی را که مشتری یا تأمین‌کننده به شما داده جست‌وجو کنید.',
    publishAction: 'انتشار',
    disconnect: 'قطع اتصال',
    publishTitle: 'انتشار برای {{name}}',
    publishBody: 'تنها سرصفحه سند به اشتراک گذاشته می‌شود. سند حسابداری در همین شرکت می‌ماند.',
    publishConfirm: 'انتشار سند',
    publishFailed: 'انتشار سند ممکن نشد.',
    publishDuplicate: 'سندی با همین نوع و شماره پیش‌تر منتشر شده است.',
    amountInvalid: 'مبلغی صفر یا بیشتر وارد کنید.',
    documentKindLabel: 'نوع',
    documentNumber: 'شماره',
    documentTitle: 'عنوان',
    documentStateLabel: 'وضعیت',
    documentSummary: 'خلاصه (اختیاری)',
    publishedTitle: 'منتشرشده',
    publishedEmpty: 'هنوز چیزی از این اتصال منتشر نشده است.',
    readByHolder: 'خوانده‌شده',
    withdraw: 'پس‌گرفتن',
    registerTitle: 'ساخت شناسه بین‌سازمانی ورزا',
    registerSubtitle: 'با دعوت‌نامه خود شناسه‌ای بسازید که در همه کسب‌وکارهای ورزا متعلق به شما می‌ماند.',
    registerName: 'نام کامل یا نام شرکت',
    registerAction: 'ساخت شناسه من',
    registerFailed: 'تکمیل ثبت‌نام ممکن نشد.',
    invitationCode: 'کد دعوت‌نامه',
    invitationHint: 'ثبت‌نام خصوصی است. کد یک‌بارمصرفی را که برای شما فرستاده‌اند وارد کنید.',
    invitationInvalid: 'این دعوت‌نامه نامعتبر، منقضی، لغوشده یا استفاده‌شده است.',
    passwordHint: 'دست‌کم {{count}} نویسه.',
    passwordTooShort: 'دست‌کم {{count}} نویسه وارد کنید.',
    tooManyAttempts: 'تلاش‌ها زیاد بود. یک دقیقه بعد دوباره تلاش کنید.',
    alreadyHave: 'پیش‌تر حساب دارید؟',
    orSignUpWith: 'یا ثبت‌نام با',
    signUpGoogle: 'ثبت‌نام با گوگل',
    signUpMicrosoft: 'ثبت‌نام با مایکروسافت',
    benefit: {
      free: 'رایگان و مستقل از هر شرکتی',
      documents: 'خواندن همه اسناد صادرشده برای شما',
      portable: 'همان کد، هر جا که کار کنید',
    },
    checkEmailTitle: 'ایمیل خود را ببینید',
    checkEmailBody: 'اگر {{email}} قابل ثبت باشد، پیوند تأیید در راه است.',
    checkEmailHint: 'این پیوند ۲۴ ساعت اعتبار دارد و یک‌بار مصرف است.',
    developmentToken: 'نسخه توسعه:',
    confirmNow: 'همین حالا تأیید کنید',
    resend: 'ارسال دوباره پیوند',
    backToSignIn: 'بازگشت به ورود',
    confirmingTitle: 'در حال تأیید ایمیل شما',
    confirmingBody: 'لحظه‌ای صبر کنید تا پیوند بررسی شود.',
    confirmedTitle: 'شناسه شما فعال شد',
    confirmedBody: 'این کد اکنون در همه کسب‌وکارهای ورزا از آنِ شماست.',
    confirmedHint: 'وارد شوید تا کارت خود را ببینید، به اشتراک بگذارید و اسناد صادرشده برای خود را بخوانید.',
    yourCode: 'کد شناسه بین‌سازمانی شما',
    signInNow: 'ورود',
    confirmFailedTitle: 'این پیوند دیگر کار نمی‌کند',
    confirmFailedBody: 'پیوند تأیید پس از ۲۴ ساعت منقضی می‌شود و تنها یک‌بار قابل استفاده است.',
    confirmFailedHint: 'پیوند تازه بخواهید یا با همان نشانی دوباره ثبت‌نام کنید.',
    registerAgain: 'ثبت‌نام دوباره',
    publicTitle: 'یک شناسه بین‌سازمانی ورزا',
    publicBody: 'این کد ساختار درستی دارد و ارقام کنترلی آن مطابقت می‌کند.',
    publicHint: 'برای اتصال به این دارنده وارد شوید، یا شناسه خودتان را بسازید.',
    publicConnect: 'ورود برای اتصال',
    publicRegister: 'ساخت شناسه خودم',
    publicInvalidTitle: 'این یک شناسه بین‌سازمانی ورزا نیست',
    publicInvalidBody: 'ارقام کنترلی با بقیه کد مطابقت ندارد.',
    publicInvalidHint: 'کد با VI آغاز می‌شود و هجده رقم دارد که در گروه‌های چهارتایی نوشته می‌شود.',
  },
  portal: {
    name: 'پورتال شناسه',
    overview: 'نمای کلی',
    documents: 'اسناد',
    workforce: 'کار من',
    identity: 'شناسه من',
    welcome: 'خوش آمدید، {{name}}',
    overviewSubtitle: 'آنچه کسب‌وکارهای ورزا برای شناسه بین‌سازمانی شما صادر کرده‌اند.',
    readOnlyTitle: 'اتاق مطالعه، نه دفتر کل',
    readOnlyBody: 'اینجا چیزی را شما وارد یا ویرایش نمی‌کنید. هر سند رونوشتی است که کسب‌وکار صادرکننده منتشر کرده و سند اصلی نزد او می‌ماند.',
    manageIdentity: 'مدیریت شناسه من',
    recentTitle: 'اسناد اخیر',
    recentBody: 'پنج رونوشت اخیر صادرشده برای شما.',
    seeAll: 'مشاهده همه اسناد',
    documentsTitle: 'اسناد صادرشده برای من',
    documentsSubtitle: 'همه اسناد باز، تسویه‌شده و ابطال‌شده، از همه کسب‌وکارهای متصل به شناسه شما.',
    identityTitle: 'شناسه من',
    identitySubtitle: 'کارت شما، اینکه چه کسی می‌تواند شما را بیابد، و کدام کسب‌وکارها می‌توانند برایتان منتشر کنند.',
  },
  calendar: {
    solarHijriField: 'هجری شمسی (سال/ماه/روز)',
    gregorianField: 'میلادی (سال/ماه/روز)',
    islamicHijriField: 'هجری قمری (سال/ماه/روز)',
    gregorianEquivalent: 'معادل میلادی',
    equivalent: 'معادل {{calendar}}',
    system: {
      persian: 'هجری شمسی',
      gregorian: 'میلادی',
      islamic: 'هجری قمری',
    },
    openCalendar: 'باز کردن تقویم',
    closeCalendar: 'بستن تقویم',
    chooseDate: 'انتخاب تاریخ',
    chooseMonth: 'انتخاب ماه',
    chooseYear: 'انتخاب سال',
    previousMonth: 'ماه قبل',
    nextMonth: 'ماه بعد',
    previousYear: 'سال قبل',
    nextYear: 'سال بعد',
    previousYears: 'سال‌های قبل',
    nextYears: 'سال‌های بعد',
    today: 'امروز',
    clear: 'پاک کردن',
    noDate: 'تاریخی انتخاب نشده است',
  },
  status: {
    draft: 'پیش‌نویس',
    aiPendingApproval: 'ویرایش‌شده توسط ربات ورزا · نیازمند تأیید',
    submitted: 'ارسال‌شده',
    approved: 'تأییدشده',
    posted: 'ثبت‌شده',
    reversed: 'برگشت‌شده',
    closed: 'بسته',
    active: 'فعال',
    pending: 'در انتظار',
    blocked: 'متوقف',
  },
  errors: {
    pageTitle: 'بارگذاری این صفحه ممکن نشد',
    pageMessage: 'اتصال را بررسی و دوباره تلاش کنید. اطلاعات ذخیره‌نشده حذف نشده است.',
    notFoundTitle: 'صفحه پیدا نشد',
    notFoundMessage: 'فضای کاری درخواستی وجود ندارد یا شما به آن دسترسی ندارید.',
    returnDashboard: 'بازگشت به داشبورد',
  },
  offline: {
    chip: {
      label: 'وضعیت اتصال و کارهای ارسال‌نشده',
      offline: 'برون‌خط',
      synced: 'هم‌گام است',
      waiting: 'برای ارسال: {{count}}',
      attention: 'نیازمند بررسی: {{count}}',
    },
    status: {
      online: 'متصل به ورزا',
      offline: 'اتصال به ورزا برقرار نیست',
      notEnabled: 'این دستگاه نسخهٔ برون‌خط نگه نمی‌دارد.',
      sealed: 'نسخهٔ برون‌خط تا ورود با گذرواژه قفل است.',
      copyConfirmed: 'نسخهٔ برون‌خط {{when}} تأیید شده است.',
    },
    state: {
      pending: 'در انتظار',
      failed: 'تلاش مجدد',
      rejected: 'رد شد',
      uncertain: 'نامشخص',
    },
    pause: {
      offline: 'در انتظار اتصال',
      sealed: 'نسخهٔ برون‌خط قفل است',
      authentication: 'برای ارسال دوباره وارد شوید',
      rejected: 'سرور یک سند را نپذیرفت',
      server: 'سرور پاسخ نداد',
      uncertain: 'یک سند نیازمند بررسی است',
    },
    error: {
      unknown: 'سرور آن را نپذیرفت.',
      unreadable: 'این سند دیگر در این دستگاه خواندنی نیست.',
      no_reply: 'ارسال شد اما پاسخی نرسید. پیش از ارسال دوباره، دفتر اسناد را بررسی کنید.',
      needsConnection: 'این کار به اتصال با سرور ورزا نیاز دارد.',
    },
    queue: {
      empty: 'چیزی در انتظار ارسال نیست.',
      lines_one: '{{count}} ردیف',
      lines_other: '{{count}} ردیف',
      journalCreate: 'سند حسابداری تازه',
      journalUpdate: 'ویرایش سند حسابداری',
      journalDelete: 'حذف سند حسابداری',
      journalSubmit: 'ارسال سند برای تصویب',
    },
    lastSync: 'آخرین تلاش {{when}}',
    syncNow: 'ارسال کن',
    syncing: 'در حال ارسال…',
    discard: 'دور انداختن',
    manageDevice: 'تنظیمات دستگاه',
    notice: {
      connectionLost: 'کار در حالت برون‌خط',
      offlineSession: 'ورود برون‌خط',
      copyAge: 'ارقام از نسخه‌ای است که {{when}} گرفته شده است.',
      copyUnknown: 'ارقام از نسخهٔ نگهداری‌شده روی این دستگاه است.',
      draftsQueued: 'پیش‌نویس‌های تازه همین‌جا نگه داشته و با بازگشت اتصال ارسال می‌شوند.',
      readOnly: 'تا بازگشت اتصال، امکان ذخیره وجود ندارد.',
      waiting: 'برای ارسال: {{count}}',
    },
    unlock: {
      eyebrow: 'بدون اتصال',
      title: 'ورود برون‌خط',
      subtitle: 'ورزا به سرور دسترسی ندارد. گذرواژهٔ شما نسخهٔ نگهداری‌شده روی این دستگاه را باز می‌کند.',
      account: 'حساب کاربری',
      copyTaken: 'زمان تهیهٔ نسخه',
      windowCloses: 'پایان اعتبار دسترسی برون‌خط',
      passwordHint: 'همان گذرواژه‌ای که با آن به ورزا وارد می‌شوید.',
      action: 'باز کردن نسخهٔ برون‌خط',
      opening: 'در حال باز کردن…',
      passwordError: 'این گذرواژه نسخهٔ این دستگاه را باز نمی‌کند.',
      lockedError: 'تلاش‌های نادرست زیاد بوده است. کمی صبر کنید.',
      expiredError: 'اعتبار این نسخه پایان یافته است. برای تمدید، با اتصال وارد شوید.',
      failedError: 'باز کردن نسخهٔ برون‌خط روی این دستگاه ممکن نشد.',
      barred: 'تلاش‌های نادرست زیاد بوده است. {{seconds}} ثانیه دیگر تلاش کنید.',
      scope: 'ارقام را همان‌گونه می‌بینید که در زمان تهیهٔ نسخه بوده‌اند. پیش‌نویس اسناد همین‌جا نگه داشته و پس از بازگشت اتصال ارسال می‌شود؛ ثبت قطعی، تصویب و عملیات پرداخت به اتصال نیاز دارند.',
      tryConnection: 'بررسی دوبارهٔ اتصال',
    },
    forget: {
      start: 'پاک کردن اطلاعات این دستگاه',
      action: 'پاک کن',
      confirm: 'نسخهٔ برون‌خط و کلید گذرواژهٔ آن از این دستگاه حذف می‌شود.',
      confirmWithQueue: 'اسناد ارسال‌نشدهٔ این دستگاه از دست می‌رود. در انتظار ارسال: {{count}}.',
    },
    login: {
      remember: 'کار برون‌خط روی این دستگاه فعال باشد',
      rememberHint: 'ورزا نسخه‌ای رمزنگاری‌شده از فضای کاری شما را همین‌جا نگه می‌دارد که با گذرواژهٔ خودتان باز می‌شود و تا {{days}} روز بدون اتصال کار می‌کند.',
    },
    device: {
      title: 'دسترسی برون‌خط',
      hint: 'نسخه‌ای رمزنگاری‌شده از این فضای کاری روی این دستگاه نگه دارید و تا {{days}} روز بدون اتصال کار کنید.',
      unsupported: 'این مرورگر نمی‌تواند نسخهٔ برون‌خط را محافظت کند. ورزا برای رمزنگاری به مبدأ ایمن (HTTPS) و حافظهٔ مرورگر نیاز دارد.',
      explain: 'گذرواژهٔ شما نسخه را قفل می‌کند و هیچ‌گاه ذخیره نمی‌شود؛ ورزا کلیدی را زیر آن می‌پیچد، پس هیچ‌چیز روی این دستگاه بدون شما خواندنی نیست.',
      passwordLabel: 'گذرواژهٔ ورزای شما',
      passwordHint: 'تنها برای قفل و باز کردن نسخهٔ این دستگاه به کار می‌رود.',
      showPassword: 'نمایش',
      hidePassword: 'پنهان',
      enable: 'نگه‌داشتن نسخهٔ برون‌خط',
      enabled: 'این دستگاه از این پس نسخهٔ برون‌خط نگه می‌دارد.',
      enableFailed: 'ساخت نسخهٔ برون‌خط روی این دستگاه ممکن نشد.',
      state: 'وضعیت',
      open: 'باز',
      sealed: 'قفل',
      account: 'حساب کاربری',
      confirmed: 'آخرین تأیید سرور',
      expires: 'پایان اعتبار دسترسی برون‌خط',
      held: 'نگهداری‌شده در این دستگاه',
      heldValue: '{{count}} صفحه · {{size}}',
      persistence: 'حافظه',
      persistenceGranted: 'مرورگر نگهداری آن را تضمین کرده است',
      persistenceRequest: 'درخواست نگهداری از مرورگر',
      clearCache: 'پاک کردن صفحه‌های ذخیره‌شده',
      queueTitle: 'در انتظار ارسال',
      queueHint: 'اسنادی که در نبود اتصال روی این دستگاه نوشته شده‌اند.',
      waiting: 'در انتظار ارسال',
      attention: 'نیازمند بررسی',
      lastSync: 'آخرین تلاش',
      scopeTitle: 'چه چیزی بدون اتصال کار می‌کند',
      scopeHint: 'دامنه به‌عمد محدود است: هر کاری که دربارهٔ دفاتر تصمیم می‌گیرد به سرور زنده نیاز دارد.',
      scopeReads: 'خواندن صفحه‌هایی که پیش‌تر باز کرده‌اید — داشبورد، دفتر کل، حساب‌ها، اسناد، انبار، دریافتنی، پرداختنی و خزانه',
      scopeDrafts: 'نوشتن، ویرایش و ارسال پیش‌نویس اسناد حسابداری',
      scopePosting: 'ثبت قطعی، تصویب، برگشت سند، دریافت و پرداخت',
      scopeLive: 'دستیار هوشمند، گزارش‌ها، خروجی‌ها و کارهای مدیریتی',
    },
    dataset: {
      session: 'حساب کاربری و سازمان‌ها',
      dashboard: 'داشبورد',
      scope: 'شعبه‌ها، دوره‌ها و ارزها',
      exchangeRates: 'نرخ ارز',
      accounts: 'کدینگ حساب‌ها',
      ledger: 'دفاتر و تراز آزمایشی',
      persons: 'اشخاص و طرف‌حساب‌ها',
      journals: 'اسناد حسابداری',
      inventory: 'انبار',
      receivables: 'حساب‌های دریافتنی',
      payables: 'حساب‌های پرداختنی',
      treasury: 'خزانه',
      reports: 'تعاریف گزارش',
    },
    journal: {
      queuedTitle: 'روی این دستگاه ذخیره شد',
      queuedBody: 'اتصالی وجود ندارد، پس این سند هنوز شماره ندارد. ورزا به‌محض بازگشت اتصال آن را ارسال می‌کند و شماره‌گذاری و بررسی در همان زمان روی سرور انجام می‌شود.',
    },
  },
  pwa: {
    install: {
      title: 'نصب ورزا',
      subtitle: 'ورزا را از میزکار یا صفحهٔ اصلی گوشی، در پنجرهٔ اختصاصی خودش باز کنید و بدون اتصال هم به کار ادامه دهید.',
      action: 'نصب',
      how: 'روش نصب',
      dismiss: 'فعلاً نه',
      never: 'دیگر نشان نده',
      reminderOff: 'یادآور نصب روی این دستگاه خاموش است.',
      reminderRestore: 'دوباره نشان بده',
      iosSteps: 'دکمهٔ هم‌رسانی (Share) را بزنید و «Add to Home Screen» را انتخاب کنید.',
      safariSteps: 'از منوی File گزینهٔ «Add to Dock» را انتخاب کنید.',
      sectionTitle: 'نصب روی این دستگاه',
      sectionHint: 'ورزای نصب‌شده در پنجرهٔ خودش باز می‌شود، نشان اختصاصی دارد و بدون اتصال هم اجرا می‌شود.',
      alreadyInstalled: 'ورزا روی این دستگاه نصب است.',
      unavailable: 'این مرورگر امکان نصب را ارائه نمی‌دهد. کروم، اج و سافاری این امکان را دارند.',
    },
    update: {
      available: 'نسخهٔ تازه‌ای از ورزا آماده است.',
      reload: 'به‌روزرسانی',
      applying: 'در حال به‌روزرسانی…',
    },
  },
};

/**
 * Arabic ships with the complete English key shape as a deliberate compatibility layer. New
 * or specialist screens therefore remain readable while their Arabic copy is reviewed, and
 * the shared shell, forms, navigation, status and recovery vocabulary below is fully Arabic.
 */
export const ar: TranslationShape<typeof en> = {
  ...en,
  workforce: {
    ...en.workforce,
    title: 'القوى العاملة',
    description: 'الأشخاص المرجعيون والعلاقات الفعالة والعقود المحكومة والأدلة التشغيلية والتعويض القابل للتفسير.',
    summary: 'ملخص القوى العاملة',
    profilesTab: 'الملفات',
    engagementsTab: 'العلاقات والعقود',
    compensationTab: 'التعويضات',
    timeTab: 'الوقت والإجازات',
    revenueTab: 'العمولات والمبيعات',
    claimsTab: 'المطالبات والقروض',
    settlementsTab: 'التسويات والدفع',
    disputesTab: 'النزاعات',
    reportsTab: 'التقارير والذكاء الاصطناعي',
    selectProfile: 'اختر ملف قوى عاملة',
    selectProfileHint: 'تقتصر السجلات التشغيلية دائماً على ملف قوى عاملة موثّق واحد.',
    workflowFailed: 'تعذر تنفيذ إجراء سير العمل. حدّث السجل وأعد المحاولة بإصداره الحالي.',
    activeProfiles: 'الملفات النشطة',
    relationships: 'العلاقات',
    roleAssignments: 'إسنادات الأدوار',
    persistedContracts: 'العقود المحفوظة',
    newProfile: 'ملف جديد',
    addRelationship: 'إضافة علاقة',
    newContract: 'عقد جديد',
    newPlan: 'خطة جديدة',
    calculate: 'حساب',
    profilesTitle: 'ملفات القوى العاملة',
    profilesHint: 'هوية قوى عاملة واحدة لكل شخص حقيقي مرجعي.',
    noProfiles: 'لا توجد ملفات قوى عاملة',
    noProfilesHint: 'أنشئ ملفاً لتأسيس العلاقات والعقود والتعويضات.',
    numberPerson: 'الرقم / الشخص',
    externalReference: 'المرجع الخارجي',
    viewProfile: 'عرض الملف',
    profileLoadFailed: 'تعذر تحميل تفاصيل الملف.',
    profileVersion: 'الملف {{number}} · إصدار التفاؤل {{version}}',
    canonicalPerson: 'الشخص المرجعي',
    relationshipsRolesTitle: 'العلاقات وإسنادات الأدوار',
    engagementsFor: 'ارتباطات القوى العاملة الفعالة لـ {{name}}.',
    selectProfileEngagement: 'اختر ملفاً قبل إنشاء ارتباط.',
    noRelationships: 'لا توجد علاقات',
    noRelationshipsHint: 'اختر ملفاً وأضف أول علاقة فعالة له.',
    actions: 'الإجراءات',
    assignRole: 'إسناد دور',
    contractsTitle: 'العقود',
    contractsHint: 'العقود المحكومة والمحفوظة لملف القوى العاملة المحدد.',
    noContracts: 'لا توجد عقود',
    noContractsHint: 'أنشئ عقداً لإحدى علاقات الملف المحدد.',
    profileRelationship: 'الملف / العلاقة',
    termsFingerprint: 'بصمة الشروط',
    compensationWorkbenchTitle: 'مساحة عمل خطة التعويض',
    compensationWorkbenchHint: 'تغييرات الخطة ذات إصدارات، والحساب نواة تفسيرية للقراءة فقط.',
    addVersion: 'إضافة إصدار',
    addComponent: 'إضافة مكوّن',
    noPlan: 'لا توجد خطة في مساحة العمل',
    noPlanHint: 'أنشئ خطة لإضافة إصدار فعال ومكوّنات التعويض.',
    plan: 'الخطة',
    planVersion: 'إصدار الخطة',
    versions: 'الإصدارات',
    component: 'المكوّن',
    kindMethod: 'النوع / الطريقة',
    multiplier: 'المضاعف',
    latestCalculationTitle: 'أحدث حساب',
    latestCalculationHint: 'لا ينشئ الحساب قيود رواتب أو دفتر أستاذ؛ استخدم البصمة لإثبات المدخلات والنتيجة.',
    noCalculation: 'لا توجد نتيجة حساب',
    noCalculationHint: 'احسب خطة تعويض فعالة لملف وعلاقة.',
    grossEarnings: 'الإجمالي المكتسب',
    netAmount: 'المبلغ الصافي',
    fingerprint: 'البصمة',
    newWorkforceProfileTitle: 'ملف قوى عاملة جديد',
    newWorkforceProfileHint: 'اربط شخصاً حقيقياً نشطاً واحداً فقط من دليل الأشخاص المرجعي في فارزا.',
    createProfile: 'إنشاء ملف',
    profileCreated: 'تم إنشاء ملف القوى العاملة.',
    addRelationshipTitle: 'إضافة علاقة',
    addRelationshipFor: 'أنشئ علاقة فعالة لـ {{name}}.',
    relationshipAdded: 'تمت إضافة العلاقة.',
    assignRoleTitle: 'إسناد دور',
    assignRoleFor: 'أسند دوراً فعالاً إلى العلاقة {{number}}.',
    roleAssigned: 'تم إسناد الدور.',
    newWorkforceContractTitle: 'عقد قوى عاملة جديد',
    newWorkforceContractHint: 'تُحفظ الشروط الأولية كلقطة JSON مرجعية غير قابلة للتغيير.',
    createContract: 'إنشاء عقد',
    contractCreated: 'تم إنشاء العقد.',
    newCompensationPlanTitle: 'خطة تعويض جديدة',
    newCompensationPlanHint: 'أنشئ رأس الخطة قبل إضافة إصدار فعال ومكوّنات الحساب.',
    createPlan: 'إنشاء خطة',
    planCreated: 'تم إنشاء خطة التعويض.',
    addPlanVersionTitle: 'إضافة إصدار لخطة التعويض',
    planVersionAdded: 'تمت إضافة إصدار الخطة.',
    addCompensationComponentTitle: 'إضافة مكوّن تعويض',
    componentHint: 'قد تكون المكوّنات مكاسب أو خصومات ثابتة أو زمنية أو بالقطعة.',
    componentAdded: 'تمت إضافة مكوّن التعويض.',
    calculateCompensationTitle: 'حساب التعويض',
    calculateCompensationHint: 'الحساب للقراءة فقط وحتمي، ويجب أن تتضمن الخطة المحددة إصداراً فعالاً نشطاً.',
    compensationCalculated: 'تم حساب التعويض ولم يُنشأ أي قيد محاسبي.',
    relationshipNumber: 'رقم العلاقة',
    effectiveTo: 'فعال حتى',
    canonicalPersonId: 'معرف الشخص المرجعي',
    personGuid: 'معرف GUID للشخص',
    workforceNumber: 'رقم القوى العاملة',
    displayName: 'اسم العرض',
    roleCode: 'رمز الدور',
    roleCodePlaceholder: 'EMPLOYEE، CONTRACTOR، RESELLER',
    contractNumber: 'رقم العقد',
    startsOn: 'يبدأ في',
    endsOn: 'ينتهي في',
    termsSnapshot: 'لقطة الشروط',
    planCode: 'رمز الخطة',
    planName: 'اسم الخطة',
    currencyAssetCode: 'رمز العملة / الأصل',
    currencyPrecision: 'دقة العملة',
    componentCode: 'رمز المكوّن',
    componentName: 'اسم المكوّن',
    method: 'الطريقة',
    expenseAccountId: 'معرف حساب المصروف',
    liabilityAccountId: 'معرف حساب الالتزام',
    profile: 'الملف',
    profileId: 'معرف الملف',
    activePlanId: 'معرف الخطة النشطة',
    relationshipId: 'معرف العلاقة',
    approvalCutoff: 'حد الموافقة الزمني',
    approvedSources: 'مصادر الوقت أو القطعة المعتمدة',
    sourcesArrayError: 'يجب أن تكون المصادر مصفوفة JSON.',
    sourceObjectError: 'يجب أن يكون المصدر {{index}} كائناً.',
    sourceMethodError: 'طريقة المصدر {{index}} غير صالحة.',
    compensationKind: { Earning: 'مكسب', Deduction: 'خصم' },
    compensationMethod: { Fixed: 'ثابت', Time: 'زمني', Piece: 'بالقطعة' },
    date: 'التاريخ',
    kind: 'النوع',
    quantity: 'الكمية',
    status: 'الحالة',
    version: 'الإصدار',
    action: 'الإجراء',
    amount: 'المبلغ',
    asset: 'الأصل',
    relationship: 'العلاقة',
    contract: 'العقد',
    period: 'الفترة',
    open: 'فتح',
    openEnded: 'مفتوح',
    submit: 'إرسال',
    approve: 'اعتماد',
    reverse: 'عكس',
    activate: 'تفعيل',
    close: 'إغلاق',
    reversalReason: 'سبب العكس',
    timeTitle: 'أدلة الوقت والحضور والإجازة ومخرجات العمل',
    timeDescription: 'تبقى الأدلة التشغيلية المحفوظة مسودة حتى إرسالها واعتمادها.',
    noWorkEvidence: 'لا توجد أدلة عمل',
    noWorkEvidenceHint: 'سجّل وردية أو حضوراً أو كشف وقت أو إجازة أو مخرجات أو أمر عمل أو وقت مشروع.',
    recordWork: 'تسجيل دليل العمل',
    recordWorkHint: 'اختر نوع الدليل؛ تُفرض وحدته المعتمدة تلقائياً.',
    noRelationship: 'لا توجد علاقة',
    noRelationshipWorkHint: 'أنشئ علاقة قوى عاملة قبل تسجيل دليل العمل.',
    projectOptional: 'معرّف المشروع (اختياري)',
    workOrderOptional: 'معرّف أمر العمل (اختياري)',
    costCenterOptional: 'معرّف مركز التكلفة (اختياري)',
    recordDraft: 'تسجيل المسودة',
    workKind: {
      Shift: 'وردية', Attendance: 'حضور', Timesheet: 'كشف وقت', Leave: 'إجازة',
      WorkOutput: 'مخرجات العمل', WorkOrder: 'أمر عمل', ProjectTime: 'وقت المشروع',
    },
    workUnit: {
      Hours: 'ساعات', Days: 'أيام', Shifts: 'ورديات', Units: 'وحدات', Jobs: 'أعمال', Kilometers: 'كيلومترات',
    },
    createClaim: 'إنشاء مطالبة مالية',
    createClaimHint: 'المصروفات قابلة للسداد؛ وتتطلب السلف والمسحوبات والقروض حداً وجدول استرداد صريحين.',
    noRelationshipClaimHint: 'أنشئ علاقة قوى عاملة قبل تسجيل مطالبة.',
    claimKind: {
      ExpenseReimbursement: 'سداد مصروف', Advance: 'سلفة',
      CommissionDraw: 'مسحوب عمولة', Loan: 'قرض',
    },
    recoverySchedule: 'جدول الاسترداد',
    recoveryCap: 'الحد الأقصى للاسترداد في كل تسوية',
    recoveryDue: 'تاريخ الاستحقاق',
    installmentAmount: 'مبلغ القسط',
    removeInstallment: 'حذف القسط',
    addInstallment: 'إضافة قسط',
    recoveryTotal: 'إجمالي الجدول: {{total}} / مبلغ المطالبة: {{amount}}',
    createClaimDraft: 'إنشاء مسودة المطالبة',
    jurisdictionTitle: 'حالة حزمة الاختصاص',
    jurisdictionDescription: 'تحقق من حزمة السياسة الفعالة والمسنَدة دون تضمين افتراضات قانونية في القوى العاملة.',
    jurisdictionCode: 'رمز الاختصاص',
    effectiveOn: 'سارية في',
    checkJurisdiction: 'فحص الحزمة المهيأة',
    jurisdictionMissing: 'لا توجد حزمة اختصاص فعالة مهيأة لهذا الرمز والتاريخ.',
    jurisdictionConfigured: 'الحزمة {{pack}} بالإصدار {{version}} مهيأة وفعالة.',
    jurisdictionNoAssumptions: 'حزمة المثال عامة ولا تقدم ادعاءات قانونية ضريبية أو عمالية أو متعلقة بالرواتب.',
    jurisdictionUnchecked: 'أدخل رمز الاختصاص والتاريخ للتحقق. حزمة EXAMPLE المضمنة غير تشريعية.',
    reportsTitle: 'تقارير القوى العاملة',
    reportsHint: 'حقائق حتمية خاصة بالمستأجر مع استشهادات المصادر.',
    noReportFacts: 'لا توجد حقائق للتقرير.',
    aiTitle: 'مساعدة ذكاء اصطناعي محكومة بالصلاحيات',
    aiHint: 'يشرح الذكاء الاصطناعي الحقائق المستشهد بها فقط؛ وتمتلك الوحدات الحتمية كل مبلغ وترحيل.',
    aiTool: 'الأداة',
    aiQuestion: 'السؤال',
    runAi: 'تشغيل أداة الذكاء الاصطناعي',
    aiUnavailable: 'أداة الذكاء الاصطناعي غير متاحة أو رُفض الإذن.',
    queryWorkforce: 'استعلام القوى العاملة',
    explainCommission: 'شرح العمولة',
    draftDisputeResponse: 'مسودة رد النزاع',
    registerHint: 'سجلات مستأجر محفوظة؛ تفرض التحولات المالية الإصدارات الحالية والصلاحيات.',
    noProfileRecords: 'لا توجد سجلات تطابق ملف القوى العاملة المحدد.',
    partnerAgreementsTitle: 'اتفاقيات الشركاء',
    noPartnerAgreements: 'لا توجد اتفاقيات شركاء',
    agreement: 'الاتفاقية',
    basis: 'الأساس',
    rate: 'المعدل',
    commissionEventsTitle: 'أحداث العمولة',
    noCommissionEvents: 'لا توجد أحداث عمولة',
    commissionAmount: 'العمولة',
    salesAttributionTitle: 'إسناد المبيعات',
    noSalesAttribution: 'لا يوجد إسناد مبيعات',
    grossSales: 'إجمالي المبيعات',
    collected: 'المحصّل',
    attribution: 'الإسناد',
    partnerAgreementSetup: 'إعداد اتفاقية الشريك',
    partnerAgreementSetupHint: 'شروط العمولة مؤرخة وذات إصدارات ويتم تفعيلها بصورة مستقلة.',
    noContractAgreementHint: 'أنشئ عقداً محكوماً قبل إعداد شروط العمولة.',
    noContractAvailable: 'لا يوجد عقد محفوظ',
    agreementCode: 'رمز الاتفاقية',
    agreementKind: 'نوع الاتفاقية',
    operatingModel: 'نموذج التشغيل',
    commissionBasis: 'أساس العمولة',
    ratePercent: 'نسبة المعدل',
    attributionPercent: 'نسبة الإسناد',
    effectiveFrom: 'ساري من',
    policyJson: 'سياسة JSON',
    createAgreementDraft: 'إنشاء مسودة الاتفاقية',
    partnerKind: { SalesAgent: 'وكيل مبيعات', Reseller: 'موزّع', ReferralPartner: 'شريك إحالة' },
    operatingModelValue: {
      Agent: 'وكيل', BuySell: 'شراء / بيع', Marketplace: 'سوق',
      Consignment: 'أمانة', Referral: 'إحالة', WhiteLabel: 'علامة بيضاء',
    },
    commissionBasisValue: {
      OrderConfirmed: 'تأكيد الطلب', Shipment: 'الشحن', InvoiceIssued: 'إصدار الفاتورة',
      CollectedRevenue: 'الإيراد المحصّل', GrossMargin: 'الهامش الإجمالي', Referral: 'الإحالة',
    },
    revenueEvidenceTitle: 'أدلة العمولة والمبيعات',
    revenueEvidenceHint: 'تبقى حقائق العمولة والمبيعات مسودات حتى تتم الموافقة عليها بصورة مستقلة.',
    noRelationshipRevenueHint: 'أنشئ علاقة قوى عاملة قبل تسجيل أدلة الإيراد.',
    commissionEvent: 'حدث العمولة',
    activeAgreement: 'الاتفاقية النشطة',
    noActiveAgreement: 'لا توجد اتفاقية نشطة',
    recognizedRevenue: 'الإيراد المعترف به',
    collectedRevenue: 'الإيراد المحصّل',
    directCost: 'التكلفة المباشرة',
    recordCommissionDraft: 'تسجيل مسودة العمولة',
    recordSalesDraft: 'تسجيل مسودة المبيعات',
    clawBack: 'استرداد العمولة',
    clawbackReason: 'سبب استرداد العمولة',
    claimsTitle: 'المصروفات والسلف والقروض',
    noFinancialClaims: 'لا توجد مطالبات مالية',
    outstanding: 'المتبقي',
    managerApprove: 'موافقة المدير',
    financeApprove: 'موافقة المالية',
    settlementsPostingTitle: 'التسويات وإعداد الترحيل',
    settlementNumber: 'رقم التسوية',
    periodFrom: 'بداية الفترة',
    periodTo: 'نهاية الفترة',
    netPayable: 'صافي المستحق',
    paymentEvidenceTitle: 'أدلة الدفع',
    noPaymentEvidence: 'لا توجد أدلة دفع',
    occurred: 'وقت الحدوث',
    reversal: 'العكس',
    reverseEvidence: 'عكس الدليل',
    calculateSettlementTitle: 'حساب التسوية',
    calculateSettlementHint: 'يستخدم جميع حقائق العمل والعمولة والاسترداد المعتمدة ضمن الفترة للعلاقة والأصل المحددين.',
    noRelationshipSettlementHint: 'أنشئ علاقة قبل حساب التسوية.',
    eligibleSources: 'المصادر المعتمدة المؤهلة: {{count}}',
    calculatePersistedSettlement: 'حساب التسوية المحفوظة',
    markStale: 'وضع علامة قديم',
    preparePostingIntegrations: 'إعداد الترحيل والتكاملات',
    recordPaymentEvidence: 'تسجيل دليل الدفع',
    paidAmount: 'المبلغ المدفوع',
    createAdjustment: 'إنشاء تعديل',
    adjustmentReason: 'سبب التعديل',
    adjustmentNumber: 'رقم تسوية التعديل',
    disputesAdjustmentsTitle: 'نزاعات العاملين وتعديلاتهم',
    disputeNumber: 'النزاع',
    subject: 'الموضوع',
    beginManagerReview: 'بدء مراجعة المدير',
    escalateFinanceHr: 'التصعيد إلى المالية / الموارد البشرية',
    resolve: 'حل النزاع',
    resolutionEvidence: 'دليل الحل والتعديل',
    portalEyebrow: 'القوى العاملة',
    myWorkforce: 'قواي العاملة',
    portalIdentityHint: 'سجلات القوى العاملة المرتبطة بهويتك الموثّقة.',
    noWorkforceLinks: 'لا توجد روابط قوى عاملة',
    noWorkforceLinksHint: 'لم تربط أي مؤسسة نشطة ملف قوى عاملة بهويتك.',
    portalDescription: 'سجلات قواك العاملة الخاصة لدى {{organization}}. تبقى المدفوعات والمحاسبة مملوكة لوحدات المستأجر المسؤولة.',
    organization: 'المؤسسة',
    portalSummary: 'ملخص القوى العاملة',
    contracts: 'العقود',
    latestNetEarnings: 'أحدث صافي أرباح',
    latestSettlement: 'أحدث تسوية',
    portalActionFailed: 'تعذر إكمال الإجراء. راجع إصدار السجل وحاول مجدداً.',
    relationshipsContracts: 'العلاقات والعقود',
    verifiedProfile: 'الملف {{number}} · سجلات موثّقة للقراءة فقط',
    noWorkforceRelationships: 'لا توجد علاقات قوى عاملة',
    noWorkforceRelationshipsHint: 'لم تُنشر أي علاقة أو عقد لهويتك.',
    record: 'السجل',
    effectivePeriod: 'فترة السريان',
    roles: 'الأدوار',
    acknowledge: 'إقرار',
    acceptOffer: 'قبول العرض',
    earningsCommissions: 'الأرباح والعمولات',
    earningsHint: 'دليل حساب نهائي؛ تبقى المحاسبة مملوكة للرواتب والدائنين.',
    noEarnings: 'لا توجد كشوف أرباح',
    noEarningsHint: 'ستظهر الأرباح وكشوف العمولات المعتمدة هنا.',
    periodEvent: 'الفترة / الحدث',
    grossBasis: 'الإجمالي أو الأساس',
    deductions: 'الاستقطاعات',
    netCommission: 'الصافي / العمولة',
    commission: 'العمولة',
    settlements: 'التسويات',
    settlementsHint: 'حالة القطع صريحة؛ ولا تتغير الكشوف النهائية إلا بالتعديلات.',
    noSettlements: 'لا توجد تسويات',
    noSettlementsHint: 'ستظهر كشوف التسوية المعتمدة هنا.',
    credits: 'الدائن',
    debits: 'المدين',
    net: 'الصافي',
    cutoff: 'القطع',
    payment: 'الدفع',
    expensesAdvancesLoans: 'المصروفات والسلف والقروض',
    privateClaimsHint: 'تظهر فقط المطالبات المرتبطة بملف القوى العاملة الموثّق الخاص بك.',
    noExpensesRecoveries: 'لا توجد مصروفات أو استردادات',
    noExpensesRecoveriesHint: 'ستظهر المصروفات والسلف والقروض التي أرسلتها هنا.',
    documents: 'المستندات',
    documentsHint: 'المستندات الآمنة المنشورة في هويتك VII.',
    noDocuments: 'لا توجد مستندات',
    noDocumentsHint: 'ستظهر مستندات القوى العاملة المنشورة هنا.',
    openDocument: 'فتح',
    submissionsDisputes: 'التقديمات والنزاعات',
    submissionsHint: 'قدّم أدلتك. يراجعها المديرون والمالية قبل أي أثر مالي.',
    noRelationshipAvailable: 'لا توجد علاقة متاحة',
    noRelationshipAvailableHint: 'تلزم علاقة نشطة أو مدعوّة قبل تقديم الأدلة.',
    timeOrLeave: 'الوقت أو الإجازة',
    type: 'النوع',
    time: 'الوقت',
    leave: 'الإجازة',
    expense: 'المصروف',
    settlementDispute: 'نزاع تسوية',
    settlement: 'التسوية',
    noSettlementAvailable: 'لا توجد تسوية متاحة',
    reviewQuestion: 'ما الذي ينبغي مراجعته؟',
    openDispute: 'فتح نزاع',
    noDisputes: 'لا توجد نزاعات',
    noDisputesHint: 'ستظهر نزاعاتك وحلولها المدعومة بالتعديلات هنا.',
    dispute: 'النزاع',
    category: 'الفئة',
    resolution: 'الحل',
    acceptResolution: 'قبول الحل',
  },
  app: {
    ...en.app,
    name: 'فارزا',
    tagline: 'نظام ذكي لإدارة الأعمال',
    product: 'المحاسبة والمخزون',
    workspace: 'مساحة العمل المالية',
    skipToContent: 'الانتقال إلى المحتوى',
    poweredBy: 'بدعم من Exirsoft',
  },
  common: {
    actions: 'الإجراءات',
    add: 'إضافة',
    all: 'الكل',
    approve: 'موافقة',
    back: 'رجوع',
    cancel: 'إلغاء',
    close: 'إغلاق',
    create: 'إنشاء',
    date: 'التاريخ',
    delete: 'حذف',
    edit: 'تعديل',
    export: 'تصدير',
    filters: 'عوامل التصفية',
    more: 'المزيد',
    new: 'جديد',
    next: 'التالي',
    previous: 'السابق',
    refresh: 'تحديث',
    reset: 'إعادة تعيين',
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saveDraft: 'حفظ كمسودة',
    search: 'بحث',
    select: 'اختر…',
    status: 'الحالة',
    total: 'الإجمالي',
    submit: 'إرسال',
    view: 'عرض',
    viewAll: 'عرض الكل',
    notAvailable: 'غير متاح',
    loading: 'جارٍ تحميل مساحة العمل…',
    retry: 'إعادة المحاولة',
    noResults: 'لا توجد نتائج مطابقة',
    lastUpdated: 'آخر تحديث',
    currency: 'العملة',
    amount: 'المبلغ',
    owner: 'المالك',
    reference: 'المرجع',
    description: 'الوصف',
    details: 'التفاصيل',
    activate: 'تفعيل',
    deactivate: 'إلغاء التفعيل',
    remove: 'إزالة',
    reject: 'رفض',
    yes: 'نعم',
    no: 'لا',
  },
  journals: {
    ...en.journals,
    functionalDebit: 'مدين بالعملة الوظيفية',
    functionalCredit: 'دائن بالعملة الوظيفية',
  },
  nav: {
    overview: 'نظرة عامة',
    workspace: 'مساحة العمل',
    operations: 'العمليات',
    accounting: 'المحاسبة',
    baseData: 'البيانات الأساسية',
    dashboard: 'لوحة المعلومات',
    journals: 'قيود اليومية',
    ledger: 'دفتر الأستاذ العام',
    accounts: 'دليل الحسابات',
    persons: 'الأشخاص',
    customers: 'العملاء',
    suppliers: 'المورّدون',
    interOrganizationIdentity: 'الهوية بين المؤسسات',
    exchangeRates: 'أسعار الصرف',
    currencyAdministration: 'إدارة العملات',
    tax: 'الضرائب',
    fixedAssets: 'الأصول الثابتة',
    budgeting: 'الموازنات',
    costing: 'التكاليف والربحية',
    projects: 'المشاريع والعقود',
    personalFinance: 'المالية الشخصية والعائلية',
    inventory: 'المخزون',
    reports: 'التقارير',
    receivables: 'الحسابات المدينة',
    payables: 'الحسابات الدائنة',
    treasury: 'النقد والبنوك',
    purchasing: 'المشتريات',
    purchaseOrders: 'أوامر الشراء',
    sales: 'المبيعات',
    salesOrders: 'أوامر البيع',
    automation: 'أتمتة المستندات',
    workforce: 'القوى العاملة',
    payroll: 'كشوف الرواتب',
    settings: 'الإعدادات',
    access: 'المستخدمون والصلاحيات',
    shortLedger: 'الأستاذ العام',
    collapse: 'طيّ التنقل',
    expand: 'توسيع التنقل',
    openMenu: 'فتح التنقل',
    closeMenu: 'إغلاق التنقل',
    quickNav: 'التنقل السريع',
    homepage: 'الصفحة الرئيسية لفارزا',
  },
  professionalServices: {
    title: 'عمليات الخدمات المهنية',
    subtitle: 'الوقت والمصروفات القابلة للاسترداد وفوترة العمل الجاري وتكلفة الموظفين والربحية الموثوقة لهذا الارتباط.',
    tabs: {
      performance: 'الأداء',
      time: 'الوقت',
      expenses: 'المصروفات',
      wip: 'العمل الجاري والفواتير',
      'staff-allocations': 'تكلفة الموظفين',
    },
    timeTitle: 'الوقت حسب المشروع',
    timeHint: 'يُثبت الوقت المملوك للمؤسسة بسعر الارتباط ولا يمكن فوترته إلا مرة واحدة.',
    noTime: 'لا توجد قيود وقت لهذا الارتباط',
    date: 'التاريخ',
    activity: 'النشاط',
    teamMember: 'عضو الفريق',
    hours: 'الساعات',
    value: 'القيمة القابلة للفوترة',
    status: 'الحالة',
    actions: 'الإجراءات',
    void: 'إبطال',
    timeVoided: 'تم إبطال قيد الوقت غير المفوتر.',
    expensesTitle: 'استرداد المصروفات',
    expensesHint: 'تكاليف مشروع مدعومة بإيصال ودليل قيد مستحق للموظف وأهلية ثابتة لإعادة الفوترة.',
    noExpenses: 'لا توجد مصروفات قابلة للاسترداد لهذا الارتباط',
    category: 'الفئة',
    amount: 'المبلغ',
    clientRecovery: 'الاسترداد من العميل',
    evidence: 'الدليل المحاسبي',
    wipTitle: 'فواتير العمل الجاري',
    wipHint: 'وقت ومصروفات ومراحل وخصم وضريبة وعملة ودليل سعر صرف ثابت مرتبط بالمدينين.',
    noWip: 'لا توجد فواتير عمل جارٍ لهذا الارتباط',
    invoice: 'الفاتورة',
    revenue: 'الإيراد قبل الضريبة',
    tax: 'الضريبة',
    due: 'تاريخ الاستحقاق',
    staffTitle: 'تخصيصات تكلفة الموظفين',
    staffHint: 'تُخصص تكلفة الرواتب المرحلة مرة واحدة بالضبط لأعضاء فريق الارتباط الفعالين.',
    noStaff: 'لا توجد تخصيصات مرحلة لتكلفة الموظفين لهذا الارتباط',
    period: 'الفترة',
    method: 'الطريقة',
    payrollRun: 'دورة الرواتب',
    performanceTitle: 'الاستخدام والتحقق والربحية',
    performanceHint: 'مؤشرات آمنة للعملة مشتقة من أدلة المشاريع والرواتب والمدينين ودفتر الأستاذ المرحلة.',
    utilization: 'الاستخدام',
    realization: 'تحقق الإيراد',
    unbilledWip: 'عمل جارٍ غير مفوتر',
    profit: 'ربح المشروع',
    receivablesAging: 'أعمار المدينين',
    retainerBalance: 'رصيد الأتعاب المقدمة',
    billedRevenue: 'الإيراد المفوتر',
    postedExpenses: 'المصروفات المرحلة',
    expenseRecovery: 'المصروفات المستردة',
    allocatedStaffCost: 'تكلفة الموظفين المخصصة',
    authoritativeFootnote: 'سجلات موثوقة بالعملة المعروضة',
  },
  context: {
    ...en.context,
    tenant: 'المؤسسة',
    branch: 'الفرع',
    allBranches: 'كل الفروع',
    period: 'الفترة المالية',
    functionalCurrency: 'العملة الوظيفية',
    currencyMenu: 'فتح تفاصيل العملة',
    enabledCurrencies: 'العملات المفعّلة',
    disabledCurrency: 'غير مفعّلة',
    viewExchangeRates: 'عرض أسعار الصرف',
    language: 'اللغة',
    english: 'الإنجليزية',
    persian: 'الفارسية',
    arabic: 'العربية',
    notifications: 'الإشعارات',
    pendingApprovals: '{{count}} موافقات معلّقة',
    userMenu: 'فتح قائمة المستخدم',
    openPeriod: 'فترة مفتوحة',
    softClosedPeriod: 'فترة مغلقة مؤقتًا',
    hardClosedPeriod: 'فترة مغلقة نهائيًا',
    scopeUnavailable: 'نطاق مساحة العمل غير متاح',
    switchingTenant: 'جارٍ تبديل المؤسسة…',
    scope: 'نطاق مساحة العمل',
    editScope: 'تغيير نطاق مساحة العمل',
  },
  auth: {
    ...en.auth,
    loginTitle: 'تسجيل الدخول إلى فارزا',
    loginSubtitle: 'استخدم حسابك لفتح مساحة عمل مالية مصرح بها.',
    username: 'اسم المستخدم',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    verifyTitle: 'تحقق من هويتك',
    verifySubtitle: 'أكمل خطوة التحقق الإضافية المطلوبة لهذا الحساب.',
    verificationMethod: 'طريقة التحقق',
    verificationCode: 'رمز التحقق',
    authenticatorHint: 'أدخل الرمز الحالي المكوّن من ستة أرقام من تطبيق المصادقة.',
    deliveredCodeHint: 'اطلب رمزًا ثم أدخل الأرقام الستة التي تصلك.',
    sendCode: 'إرسال رمز التحقق',
    codeSent: 'تم إرسال رمز تحقق جديد.',
    orContinueWith: 'أو المتابعة باستخدام',
    signIn: 'تسجيل الدخول',
    signingIn: 'جارٍ تسجيل الدخول…',
    verify: 'تحقق',
    mfaPrompt: 'هذا الحساب يتطلب مصادقة متعددة العوامل.',
    credentialsError: 'لم يتم قبول اسم المستخدم أو كلمة المرور أو رمز التحقق.',
    lockedError: 'هذا الحساب مقفل مؤقتًا. حاول لاحقًا.',
    connectionError: 'تعذّر على فارزا الاتصال بخدمة المصادقة. حاول مرة أخرى.',
    captchaError: 'تعذّر التحقق من أن الطلب صادر عن شخص. أعد تحميل الصفحة وحاول مجددًا.',
    captchaUnavailableError: 'خدمة التحقق البشري غير متاحة حاليًا. حاول بعد قليل.',
    selectTenantTitle: 'اختر مساحة عمل',
    selectTenantSubtitle: 'اختر إحدى المؤسسات المرتبطة بحسابك.',
    selecting: 'جارٍ الفتح…',
    openTenant: 'فتح مساحة العمل',
    noTenants: 'لا توجد عضوية فعّالة في مؤسسة لهذا الحساب.',
    tenantError: 'تعذّر اختيار المؤسسة. لم تتغير مساحة العمل السابقة.',
    logout: 'تسجيل الخروج',
    mfaSession: 'جلسة محمية بالمصادقة المتعددة',
    passwordSession: 'جلسة بكلمة مرور',
    forgotLink: 'هل نسيت كلمة المرور؟',
    createAccount: 'التسجيل بدعوة',
    forgotTitle: 'إعادة تعيين كلمة المرور',
    forgotSubtitle: 'أدخل بريد حسابك وسنرسل رابط إعادة تعيين آمنًا صالحًا لمرة واحدة.',
    recoverySentTitle: 'تحقق من بريدك الإلكتروني',
    recoverySentBody: 'إذا كان العنوان مرتبطًا بحساب، فسيصله رابط إعادة تعيين كلمة المرور.',
    recoverySentHint: 'حرصًا على أمانك، تنتهي صلاحية الرابط ولا يعمل إلا مرة واحدة.',
    developmentRecovery: 'نسخة التطوير:',
    resetNow: 'إعادة التعيين الآن',
    backToSignIn: 'العودة إلى تسجيل الدخول',
    sendRecovery: 'إرسال رابط إعادة التعيين',
    recoveryFailed: 'تعذر إكمال طلب إعادة التعيين. حاول مرة أخرى.',
    recoveryRateLimited: 'محاولات كثيرة جدًا. انتظر قليلًا ثم حاول مجددًا.',
    resetTitle: 'اختر كلمة مرور جديدة',
    resetSubtitle: 'استخدم الرمز الآمن من بريدك واختر كلمة مرور جديدة قوية.',
    recoveryToken: 'رمز إعادة التعيين',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور الجديدة',
    passwordLengthHint: 'استخدم {{count}} حرفًا على الأقل.',
    passwordTooShort: 'استخدم {{count}} حرفًا على الأقل.',
    passwordMismatch: 'كلمتا المرور غير متطابقتين.',
    resetInvalid: 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية أو مستخدم مسبقًا.',
    resetFailed: 'تعذر إعادة تعيين كلمة المرور. حاول مرة أخرى.',
    resetAction: 'تعيين كلمة المرور الجديدة',
    requestNewLink: 'طلب رابط إعادة تعيين جديد',
    resetCompleteTitle: 'تم تحديث كلمة المرور',
    resetCompleteBody: 'كلمة مرورك الجديدة جاهزة للاستخدام.',
    resetSessionsEnded: 'تم إنهاء الجلسات الأخرى. سجل الدخول مجددًا بكلمة المرور الجديدة.',
    adminConsole: 'إدارة النظام',
    invitation_invalid: 'هذه الدعوة غير صالحة أو منتهية أو ملغاة أو مستخدمة.',
  },
  administration: {
    ...en.administration,
    systemUsers: 'مستخدمو النظام',
    systemUsersHint: 'حدد الحسابات التي يمكنها إنشاء دعوات التسجيل. يملك مسؤولو النظام هذا الحق دائمًا.',
    noSystemUsers: 'لا يوجد مستخدمون',
    noSystemUsersHint: 'تظهر الحسابات هنا بعد إنشائها.',
    user: 'المستخدم',
    canInvite: 'يمكنه إنشاء الدعوات',
    superAdminImplicit: 'مسموح دائمًا',
    invitationAllowed: 'مسموح',
    invitationNotAllowed: 'غير مسموح',
    invitationPermissionFailed: 'تعذر تغيير صلاحية إنشاء الدعوات.',
  },
  invitations: {
    ...en.invitations,
    title: 'دعوات التسجيل',
    subtitle: 'أنشئ أو ألغِ الرموز أحادية الاستخدام التي تسمح بإنشاء حسابات فارزا الجديدة.',
    back: 'عودة',
    governance: 'حوكمة الحسابات',
    createTitle: 'إنشاء دعوة',
    createHint: 'يسمح كل رمز بحساب واحد فقط. شاركه مع الشخص المقصود وحده.',
    oneTimeTitle: 'احفظ هذا الرمز الآن',
    oneTimeHint: 'هذه المرة الوحيدة التي يظهر فيها الرمز كاملًا. لا يحفظ فارزا سوى بصمته أحادية الاتجاه.',
    rawCode: 'رمز الدعوة',
    registrationLink: 'رابط التسجيل القابل للمشاركة',
    copied: 'تم النسخ',
    copyCode: 'نسخ الرمز',
    copyLink: 'نسخ الرابط',
    copyFailed: 'تعذر النسخ تلقائيًا. حدد القيمة وانسخها يدويًا.',
    savedCode: 'حفظت الرمز',
    label: 'التسمية',
    labelHint: 'سياق اختياري لا يراه إلا مديرو الدعوات.',
    expiresIn: 'تنتهي بعد أيام',
    expiryInvalid: 'أدخل عددًا صحيحًا من 1 إلى {{count}} يومًا.',
    createAction: 'إنشاء الدعوة',
    createFailed: 'تعذر إنشاء الدعوة.',
    revokeFailed: 'تعذر إلغاء الدعوة.',
    never: 'أبدًا',
    registerTitle: 'سجل الدعوات',
    registerHint: 'بعد الإنشاء لا يبقى إلا تلميح الرمز الآمن، ولا يمكن استرجاع الرمز كاملًا.',
    emptyTitle: 'لا توجد دعوات بعد',
    emptyHint: 'أنشئ أول دعوة تسجيل أحادية الاستخدام.',
    codeHint: 'تلميح الرمز',
    created: 'تاريخ الإنشاء',
    expires: 'تاريخ الانتهاء',
    unlabelled: 'دعوة بلا تسمية',
    status: {
      active: 'نشطة',
      used: 'مستخدمة',
      revoked: 'ملغاة',
      expired: 'منتهية',
      unknown: 'غير متاحة',
    },
    revoke: 'إلغاء',
  },
  vii: {
    ...en.vii,
    registerSubtitle: 'استخدم دعوتك لإنشاء هوية تبقى ملكك لدى كل منشأة على فارزا.',
    invitationCode: 'رمز الدعوة',
    invitationHint: 'التسجيل خاص. أدخل الرمز أحادي الاستخدام الذي تمت مشاركته معك.',
    invitationInvalid: 'هذه الدعوة غير صالحة أو منتهية أو ملغاة أو مستخدمة.',
  },
  settings: {
    ...en.settings,
    rialsPerToman: 'عدد الريالات لكل تومان',
    rialTomanFactorHint: 'أدخل عدد الريالات التي تعادل تومانًا واحدًا وفق سياسة هذه المؤسسة.',
    currentRialTomanFactor: 'سياسة الفئة الحالية: 1 تومان = {{factor}} ريال',
    rialTomanFactorInvalid: 'يجب أن يكون معامل الريال والتومان أكبر من صفر.',
    title: 'الإعدادات',
    subtitle: 'إعدادات المؤسسة للغة والعملات وأمان الحساب.',
    localization: 'اللغة والعرض',
    localizationHint: 'اختر لغة الواجهة والتقويم والمظهر.',
    financial: 'الإعدادات المالية',
    security: 'الأمان والجلسات',
    voice: 'الذكاء الاصطناعي والصوت',
    device: 'هذا الجهاز',
    backup: 'النسخ الاحتياطي',
    defaultLanguage: 'اللغة الافتراضية',
    displayCalendar: 'تقويم العرض',
    gregorian: 'الميلادي',
    solarHijri: 'الهجري الشمسي',
    islamicHijri: 'الهجري القمري',
    theme: 'المظهر',
    calendarFollowsLanguage: 'اختيار التقويم مستقل عن لغة الواجهة ومحفوظ على هذا الجهاز.',
    digitSystem: 'أرقام العرض',
    latinDigits: 'لاتينية (0123456789)',
    persianDigits: 'فارسية (۰۱۲۳۴۵۶۷۸۹)',
    arabicIndicDigits: 'عربية هندية (٠١٢٣٤٥٦٧٨٩)',
    iranCurrencyDisplay: '\u0639\u0631\u0636 \u0627\u0644\u0639\u0645\u0644\u0629 \u0627\u0644\u0625\u064a\u0631\u0627\u0646\u064a\u0629',
    iranCurrencyDisplayRial: '\u0627\u0644\u0631\u064a\u0627\u0644',
    iranCurrencyDisplayToman: '\u0627\u0644\u062a\u0648\u0645\u0627\u0646',
    iranCurrencyDisplayBoth: '\u0627\u0644\u0631\u064a\u0627\u0644 \u0648\u0627\u0644\u062a\u0648\u0645\u0627\u0646',
    iranCurrencyDisplayHint: '\u064a\u062a\u063a\u064a\u0631 \u0627\u0644\u0639\u0631\u0636 \u0641\u0642\u0637\u061b \u062a\u0628\u0642\u0649 \u0641\u0626\u0629 \u0627\u0644\u0645\u062d\u0627\u0633\u0628\u0629 \u0627\u0644\u0645\u062e\u0632\u0646\u0629 \u0648\u0627\u0644\u0645\u0628\u0627\u0644\u063a \u0627\u0644\u0623\u0635\u0644\u064a\u0629 \u062f\u0648\u0646 \u062a\u063a\u064a\u064a\u0631.',
    dualCalendarHint: 'تُحفظ التواريخ بصيغة ISO المعيارية، وتعرض النماذج أيضًا ما يقابلها في التقويمات الأخرى.',
  },
  voice: {
    ...en.voice,
    title: 'الأوامر الصوتية المحلية',
    startListening: 'بدء أمر صوتي محلي',
    stopListening: 'إيقاف الاستماع',
    holdToSpeak: 'اضغط مطولاً على العلامة للتحدث',
    holdHint: 'اضغط مطولاً لمدة ثلاث ثوانٍ لفتح الميكروفون.',
    transcriptTitle: 'ما سمعه هذا الجهاز',
    localPrivacy: 'خاص ومحلي',
    localPrivacyHint: 'يبقى صوت الميكروفون والنص المستخرج داخل هذا المتصفح. لا يُرسل إلى فارزا إلا الأمر المنظم للتفويض والتدقيق.',
    installTitle: 'تثبيت نماذج الصوت المحلية',
    installHint: 'ينزّل فارزا حزمة النماذج المثبتة من خادم فارزا ويحفظها على هذا الجهاز للاستخدام دون اتصال.',
    installBasic: 'تثبيت النماذج الأساسية',
    installEnhanced: 'تثبيت المحلل المحسن',
    installed: 'النماذج الأساسية مثبتة',
    enhancedInstalled: 'المحلل المحسن مثبت',
    removeModels: 'إزالة النماذج المحلية',
    enable: 'تمكين الأوامر الصوتية',
    enableHint: 'الصوت متوقف افتراضياً، ولا يفتح الميكروفون إلا عند الضغط على زره.',
    language: 'لغة التحدث',
    autoLanguage: 'اتباع لغة الواجهة',
    speechModel: 'نموذج التعرف على الكلام',
    speechTurbo: 'Whisper Large v3 Turbo — الأفضل للفارسية والعربية',
    speechSmall: 'Whisper Small — تنزيل أصغر',
    speechModelHint: 'Turbo أدق بوضوح في الفارسية والعربية ويحتاج إلى معالج رسومي. Small أصغر حجماً ويعمل على أي جهاز.',
    speechModelNoGpu: 'لا يدعم هذا المتصفح WebGPU صالحاً، لذلك يتوفر Whisper Small فقط.',
    speechModelNoF16: 'لا يدعم محول الرسوم دقة نصفية، لذلك يعمل Turbo ببنية الدقة الكاملة الأكبر حجماً وبالدقة نفسها.',
    enhanced: 'تحليل محلي محسن',
    enhancedHint: 'استخدم نموذج Qwen الاختياري فقط عندما يكون التوجيه الحتمي والدلالي ملتبساً.',
    models: 'حزم النماذج على الجهاز',
    diagnostics: 'تشخيص الجهاز',
    runtime: 'بيئة التشغيل المحددة',
    halfPrecision: 'دقة GPU النصفية (fp16)',
    cpuThreads: 'المعالجات المنطقية',
    testMicrophone: 'اختبار إذن الميكروفون',
    microphoneReady: 'الميكروفون متاح. لم يُحتفظ بأي تسجيل.',
    microphoneDenied: 'الوصول إلى الميكروفون محظور في هذا المتصفح.',
    confirm: 'تأكيد الأمر',
    confirmHint: 'راجع الأمر المحلول قبل المتابعة.',
    runTypedCommand: 'تشغيل «{{command}}» كأمر محلي',
    state: {
      ...en.voice.state,
      disabled: 'الأوامر الصوتية متوقفة',
      initializing: 'جارٍ إعداد النماذج المحلية…',
      idle: 'جاهز',
      listening: 'جارٍ الاستماع على هذا الجهاز…',
      transcribing: 'جارٍ تحويل الكلام محلياً…',
      transcribed: 'تم التعرف عليه على هذا الجهاز',
      understanding: 'جارٍ الفهم محلياً…',
      clarifying: 'يلزم مزيد من التفاصيل',
      preparing: 'جارٍ التحقق من الصلاحيات وحل السجلات…',
      confirming: 'راجع وأكد',
      executing: 'جارٍ تنفيذ الأمر المعتمد…',
      success: 'اكتمل الأمر',
      error: 'فشل الأمر الصوتي',
    },
  },
  assistant: {
    ...en.assistant,
    askVarza: 'اسأل فارزا',
    title: 'مساعد فارزا المحاسبي',
    name: 'فارزا',
    subtitle: 'المحاسب الذكي',
    close: 'إغلاق فارزا',
    resize: 'تغيير حجم لوحة المساعد',
    chat: 'المحادثة',
    history: 'السجل',
    historyHint: 'كل محادثاتك مع فارزا، الأحدث أولاً.',
    historySearch: 'البحث في المحادثات',
    historyEmpty: 'لا توجد محادثات بعد',
    historyEmptyHint: 'اسأل فارزا سؤالاً وسيُحفظ الحوار للعودة إليه.',
    openConversation: 'فتح هذه المحادثة',
    activeConversation: 'مفتوحة الآن',
    schedules: 'الجداول',
    advisories: 'التنبيهات الاستشارية',
    advisoryTitle: 'التنبيهات المالية',
    advisoryHint: 'تنبيهات للقراءة فقط عن الأرصدة المتأخرة والمخزون وحدود النقد والتسويات والتكرارات المحتملة.',
    advisoryFailed: 'تعذر تحميل التنبيهات',
    noAdvisories: 'لا توجد تنبيهات نشطة',
    noAdvisoriesHint: 'لم يكتشف فارزا حالة تحتاج إلى انتباه.',
    dismissAdvisory: 'تجاهل',
    advisorySeverity: { Info: 'معلومة', Warning: 'تحذير', Critical: 'حرج' },
    noAccess: 'الوصول إلى المساعد غير ممكّن',
    noAccessHint: 'اطلب من مسؤول المؤسسة إضافة صلاحيات المساعد المناسبة إلى دورك.',
    conversations: 'المحادثات',
    newConversation: 'محادثة جديدة',
    previewMode: 'معاينة محلية',
    connected: 'متصل',
    pendingJournal: 'عدّله روبوت فارزا · يلزم التحقق البشري',
    pendingReceipt: 'عدّله روبوت فارزا · يلزم التحقق البشري',
    pendingInvoice: 'عدّل روبوت فارزا الفاتورة · يلزم التحقق البشري',
    pendingSupplierPayment: 'عدّل روبوت فارزا دفعة المورد · يلزم التحقق البشري',
    pendingBankTransfer: 'عدّل روبوت فارزا التحويل البنكي · يلزم التحقق البشري',
    pendingCashTransaction: 'عدّل روبوت فارزا المعاملة النقدية · يلزم التحقق البشري',
    pendingStatementImport: 'عدّل روبوت فارزا استيراد كشف الحساب · يلزم التحقق البشري',
    pendingPerson: 'شخص جديد مقترح · يلزم التأكيد',
    aiProposalHint: 'يرسل التحقق البشري المقترح إلى سير الموافقة المعتاد؛ لا يستطيع الروبوت اعتماده أو ترحيله.',
    confirmProposal: 'التحقق من مستند الروبوت',
    confirmPerson: 'إنشاء الشخص',
    speak: 'قراءة هذا الرد بصوت عالٍ',
    thinking: 'يراجع فارزا بيانات المؤسسة…',
    placeholder: 'صِف معاملة أو اطرح سؤالاً أو اطلب تقريراً…',
    voice: 'تسجيل رسالة صوتية',
    audioReply: 'قراءة الرد بصوت عالٍ',
    elevenLabsAudio: 'صوت ElevenLabs',
    transcribing: 'جارٍ تحويل تسجيلك والرد عليه…',
    voiceEmpty: 'لم يُكتشف كلام. حاول مرة أخرى.',
    voiceFailed: 'تعذرت معالجة الرسالة الصوتية. حاول مرة أخرى.',
    microphoneDenied: 'رُفض الوصول إلى الميكروفون. اسمح به في إعدادات المتصفح ثم حاول مجدداً.',
    recording: 'جارٍ التسجيل',
    recordingLimit: 'يتوقف التسجيل تلقائياً بعد دقيقتين.',
    stopAndSend: 'إيقاف وإرسال',
    discardRecording: 'تجاهل التسجيل',
    speechFailed: 'تعذر تشغيل الرد الصوتي.',
    reviewNotice: 'تبقى إدخالات الذكاء الاصطناعي معلقة حتى يؤكدها شخص مخول.',
    send: 'إرسال',
    sending: 'جارٍ إرسال الرسالة',
    sendFailed: 'تعذر إرسال الرسالة. ما زال نصك موجوداً؛ حاول مرة أخرى.',
    sendHint: 'Enter للإرسال · Shift + Enter لإضافة سطر',
    welcome: 'كيف يمكنني مساعدتك في الحسابات؟',
    welcomeHint: 'يمكنني شرح الأرصدة أو إعداد تقرير HTML أو اقتراح قيد متوازن لمراجعته.',
    promptTransaction: 'سجّل مصروفاً مكتبياً نقدياً بقيمة 12,500,000 IRR',
    promptReport: 'اعرض ملخص ميزان المراجعة لهذا الشهر',
    promptReview: 'ما القيود التي ما زالت تحتاج إلى انتباه؟',
    scheduleName: 'اسم الجدول',
    schedulePrompt: 'مهمة فارزا',
    interval: 'تكرار التشغيل',
    hourly: 'كل ساعة',
    daily: 'يومياً',
    weekly: 'أسبوعياً',
    createSchedule: 'إنشاء جدول',
    pause: 'إيقاف مؤقت',
    resume: 'استئناف',
    memory: 'الذاكرة',
    knowledge: 'المعرفة',
    evidence: 'الأدلة والاستدلال',
    intent: 'النية',
    risk: 'المخاطر',
    duration: 'المدة',
    tokens: 'الرموز',
    actionProposal: 'الإجراء المقترح',
    sources: 'المصادر والاستشهادات',
    noEvidence: 'لم تُستخدم أدلة خارجية في هذا الرد.',
    execution: 'تفاصيل التنفيذ',
    budget: 'ميزانية الذكاء الاصطناعي للمؤسسة',
    reviewerRejected: 'رفضه المراجع البشري.',
    memoryTitle: 'ذاكرة محكومة',
    memoryHint: 'لا تُقدم للمحادثات المستقبلية إلا الذكريات المعتمدة والمحددة النطاق، ويمكنك إلغاؤها متى شئت.',
    knowledgeTitle: 'معرفة المؤسسة',
    knowledgeHint: 'حمّل سياسات وإجراءات المؤسسة القابلة للاستشهاد، وراجع النتيجة المستخرجة ثم انشرها للاسترجاع.',
    knowledgeDocumentTitle: 'عنوان المستند',
    knowledgeLanguage: 'اللغة',
    knowledgeFile: 'ملف المصدر',
    uploadKnowledge: 'تحميل ومعالجة',
    processingKnowledge: 'جارٍ المعالجة…',
    publishKnowledge: 'نشر',
    knowledgeUploadFailed: 'تعذرت معالجة المصدر. تحقق من التنسيق أو طبقة النص.',
    characters: 'حرفاً',
    chunks: 'مقطعاً',
  },
  theme: {
    light: 'فاتح',
    dark: 'داكن',
    system: 'مطابقة النظام',
    switchToDark: 'التبديل إلى المظهر الداكن',
    switchToLight: 'التبديل إلى المظهر الفاتح',
  },
  landing: {
    ...en.landing,
    nav: {
      about: 'عن فارزا',
      identity: 'الهوية بين المؤسسات',
      plans: 'الخطط والأسعار',
      support: 'الدعم',
      policy: 'السياسات',
      sections: 'أقسام الصفحة الرئيسية',
    },
    signIn: 'تسجيل الدخول',
    openWorkspace: 'فتح النظام',
    explore: 'اقرأ المزيد',
  },
  calendar: {
    solarHijriField: 'هجري شمسي (سنة/شهر/يوم)',
    gregorianField: 'ميلادي (سنة/شهر/يوم)',
    islamicHijriField: 'هجري قمري (سنة/شهر/يوم)',
    gregorianEquivalent: 'المقابل الميلادي',
    equivalent: 'المقابل في {{calendar}}',
    system: {
      persian: 'الهجري الشمسي',
      gregorian: 'الميلادي',
      islamic: 'الهجري القمري',
    },
    openCalendar: 'فتح التقويم',
    closeCalendar: 'إغلاق التقويم',
    chooseDate: 'اختر تاريخًا',
    chooseMonth: 'اختر شهرًا',
    chooseYear: 'اختر سنة',
    previousMonth: 'الشهر السابق',
    nextMonth: 'الشهر التالي',
    previousYear: 'السنة السابقة',
    nextYear: 'السنة التالية',
    previousYears: 'السنوات السابقة',
    nextYears: 'السنوات التالية',
    today: 'اليوم',
    clear: 'مسح',
    noDate: 'لم يتم اختيار تاريخ',
  },
  status: {
    draft: 'مسودة',
    aiPendingApproval: 'عدّله روبوت فارزا · يلزم التحقق',
    submitted: 'مُرسل',
    approved: 'مُعتمد',
    posted: 'مُرحّل',
    reversed: 'معكوس',
    closed: 'مغلق',
    active: 'نشط',
    pending: 'معلّق',
    blocked: 'محظور',
  },
  errors: {
    pageTitle: 'تعذّر تحميل هذه الصفحة',
    pageMessage: 'تحقق من الاتصال وحاول مرة أخرى. لم يتم حذف العمل غير المحفوظ.',
    notFoundTitle: 'الصفحة غير موجودة',
    notFoundMessage: 'مساحة العمل المطلوبة غير موجودة أو ليست لديك صلاحية الوصول إليها.',
    returnDashboard: 'العودة إلى لوحة المعلومات',
  },
  security: {
    ...en.security,
    recaptchaNotice: 'هذا الموقع محمي بواسطة reCAPTCHA وتُطبّق <privacy>سياسة خصوصية</privacy> Google و<terms>شروط الخدمة</terms>.',
  },
  offline: {
    ...en.offline,
    chip: {
      label: 'الاتصال والعمل غير المرسل',
      offline: 'غير متصل',
      synced: 'محدّث',
      waiting: 'بانتظار الإرسال: {{count}}',
      attention: 'يتطلب الانتباه: {{count}}',
    },
    status: {
      online: 'متصل بفارزا',
      offline: 'لا يوجد اتصال بفارزا',
      notEnabled: 'هذا الجهاز لا يحتفظ بنسخة للعمل دون اتصال.',
      sealed: 'نسخة العمل دون اتصال مقفلة حتى تسجل الدخول بكلمة المرور.',
      copyConfirmed: 'تم تأكيد نسخة العمل دون اتصال {{when}}.',
    },
    state: {
      pending: 'بانتظار الإرسال',
      failed: 'إعادة المحاولة',
      rejected: 'مرفوض',
      uncertain: 'غير مؤكد',
    },
    error: {
      unknown: 'لم يقبل الخادم هذا الطلب.',
      unreadable: 'لم يعد من الممكن قراءة هذا الإدخال على الجهاز.',
      no_reply: 'تم الإرسال ولم يصل رد. تحقق من السجل قبل إعادة الإرسال.',
      needsConnection: 'يتطلب هذا اتصالًا بخادم فارزا.',
    },
    unlock: {
      eyebrow: 'لا يوجد اتصال',
      title: 'تسجيل الدخول دون اتصال',
      subtitle: 'لا يستطيع فارزا الوصول إلى الخادم. تفتح كلمة المرور النسخة المحفوظة على هذا الجهاز.',
      account: 'الحساب',
      copyTaken: 'تاريخ أخذ النسخة',
      windowCloses: 'ينتهي الوصول دون اتصال',
      passwordHint: 'كلمة المرور نفسها التي تستخدمها لتسجيل الدخول إلى فارزا.',
      action: 'فتح النسخة المحلية',
      opening: 'جارٍ الفتح…',
      passwordError: 'كلمة المرور هذه لا تفتح النسخة الموجودة على الجهاز.',
      lockedError: 'محاولات كثيرة جدًا. انتظر قبل المحاولة مجددًا.',
      expiredError: 'انتهت صلاحية هذه النسخة. اتصل بالإنترنت لتجديدها.',
      failedError: 'تعذّر فتح نسخة العمل دون اتصال على هذا الجهاز.',
      barred: 'محاولات كثيرة جدًا. حاول بعد {{seconds}} ثانية.',
      scope: 'سترى الأرقام كما كانت عند أخذ النسخة. تُحفظ المسودات هنا وتُرسل بعد عودة الاتصال؛ أما الترحيل والموافقة والدفع فتتطلب اتصالًا.',
      tryConnection: 'إعادة محاولة الاتصال',
    },
    forget: {
      start: 'نسيان هذا الجهاز',
      action: 'نسيانه',
      confirm: 'ستُحذف النسخة المحلية وكلمة مرورها من هذا الجهاز.',
      confirmWithQueue: 'ستُفقد الإدخالات غير المرسلة على هذا الجهاز. بانتظار الإرسال: {{count}}.',
    },
    login: {
      remember: 'السماح بالعمل دون اتصال على هذا الجهاز',
      rememberHint: 'يحتفظ فارزا هنا بنسخة مشفرة من مساحة العمل تفتحها كلمة المرور، ويمكن استخدامها حتى {{days}} يومًا دون اتصال.',
    },
  },
  pwa: {
    ...en.pwa,
    install: {
      ...en.pwa.install,
      title: 'تثبيت فارزا',
      action: 'تثبيت',
      dismiss: 'ليس الآن',
      sectionTitle: 'التثبيت على هذا الجهاز',
    },
    update: {
      available: 'يتوفر إصدار جديد من فارزا.',
      reload: 'التحديث الآن',
      applying: 'جارٍ التحديث…',
    },
  },
};
